import { AnimatePresence, motion } from 'framer-motion'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded'
import MapRoundedIcon from '@mui/icons-material/MapRounded'
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type SetStateAction } from 'react'
import { createPortal } from 'react-dom'
import { useMemberLayout } from './Member/MemberLayoutContext'
import type { Goal, SubTask, Task } from '../types/timeline'
import type { TimelineBoardKey, WorkspaceFileMetadata } from '../types/schema'
import type { CreateTimelineElementInput } from '../types/timelineElement'
import { useAiJob } from '../context/useAiJob'
import { useAuth } from '../context/useAuth'
import { useWorkspace } from '../context/useWorkspace'
import { useChat } from '../context/useChat'
import { useCredits } from '../context/useCredits'
import { useGlobalActionLoading } from '../context/useGlobalActionLoading'
import { CreditBalance } from './Credits/CreditBalance'
import { formatCreditNumber } from './Credits/formatCredits'
import { useData } from '../context/useData'
import { useTimeline } from '../context/useTimeline'
import { useToast } from '../context/useToast'
import { useAccessRestriction } from '../hooks/useAccessRestriction'
import { useWorkspaceAiSuggestions } from '../hooks/useWorkspaceAiSuggestions'
import { useWorkspaceDraft } from '../hooks/useWorkspaceDraft'
import { buildGoalsFromTimelineElements } from '../utils/elementsToGoals'
import { ControlNotice } from './Common/ControlNotice'
import type { MemberControls, MemberAiDailyBudgetCounter } from '../types/memberControls'
import { subscribeToMyEffectiveControls } from '../services/memberControlsService'
import { KeyboardDock } from './Common/KeyboardDock'
import {
  collectChangedNodeIds,
  generateValidatedTimelineEditBatch,
  runAiMagicChatTurn,
  collapseRevisedProposalTurn,
  type AiMagicChatTurn,
  type AiMagicProposalPreview,
  type AiMagicQuickControl,
  type BatchEditTarget,
  type ProposalRevisionContext,
  type ValidationIssue,
} from '../services/aiService'
import { emitTrackEvent } from '../utils/gamificationEvents'
import { switchedOffModulesFor } from '../utils/workspaceModules'
import { fireAutomationEvent } from '../services/automationBus'
import { PREMIUM_ACTION_COSTS } from '../services/creditStorefrontService'
import { walletService, type WalletReservationPurpose } from '../services/walletService'
import { spreadIndividualBoardCanvas } from '../services/checklistWriter'
import { invalidateChecklistBoardSessionSnapshot } from './ChecklistDrawingBoard'
import { classifyClarificationRelevance } from '../services/clarificationRelevance'
import { normalizeGeminiUsage, quoteAiCredits } from '../utils/aiBilling'
import { estimateContextCost, BASE_ACTION_COSTS } from '../utils/contextCostEstimator'
import { findLineCreatePhrase } from '../services/lineCreatePhrases'
import { DEFAULT_LINE_WORKSPACE_POLICY, getLineWorkspacePolicy, type LineWorkspacePolicy } from '../services/lineWorkspacePolicy'
import { watchLineBuildOutcome } from '../services/lineLaggingUsageObserver'
import { type DataContext, type DataContextKind } from '../types/aiChatSource'
import type { AiOperationTargetRef } from '../types/aiOperational'
import { type EditTarget, targetKey, isCreatePlanReference, isEditableTarget } from './AiGenerator/editTarget'
import { buildProposalSummaryForGoal } from './AiGenerator/proposalSummary'
import { toOperationalTargetRef } from './AiGenerator/operationalTargetRef'
import InterviewQuestionSet from './AiGenerator/InterviewQuestionSet'
import type { InterviewAnswers } from './AiGenerator/interviewSubmit'
import { buildInterviewEnvelope, buildInterviewSubmission } from './AiGenerator/interviewSubmit'
import { upgradeQuickControlsToQuestionSet } from './AiGenerator/interviewLegacyUpgrade'
import type { AiMagicQuestionSet } from '../services/ai/interview/aiMagicQuestionSet'
import type { LineInterviewEscalation } from '../services/ai/interview/lineInterviewEscalation'
import { buildTimelineConstraintContext } from '../utils/constraintContext'
import { toConsolePath } from '../utils/consoleRoutes'
import { buildTimelineRuleContext } from '../services/ai/context/timelineRuleContext'
import { buildLocalWorkspacePulseSnapshot } from '../services/ai/context/localWorkspacePulse'
import { mergeOperationalPulseHints, workspacePulseToOperationalHints } from '../services/ai/context/pulseContext'
import { createLineOperationalPlan } from '../services/ai/orchestrator/lineOperationalOrchestrator'
import { buildIntegrationRuntimeRecords } from '../services/integrationRuntimeSummaryService'
import { useIntegrationSubscriptions } from '../hooks/useIntegrationSubscriptions'
import {
  hasPreparedProposalIntent,
  isDetailedCreatePrompt,
  parseAiMagicExecutePathResponse,
  parseAiMagicOperationalConfirmationResponse,
  routeAiMagicOperationalPlan,
} from '../services/ai/orchestrator/aiMagicOperationalRouting'
import { createAutomationGraph } from '../services/automationService'
import { buildExecutionPreview } from '../services/ai/execution/executionPreview'
import { buildExecutionTheaterOutline } from '../services/ai/execution/executionTheater'
import { buildAiMagicExecutionResultDetail } from '../services/ai/execution/automationExecutionPresentation'
import {
  compileAutomationActionBundleFromPrompt,
  compileAutomationDraftFromPrompt,
} from '../services/ai/execution/automationDraftCompiler'
import { executeAutomationActionBundle } from '../services/automationActionExecutionService'
import { metadataFromOperationalPlan, type AiOperationalProposalMetadata } from '../services/ai/proposals/operationalProposalAdapter'
import { dispatchLineExecute } from '../services/ai/execute/dispatcher'
import { resolveLineExecuteConfirmation } from '../services/ai/execute/confirmPayloadHandler'
import { adapterKindSupportsTourFallback, isShowMeHowReply, SHOW_ME_HOW_CONTROL } from '../services/ai/execute/tourFallback'
import { LINE_EXECUTE_KIND_TO_ACTION_ID } from '../services/ai/execute/lineExecuteKindMap'
import { getAction, teachAction } from '../services/actions/registry'
import { featureFlag } from '../utils/featureFlags'
import { useTourGuide } from './tourguide/useTourGuide'
import { WEB_INTENT_CATALOG, buildWorkspaceDynamicIntentCatalog } from '../services/ai/intents/webIntentCatalog'
import '../services/ai/execute/resolvers'
import { useCanvasPermissions } from '../hooks/useCanvasPermissions'
import type {
  AIResultStatus,
  ActionPayload,
  AiOperatingMode,
  ProactiveSuggestionPayload,
  RoutingPayload,
  SuggestionAcceptancePayload,
} from '../types/ai'
import { LineChatBubble } from './Line/LineChatBubble'
import { LineComposerAmbientChip } from './Line/LineComposerAmbientChip'
import { CitationDispatchProvider } from './Line/citationDispatch'
import { ProposalSummaryCard } from './AiGenerator/ProposalSummaryCard'
import { RailPageShell } from './Common'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import { AiChatProvider } from '../context/AiChatContext'
import { useAiChat } from '../context/useAiChat'

import { resolveAiTargetBoard } from '../utils/aiBoardIntent'
import { emitLineSignal } from '../services/lineLearningSignals'
import { AgentThinkingIndicator } from './Line/AgentThinkingIndicator'
import { buildLineThinkingStatuses } from './Line/lineThinkingStatuses'
import { AiChatHistoryPanel } from './AiChatHistoryPanel'
import {
  createAiChatSession,
  deleteAiChatSession,
  renameAiChatSession,
  saveAiChatSession,
  subscribeAiChatSessions,
  DEFAULT_PENDING_PROPOSAL_TTL_MS,
  type AiChatAttachment,
  type AiChatPendingProposal,
  type AiChatSession,
} from '../services/aiChatHistoryService'
import { useSubscription } from '../context/useSubscription'
import { getTierLimit } from '../services/subscriptionGuard'
import { limitsLiftedForWorkspace } from '../utils/demoUnlock'
import { AiComposerMenu } from './AiComposerMenu'
import { detectRuleCandidate } from '../services/ai/rules/userRuleDetector'
import { appendUserRule } from '../services/memoryService'
import { MergedComposer } from './Common/MergedComposer/MergedComposer'
import { AiTierPickerButton } from './AiTierSwitcher/AiTierPickerButton'
import { AiTierUpsellModal, isTierUpsellOnCooldown } from './AiTierSwitcher/AiTierUpsellModal'
import { AiTierCoachmark } from './AiTierSwitcher/AiTierCoachmark'
import { ChatSourceExpandModal } from './AiGenerator/ChatSourceExpandModal'
import { WorkspaceItemPicker } from './Common/WorkspaceItemPicker'
import { trackAiTierEvent } from '../services/aiTierAnalytics'
import { useAiTier } from '../hooks/useAiTier'
import { evaluateTierAccess } from '../utils/aiTierAccessGate'
import { capChatSelection } from '../utils/chatContextCap'
import { PRICING_CATALOG } from './Settings/pricingConfig'
import { lineFeatureFlags } from '../config/lineFeatureFlags'
import type { AiTierId } from '../types/ai'
import type { AiChatHandoffRequest } from '../types/aiChatSource'
import { generateAndRegisterTour } from '../services/aiTourGenerator'
import { JourneyTriggerCard } from './JourneyTriggerCard'
import { getStorageFileUrl, uploadWorkspaceFiles } from '../services/storageHubService'
import { extractDocumentText, extractSpreadsheetText } from '../utils/fileExtractor'
import { effectiveEntitlementLimit, LIFETIME_PASSES } from '../config/lifetimeEntitlements'
import { UpgradeLockBadge } from './Common/UpgradeLockBadge'
import { WorkspacePulse, type WorkspacePulsePremiumPanel } from './Ai/WorkspacePulse'
import { isNoSignInPreviewActive } from '../debug'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import { AttachmentPreviewDialog, type AttachmentPreviewItem, type AttachmentPreviewKind } from './Common/AttachmentPreviewDialog'
import { AppDialog } from './Common/AppDialog'
import { StatusChip, TagChip } from './Common/Chip'
import { Textarea } from './Common/Textarea'
import { TextInput } from './Common/TextInput'
import { Button } from './Common/Button'
import { IconButton } from './Common/IconButton'
import { Pressable } from './Common/Pressable'
import { Fab } from './Common/Fab'

const MotionPressable = motion.create(Pressable)
import { useNls } from '../nls/nlsProvider'
import {
  composeFollowUpAnchor,
  canPromoteAiMagicDraftRun,
  findOpenClarificationTurn,
  getActivePendingProposal,
  getVisibleAiMagicSessions,
  aiMagicRunChatId,
  isAiMagicRunVisible,
  isLegacyEmptyAiChatDraft,
  selectAiMagicSessionToAutoOpen,
  type AiMagicRunScope,
  withPendingProposalAnchor,
} from './AiGenerator/aiMagicSessionModel'
import {
  analyzeAttachmentInventory,
  analyzeAttachmentPlanningContext,
  findLatestAttachmentApproval,
  renderAttachmentPlanningBrief,
  type AttachmentInventory,
  type AttachmentPlanningDigest,
  type AttachmentPlanningDigestSnapshot,
  type AttachmentPlanningSourceInput,
  type AttachmentPlanningSourceKind,
} from '../services/ai/attachment/attachmentPlanningIntelligence'
import { createAiChecklistCapabilityContext } from '../services/ai/context/checklistCapabilityContext'
import { stripClarificationEnvelope } from '../services/ai/clarificationEnvelope'

const toAttachmentPlanningSourceKind = (kind: DataContextKind): AttachmentPlanningSourceKind => {
  switch (kind) {
    case 'spreadsheet':
    case 'document':
    case 'gsheet':
    case 'link':
    case 'asset':
    case 'upload':
      return kind
    default:
      return 'document'
  }
}

type SpeechRecognitionResultLike = {
  readonly isFinal: boolean
  readonly [index: number]: { transcript: string }
}

type SpeechRecognitionEventLike = Event & {
  readonly resultIndex: number
  readonly results: ArrayLike<SpeechRecognitionResultLike>
}

type SpeechRecognitionLike = EventTarget & {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: Event) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
}

const FREE_CHARACTER_LIMIT = 5000
const PAID_CHARACTER_LIMIT = 5000
const FREE_COOLDOWN_SECONDS = 5 * 60
// Flat ingestion surcharge per attached data source (spreadsheet / document /
// linked sheet). Local parsing is free; this fee is held until the user clicks
// Generate and is burned alongside the normal text/image estimate.
// Auto-stop dictation after this much silence.
// Each Gemini recognition result resets the timer; only sustained silence
// fires it and toggles the mic back to its idle (grey) state.
const SPEECH_SILENCE_AUTO_STOP_MS = 5_500
const createChatTurnId = () => crypto.randomUUID()
type PendingDeleteDialog = {
  session: AiChatSession
  closeHistoryLimitOnConfirm: boolean
}

const formatCountdown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0')
  const remainingSeconds = (seconds % 60).toString().padStart(2, '0')
  return `${minutes}:${remainingSeconds}`
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Phrases that signal the user wants the edit to cascade into descendants.
 * Detection runs client-side BEFORE we call the model — when matched, we
 * expand the selected targets to include all descendants so each child gets
 * its own scoped AI call (the per-target edit prompt is still strictly
 * scoped — see aiService.buildAiEditPrompt).
 */
const CASCADE_PHRASES: RegExp[] = [
  /\ball\s+(?:items?|tasks?|subtasks?|children|nodes?)\b/i,
  /\bevery\s+(?:item|task|subtask|child|node)\b/i,
  /\beach\s+(?:item|task|subtask|child|node)\b/i,
  /\bunder\s+this\b/i,
  /\bunderneath\b/i,
  /\bof\s+(?:its|the)\s+(?:children|descendants|subtasks)\b/i,
]

/** Softer hints that mean "consider cascading" without committing to it. The
 * AI gets the subtree as context in this case and decides which descendants
 * (if any) actually need to change. We don't implement the subtree-context
 * path today — for now soft hints also expand client-side, matching the
 * stronger phrases. Recorded separately so we can revisit later. */
const CASCADE_SOFT_HINTS: RegExp[] = [
  /\bevery\b/i,
  /\beach\b/i,
  /\ball\b/i,
]

const detectCascadeIntent = (instruction: string): 'cascade' | 'soft' | 'none' => {
  for (const pattern of CASCADE_PHRASES) {
    if (pattern.test(instruction)) return 'cascade'
  }
  for (const pattern of CASCADE_SOFT_HINTS) {
    if (pattern.test(instruction)) return 'soft'
  }
  return 'none'
}

// applyEditToGoal previously lived here — replaced by aiService.applyPatchAt
// inside the validated batch entry point, which handles per-target dispatch
// and accumulates patches onto a single working goal.

const timelineDateToInput = (value: Goal['startDate']): string | undefined => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value).toISOString()
  return undefined
}

/**
 * Flatten an AI-generated Goal → Task → Subtask tree into the ordered
 * `CreateTimelineElementInput[]` shape `createElements` expects. Order matters:
 * the goal must be first so children can resolve `parentId` through the id
 * translation map inside `createElements`. Each node's inline `checklist[]`
 * rides along so `syncChecklistBoardItems` fans them out into the flat
 * `checklists` collection at write time.
 */
const flattenGoalToElementInputs = (
  goal: Goal,
  board: TimelineBoardKey,
): CreateTimelineElementInput[] => {
  const inputs: CreateTimelineElementInput[] = []
  inputs.push({
    id: goal.id,
    type: 'GOAL',
    parentId: null,
    title: goal.title,
    description: goal.description,
    checklist: goal.checklist,
    weight: 1,
    progress: 0,
    startDate: timelineDateToInput(goal.startDate),
    endDate: timelineDateToInput(goal.endDate),
    color: goal.baseColor,
    board,
  })
  goal.tasks.forEach((task: Task) => {
    inputs.push({
      id: task.id,
      type: 'TASK',
      parentId: goal.id,
      title: task.title,
      description: task.description,
      checklist: task.checklist,
      weight: 1,
      progress: 0,
      startDate: timelineDateToInput(task.startDate),
      endDate: timelineDateToInput(task.endDate),
      color: task.color,
      board,
    })
    task.subTasks.forEach((sub: SubTask) => {
      inputs.push({
        id: sub.id,
        type: 'SUBTASK',
        parentId: task.id,
        title: sub.title,
        description: sub.description,
        checklist: sub.checklist,
        weight: 1,
        progress: 0,
        startDate: timelineDateToInput(sub.startDate),
        endDate: timelineDateToInput(sub.endDate),
        color: sub.color,
        board,
      })
    })
  })
  return inputs
}

type GeneratedGoalMeta = {
  mode: 'generate' | 'edit'
  baseline: Goal | null
  issues?: ValidationIssue[]
  aiNote?: string
  aiChangedIds?: string[]
  agentActions?: ActionPayload[]
  confidence?: number
  reasoning?: string[]
  targetBoard?: TimelineBoardKey
  operationalProposal?: AiOperationalProposalMetadata
}

export type PendingPreviewRecord = {
  goal: Goal
  meta: GeneratedGoalMeta
}

const pendingPreviewStorageKey = (workspaceId: string | null | undefined) =>
  `aiMagic.pendingPreviews.v1.${workspaceId || 'personal'}`

const readPendingPreviewStore = (workspaceId: string | null | undefined): Record<string, PendingPreviewRecord> => {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(pendingPreviewStorageKey(workspaceId))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, PendingPreviewRecord>
      : {}
  } catch {
    return {}
  }
}

const writePendingPreviewStore = (
  workspaceId: string | null | undefined,
  updater: (current: Record<string, PendingPreviewRecord>) => Record<string, PendingPreviewRecord>,
) => {
  if (typeof window === 'undefined') return
  try {
    const next = updater(readPendingPreviewStore(workspaceId))
    window.localStorage.setItem(pendingPreviewStorageKey(workspaceId), JSON.stringify(next))
  } catch {
    // Storage persistence is best-effort; the in-memory map still supports the
    // current mounted session.
  }
}

const previewKindForContext = (context: DataContext): AttachmentPreviewKind => {
  if (context.kind === 'chat') return 'chat'
  if (context.kind === 'form') return 'form'
  if (context.kind === 'form_responses') return 'form_responses'
  if (context.kind === 'design') return 'design'
  if (context.kind === 'gsheet') return 'gsheet'
  if (context.kind === 'spreadsheet') return 'spreadsheet'
  if (context.kind === 'document') return 'document'
  if (context.kind === 'link') return 'link'
  if (context.kind === 'asset') return 'asset'
  if (context.kind === 'upload') {
    if (context.mimeType?.startsWith('image/')) return 'image'
    if (context.mimeType?.startsWith('audio/')) return 'audio'
    if (context.mimeType?.startsWith('video/')) return 'video'
    if (context.mimeType === 'application/pdf') return 'document'
    return 'file'
  }
  if (context.mimeType?.startsWith('image/')) return 'image'
  if (context.mimeType?.startsWith('audio/')) return 'audio'
  if (context.mimeType?.startsWith('video/')) return 'video'
  if (context.mimeType === 'application/pdf') return 'document'
  if (import.meta.env.DEV) {
    console.warn('[AiGeneratorPanel] Unknown context kind in previewKindForContext:', (context as { kind: unknown }).kind)
  }
  return 'file'
}

const buildAttachmentAnalysisKey = (contexts: DataContext[]) => {
  if (contexts.length === 0) return ''
  return JSON.stringify(
    contexts.map((context) => ({
      id: context.id,
      label: context.label,
      kind: context.kind,
      mimeType: context.mimeType ?? '',
      textLength: context.text.length,
    })),
  )
}

const toAttachmentPlanningSnapshot = (digest: AttachmentPlanningDigest): AttachmentPlanningDigestSnapshot => ({
  summary: digest.summary,
  sources: digest.sources,
  inferred: digest.inferred,
  planOutline: digest.planOutline,
  followUpQuestions: digest.followUpQuestions,
  warnings: digest.warnings,
})

const findLatestAttachmentIntelligence = (turns: AiMagicChatTurn[]): AttachmentPlanningDigestSnapshot | null => {
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const digest = turns[index]?.attachmentIntelligence
    if (digest) return digest
  }
  return null
}

const buildAttachmentInventoryQuestionSet = (
  inventory: AttachmentInventory,
  prompt: string,
): AiMagicQuestionSet => {
  const itemIds = inventory.sources.flatMap((source) => source.items.map((item) => item.id))
  return {
    id: `attachment-inventory-${Date.now().toString(36)}`,
    title: prompt,
    skippable: false,
    questions: [{
      id: 'attachment-items',
      prompt,
      type: 'ATTACHMENT_INVENTORY',
      attachmentInventory: inventory,
      // Large inventories start with a bounded visible/default selection, but
      // the control can reveal and select every extracted item.
      default: inventory.extractedCount > 40 ? itemIds.slice(0, 40) : itemIds,
    }],
  }
}

const buildLineInterviewEscalationQuestionSet = (
  escalation: LineInterviewEscalation,
  t: ReturnType<typeof useNls>['t'],
): AiMagicQuestionSet => ({
  id: `line-interview-escalation-${Date.now().toString(36)}`,
  skippable: false,
  questions: escalation.triggers.map((trigger) => trigger.kind === 'scale'
    ? {
        id: 'scope-scale',
        prompt: t('NLSAiInterviewScaleQuestion', { tasks: trigger.taskCount, checklists: trigger.checklistCount }),
        type: 'RADIO' as const,
        required: true,
        options: [
          { label: t('NLSAiInterviewScaleKeepAsIs'), value: 'keep_as_is' },
          { label: t('NLSAiInterviewScaleNarrower'), value: 'narrower' },
          { label: t('NLSAiInterviewScaleWider'), value: 'wider' },
        ],
      }
    : {
        id: 'attachment-conflict',
        prompt: t('NLSAiInterviewAttachmentConflict', { files: trigger.attachmentCount, prompt: trigger.promptCount }),
        type: 'RADIO' as const,
        required: true,
        options: [
          { label: t('NLSAiInterviewAttachmentConflictFiles'), value: 'files' },
          { label: t('NLSAiInterviewAttachmentConflictPrompt'), value: 'prompt' },
          { label: t('NLSAiInterviewAttachmentConflictBlend'), value: 'blend' },
          { label: t('NLSAiInterviewAttachmentConflictNarrower'), value: 'narrower' },
        ],
      }),
})

const previewFromTurn = (turn: AiMagicChatTurn): PendingPreviewRecord | null => {
  const preview = turn.proposalPreview
  if (!preview?.goal || !preview.meta) return null
  return {
    goal: preview.goal,
    meta: {
      mode: preview.meta.mode,
      baseline: preview.meta.baseline ?? null,
      issues: preview.meta.issues,
      aiNote: preview.meta.aiNote,
      aiChangedIds: preview.meta.aiChangedIds,
      agentActions: preview.meta.agentActions,
      confidence: preview.meta.confidence,
      reasoning: preview.meta.reasoning,
      targetBoard: preview.meta.targetBoard,
      operationalProposal: preview.meta.operationalProposal,
    },
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const resolvePendingPreview = (
  turnId: string,
  refMap?: Map<string, PendingPreviewRecord> | null,
  store?: Record<string, PendingPreviewRecord> | null,
  turns?: AiMagicChatTurn[] | null,
): PendingPreviewRecord | null => {
  const fromRef = refMap?.get(turnId)
  if (fromRef) return fromRef
  const fromStore = store?.[turnId]
  if (fromStore) return fromStore
  const turn = turns?.find((candidate) => candidate.id === turnId)
  if (turn) {
    return previewFromTurn(turn)
  }
  return null
}

const pendingProposalForTurn = (
  turnId: string,
  preview: PendingPreviewRecord,
  summary: NonNullable<AiMagicChatTurn['proposalSummary']>,
  ttlMs = DEFAULT_PENDING_PROPOSAL_TTL_MS,
): AiChatPendingProposal => ({
  turnId,
  goal: preview.goal,
  meta: preview.meta,
  summary,
  expiresAt: Date.now() + ttlMs,
})

const findLatestProposalRevisionTurn = (turns: AiMagicChatTurn[]) => {
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index]
    if (!turn.id || turn.role !== 'ASSISTANT' || !turn.proposalRevisionRequested) continue
    const preview = previewFromTurn(turn)
    if (preview) return { turnId: turn.id, preview }
  }
  return null
}

const mergeUniqueDataContexts = (current: DataContext[], next: DataContext[]) => {
  const map = new Map(current.map((context) => [context.id, context]))
  next.forEach((context) => {
    map.set(context.id, context)
  })
  return Array.from(map.values())
}

const mergeUniqueAiChatAttachments = (current: AiChatAttachment[], next: AiChatAttachment[]) => {
  const map = new Map(current.map((attachment) => [attachment.id, attachment]))
  next.forEach((attachment) => {
    map.set(attachment.id, attachment)
  })
  return Array.from(map.values())
}

const fileSignature = (file: File) => `${file.name}:${file.size}:${file.lastModified}:${file.type}`

const mergeUniqueFiles = (current: File[], next: File[]) => {
  const seen = new Set(current.map(fileSignature))
  const merged = [...current]
  next.forEach((file) => {
    const signature = fileSignature(file)
    if (seen.has(signature)) return
    seen.add(signature)
    merged.push(file)
  })
  return merged
}

export const buildAiChatAttachmentPayloads = (targets: EditTarget[], contexts: DataContext[]) => [
  ...targets.map((target) => ({ id: targetKey(target), kind: 'timeline' as const, label: target.label, target })),
  ...contexts.map((context) => {
    let messages = context.messages
    if (context.kind === 'chat' && Array.isArray(context.messages) && context.messages.length > 0) {
      messages = capChatSelection(context.messages).messages
    }
    return {
      id: context.id,
      kind: context.kind,
      label: context.label,
      text: context.kind === 'chat' ? (context.text || '').slice(0, 20_000) : (context.text || '').slice(0, 5_000),
      fileId: context.fileId,
      mimeType: context.mimeType,
      channelId: context.channelId,
      channelName: context.channelName,
      messages,
      formId: context.formId,
      formResponseIds: context.formResponseIds,
      formResponseCount: context.formResponseCount,
    }
  }),
]

const buildSeedReferenceTree = (goals: Goal[], targets: EditTarget[]): string | null => {
  const references: Array<
    | { kind: string; label: string; tree: Goal }
    | { kind: string; label: string; tree: Task }
    | { kind: string; label: string; tree: SubTask }
  > = []
  for (const target of targets) {
    if (target.kind === 'goal') {
      const goal = goals.find((candidate) => candidate.id === target.goalId)
      if (goal) references.push({ kind: 'goal', label: target.label, tree: goal })
    } else if (target.kind === 'task') {
      const task = goals.find((goal) => goal.id === target.goalId)?.tasks.find((candidate) => candidate.id === target.taskId)
      if (task) references.push({ kind: 'task', label: target.label, tree: task })
    } else if (target.kind === 'subtask') {
      const subtask = goals.find((goal) => goal.id === target.goalId)?.tasks
        .find((task) => task.id === target.taskId)?.subTasks.find((candidate) => candidate.id === target.subTaskId)
      if (subtask) references.push({ kind: 'subtask', label: target.label, tree: subtask })
    }
  }
  return references.length ? JSON.stringify(references).slice(0, 80_000) : null
}

const turnAttachmentContexts = (turn: AiMagicChatTurn) =>
  Array.isArray(turn.attachmentContexts) ? turn.attachmentContexts : []

const ensureTurnAttachmentContexts = (turns: AiMagicChatTurn[], attachments: AiChatAttachment[]) => {
  if (attachments.length === 0 || turns.some((turn) => turnAttachmentContexts(turn).length > 0)) return turns
  const lastUserIndex = [...turns].map((turn, index) => ({ turn, index })).reverse().find(({ turn }) => turn.role === 'USER')?.index
  if (lastUserIndex === undefined) return turns
  return turns.map((turn, index) => index === lastUserIndex ? { ...turn, attachmentContexts: attachments } : turn)
}

const sanitizeTurnForStorage = (turn: AiMagicChatTurn): AiMagicChatTurn => ({
  ...turn,
  text: turn.role === 'USER' ? stripClarificationEnvelope(turn.text) : turn.text,
  attachmentPreviews: turn.attachmentPreviews?.map((item) => ({
    ...item,
    url: item.url?.startsWith('blob:') ? undefined : item.url,
  })),
})

type AiMagicModeState = {
  activeChatId: string | null
  isDraftingNewChat: boolean
  chatTurns: AiMagicChatTurn[]
  prompt: string
  imageFiles: File[]
  sessionImageFiles: File[]
  pendingUploadFiles: Array<{ id: string; file: File }>
  dataContexts: DataContext[]
  sessionDataContexts: DataContext[]
  editTargets: EditTarget[]
  quickControls: AiMagicQuickControl[]
  quickSelections: Record<string, string[]>
  clarificationTurnId: string | null
  /**
   * Per-question answers collected inside the currently-open InterviewQuestionSet.
   * Cleared alongside `clarificationTurnId` when the set is submitted or skipped.
   */
  interviewAnswers: InterviewAnswers
  /** id of the question the composer's free-text bar is currently answering. */
  focusedQuestionId: string | null
  attachmentDigest: AttachmentPlanningDigest | null
  attachmentDigestFingerprint: string | null
  attachmentDigestStatus: 'idle' | 'analyzing' | 'ready' | 'error'
  attachmentDigestError: string | null
}

const createEmptyModeState = (): AiMagicModeState => ({
  activeChatId: null,
  isDraftingNewChat: true,
  chatTurns: [],
  prompt: '',
  imageFiles: [],
  sessionImageFiles: [],
  pendingUploadFiles: [],
  dataContexts: [],
  sessionDataContexts: [],
  editTargets: [],
  quickControls: [],
  quickSelections: {},
  clarificationTurnId: null,
  interviewAnswers: {},
  focusedQuestionId: null,
  attachmentDigest: null,
  attachmentDigestFingerprint: null,
  attachmentDigestStatus: 'idle',
  attachmentDigestError: null,
})

const resolveStateAction = <T,>(current: T, update: SetStateAction<T>) =>
  typeof update === 'function' ? (update as (value: T) => T)(current) : update

const CREATE_CHIPS = [
  'Plan my week',
  'Draft a checklist',
  'Design a form',
  'Suggest KPIs for this board',
] as const

const EXECUTE_CHIPS = [
  'Move stalled items forward',
  'Send a status update',
  'Regenerate the schedule',
  'Assign due dates for open items',
] as const

const renderLineCardText = (text: string) => {
  const lines = text.split('\n')
  return lines.map((line, lineIndex) => {
    const trimmed = line.trim()
    const isHeading = /^#{1,4}\s+/.test(trimmed)
    if (isHeading) {
      const headingText = trimmed.replace(/^#{1,4}\s+/, '')
      return (
        <strong key={lineIndex} style={{ display: 'block', margin: '6px 0 2px' }}>
          {headingText}
        </strong>
      )
    }
    return (
      <Fragment key={lineIndex}>
        {line}
        {lineIndex < lines.length - 1 ? '\n' : null}
      </Fragment>
    )
  })
}

const AiGeneratorPanelContent = ({
  isOpen,
  onOpenManage,
  onOpenAutomate,
  onGeneratedGoal,
  handoffRequest,
  activeBoard = 'DASHBOARD',
  externalOpenSessionRequest,
  forcedOperatingMode = null,
  hideModeTabs = false,
  layout = 'desktop',
  onNewChatStarted,
  effectiveControls,
  aiBudgetCounter,
}: {
  isOpen: boolean
  activeBoard?: TimelineBoardKey
  onOpenManage?: () => void
  onOpenAutomate?: (workflowId: string) => void
  onGeneratedGoal?: (
    goal: Goal,
    meta?: GeneratedGoalMeta,
  ) => void
  handoffRequest?: AiChatHandoffRequest | null
  externalOpenSessionRequest?: { session: AiChatSession; nonce: number } | null
  forcedOperatingMode?: AiOperatingMode | null
  hideModeTabs?: boolean
  layout?: 'desktop' | 'mobile'
  onNewChatStarted?: () => void
  effectiveControls?: MemberControls | null
  aiBudgetCounter?: MemberAiDailyBudgetCounter | null
}) => {
  const { t } = useNls()
  const memberLayout = useMemberLayout()
  const { operatingMode, setOperatingMode, isPlanningMode, setIsPlanningMode, setPhase, isMobileMenuOpen, setIsMobileMenuOpen } = useAiChat()
  const effectiveOperatingMode = forcedOperatingMode ?? operatingMode
  const isMobileLayout = layout === 'mobile'
  const { currentUser } = useAuth()
  const { restricted: isAccessRestricted } = useAccessRestriction()
  const aiSuggestionsEnabled = useWorkspaceAiSuggestions()
  const { activeWorkspaceId, activeRole, canPerform, workspaces } = useWorkspace()
  const [linePolicy, setLinePolicy] = useState<LineWorkspacePolicy>(DEFAULT_LINE_WORKSPACE_POLICY)
  useEffect(() => {
    let active = true
    void getLineWorkspacePolicy(activeWorkspaceId).then((policy) => {
      if (active) setLinePolicy(policy)
    })
    return () => { active = false }
  }, [activeWorkspaceId])
  const { setActiveChannelId } = useChat()
  const { startTour } = useTourGuide()

  const credits = useCredits()
  const { plan, entitlements } = useSubscription()
  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null,
    [activeWorkspaceId, workspaces],
  )
  const tierPlanName = useMemo(() => (
    PRICING_CATALOG.teamPlans.find((entry) => entry.id === plan)?.name
      ?? PRICING_CATALOG.personalPlans.find((entry) => entry.id === plan)?.name
      ?? ''
  ), [plan])
  const {
    tier: activeAiTier,
    availableTiers: aiAvailableTiers,
    lockedTiers: aiLockedTiers,
    setTier: setActiveAiTier,
    isLegacyPlan: isTierSwitcherLegacyPlan,
  } = useAiTier({ workspaceId: activeWorkspaceId, planId: plan, limitsLifted: limitsLiftedForWorkspace(activeWorkspace) })
  const [tierUpsell, setTierUpsell] = useState<{ tier: AiTierId; trigger: 'locked_chip' | 'quota_exhausted' } | null>(null)
  const handleLockedTierClick = useCallback((attempted: AiTierId) => {
    trackAiTierEvent({ name: 'line_tier_locked_click', props: { attemptedTier: attempted, plan: plan ?? null } })
    if (isTierUpsellOnCooldown(attempted)) return
    trackAiTierEvent({ name: 'line_upsell_shown', props: { trigger: 'locked_chip', attemptedTier: attempted } })
    setTierUpsell({ tier: attempted, trigger: 'locked_chip' })
  }, [plan])
  const handleTierChange = useCallback((next: AiTierId) => {
    if (next === activeAiTier) return
    trackAiTierEvent({ name: 'line_tier_switched', props: { from: activeAiTier, to: next, plan: plan ?? null } })
    setActiveAiTier(next)
  }, [activeAiTier, plan, setActiveAiTier])
  // U5: "Show me how" secondary — launch the registry-attached tour for a
  // recently completed execute action. teachAction is called for telemetry;
  // the actual tour start uses the tourId stamped on the turn at dispatch time.
  const handleShowMeHow = useCallback(async (tourId: string) => {
    // Best-effort telemetry: find the ActionId whose tourId matches the stamp.
    // The tour launches regardless of whether a registry match is found.
    const matchingEntry = Object.entries(LINE_EXECUTE_KIND_TO_ACTION_ID).find(
      ([, actionId]) => actionId && getAction(actionId)?.tourId === tourId,
    )
    if (matchingEntry) {
      const actionId = matchingEntry[1] as string
      await teachAction(actionId, {
        workspaceId: activeWorkspaceId,
        actor: { uid: currentUser?.uid ?? '' },
        canPerform,
        invokedFrom: 'ai',
      })
    }
    startTour(tourId, { force: true })
  }, [activeWorkspaceId, canPerform, currentUser?.uid, startTour])

  const tierSwitcherShownRef = useRef(false)
  useEffect(() => {
    if (tierSwitcherShownRef.current) return
    if (!lineFeatureFlags.aiTierSwitcher || isTierSwitcherLegacyPlan) return
    tierSwitcherShownRef.current = true
    trackAiTierEvent({
      name: 'line_tier_switcher_shown',
      props: { plan: plan ?? null, defaultTier: activeAiTier, availableTiers: aiAvailableTiers },
    })
  }, [activeAiTier, aiAvailableTiers, isTierSwitcherLegacyPlan, plan])
  const { canEditElement } = useCanvasPermissions()
  useEffect(() => {
    if (!forcedOperatingMode || operatingMode === forcedOperatingMode) return
    setOperatingMode(forcedOperatingMode)
  }, [forcedOperatingMode, operatingMode, setOperatingMode])
  const handleOperatingModeChange = useCallback((nextMode: AiOperatingMode) => {
    if (forcedOperatingMode) {
      if (operatingMode !== forcedOperatingMode) setOperatingMode(forcedOperatingMode)
      return
    }
    setOperatingMode(nextMode)
  }, [forcedOperatingMode, operatingMode, setOperatingMode])
  // `useData().goals` is a legacy nested document that the AI-pending-save
  // path never updates — so reading from it lets the AI see stale or empty
  // dates for items the user has actually been editing. The flat
  const { elements, checklistItems, createElements, canPerformOnNode, memberProfile } = useTimeline()
  const [subscribedControls, setSubscribedControls] = useState<MemberControls | null>(null)
  useEffect(() => {
    if (effectiveControls !== undefined) return
    if (!activeWorkspaceId) return
    return subscribeToMyEffectiveControls(activeWorkspaceId, setSubscribedControls)
  }, [activeWorkspaceId, effectiveControls])

  const controls = effectiveControls !== undefined ? effectiveControls : subscribedControls
  const aiControl = controls?.ai
  const aiMode = aiControl?.mode ?? 'INHERIT'
  const counter = aiBudgetCounter ?? memberProfile?.aiBudgetCounter
  const committed = counter?.committed ?? 0
  const dailyCredits = aiControl?.dailyCredits ?? 0

  let aiNoticeMessage: string | null = null
  let isAiBlocked = false

  if (aiMode === 'OFF') {
    aiNoticeMessage = t('NLSControlNoticeAiOff')
    isAiBlocked = true
  } else if (aiMode === 'BUDGET' && dailyCredits > 0 && committed >= dailyCredits) {
    aiNoticeMessage = t('NLSControlNoticeAiBudgetExceeded')
    isAiBlocked = true
  }
  const boardElements = elements
  const goals = useMemo(() => buildGoalsFromTimelineElements(elements), [elements])
  const lineExecuteWebIntents = useMemo(() => [
    ...WEB_INTENT_CATALOG,
    ...buildWorkspaceDynamicIntentCatalog({
      workspaceId: activeWorkspaceId,
      workspaceSlug: activeWorkspace?.slug || activeWorkspaceId,
      board: activeBoard,
      timelineElements: boardElements,
      checklistItems,
    }),
  ], [activeBoard, activeWorkspace?.slug, activeWorkspaceId, boardElements, checklistItems])
  // Kept around because Onboarding still needs it elsewhere — intentionally
  // unused here. Suppress lint via a void reference.
  const dataContext = useData()
  void dataContext
  const { showToast } = useToast()
  const { runWithGlobalLoading } = useGlobalActionLoading()
  const [modeState, setModeState] = useState<Record<AiOperatingMode, AiMagicModeState>>(() => ({
    CREATE: createEmptyModeState(),
    EXECUTE: createEmptyModeState(),
  }))
  const modeStateRef = useRef(modeState)
  useEffect(() => {
    modeStateRef.current = modeState
  }, [modeState])
  const updateModeState = useCallback((
    mode: AiOperatingMode,
    updater: (current: AiMagicModeState) => AiMagicModeState,
  ) => {
    setModeState((current) => ({
      ...current,
      [mode]: updater(current[mode] ?? createEmptyModeState()),
    }))
  }, [])
  const [createPromptDraft, setCreatePromptDraft] = useWorkspaceDraft('draft.aiPrompt.CREATE')
  const [executePromptDraft, setExecutePromptDraft] = useWorkspaceDraft('draft.aiPrompt.EXECUTE')

  const hasHydratedCreateRef = useRef(false)
  const hasHydratedExecuteRef = useRef(false)

  // Hydrate each mode's prompt from its draft once on mount only
  useEffect(() => {
    if (!hasHydratedCreateRef.current) {
      hasHydratedCreateRef.current = true
      if (createPromptDraft) {
        updateModeState('CREATE', (current) => ({
          ...current,
          prompt: current.prompt || createPromptDraft,
        }))
      }
    }
  }, [createPromptDraft, updateModeState])

  useEffect(() => {
    if (!hasHydratedExecuteRef.current) {
      hasHydratedExecuteRef.current = true
      if (executePromptDraft) {
        updateModeState('EXECUTE', (current) => ({
          ...current,
          prompt: current.prompt || executePromptDraft,
        }))
      }
    }
  }, [executePromptDraft, updateModeState])

  // Sync modeState prompts to their respective workspace drafts
  useEffect(() => {
    if (hasHydratedCreateRef.current) {
      setCreatePromptDraft(modeState.CREATE.prompt)
    }
  }, [modeState.CREATE.prompt, setCreatePromptDraft])

  useEffect(() => {
    if (hasHydratedExecuteRef.current) {
      setExecutePromptDraft(modeState.EXECUTE.prompt)
    }
  }, [modeState.EXECUTE.prompt, setExecutePromptDraft])

  const currentModeState = modeState[effectiveOperatingMode] ?? createEmptyModeState()
  const setCurrentModeState = useCallback((updater: (current: AiMagicModeState) => AiMagicModeState) => {
    updateModeState(effectiveOperatingMode, updater)
  }, [effectiveOperatingMode, updateModeState])

  const activePromptDraft = effectiveOperatingMode === 'CREATE' ? createPromptDraft : executePromptDraft
  const setActivePromptDraft = useCallback((nextPrompt: string) => {
    if (effectiveOperatingMode === 'CREATE') {
      setCreatePromptDraft(nextPrompt)
    } else {
      setExecutePromptDraft(nextPrompt)
    }
  }, [effectiveOperatingMode, setCreatePromptDraft, setExecutePromptDraft])

  const prompt = currentModeState.prompt || (!hasHydratedCreateRef.current && !hasHydratedExecuteRef.current ? activePromptDraft : '')
  const setPrompt = useCallback((update: SetStateAction<string>) => {
    setCurrentModeState((current) => {
      const nextPrompt = resolveStateAction(current.prompt, update)
      return { ...current, prompt: nextPrompt }
    })
  }, [setCurrentModeState])
  const [interimSpeech, setInterimSpeech] = useState('')
  // Multi-image attachments. Each pick appends to the array; chips render
  // above the textarea and route through `generateTimelineGoalFromImages`
  // in a single Gemini call.
  const imageFiles = currentModeState.imageFiles
  const setImageFiles = useCallback((update: SetStateAction<File[]>) => {
    setCurrentModeState((current) => ({ ...current, imageFiles: resolveStateAction(current.imageFiles, update) }))
  }, [setCurrentModeState])
  const sessionImageFiles = currentModeState.sessionImageFiles
  const pendingUploadFiles = currentModeState.pendingUploadFiles
  const setPendingUploadFiles = useCallback((update: SetStateAction<Array<{ id: string; file: File }>>) => {
    setCurrentModeState((current) => ({ ...current, pendingUploadFiles: resolveStateAction(current.pendingUploadFiles, update) }))
  }, [setCurrentModeState])
  // Locally-parsed data sources (spreadsheets, docs, linked sheets). Held in
  // state until Generate; their text rides into the Gemini call but stays out
  // of the char-counted textarea so it isn't billed twice.
  const dataContexts = currentModeState.dataContexts
  const setDataContexts = useCallback((update: SetStateAction<DataContext[]>) => {
    setCurrentModeState((current) => ({ ...current, dataContexts: resolveStateAction(current.dataContexts, update) }))
  }, [setCurrentModeState])
  const sessionDataContexts = currentModeState.sessionDataContexts
  const attachmentDigest = currentModeState.attachmentDigest
  const attachmentDigestFingerprint = currentModeState.attachmentDigestFingerprint
  const attachmentDigestStatus = currentModeState.attachmentDigestStatus
  const attachmentDigestError = currentModeState.attachmentDigestError
  useEffect(() => {
    // Browser File objects cannot survive reload. Drop orphaned local-upload
    // placeholders rather than creating a history record with an unreadable file.
    setDataContexts((current) => current.filter((context) => !context.id.startsWith('pending:')))
  }, [setDataContexts])
  const { snapshot: aiJobSnapshot, beginJob, abort: abortAiJob, claim: claimAiJob, subscribeAutoHandoff } = useAiJob()
  // Derive in-flight state from the global AiJobContext so the spinner / Send
  // button / close gating survive panel close + reopen. Source filter ensures
  // we don't pick up a Report Generator job running in parallel (kept for
  // future — concurrency is single-job today).
  const isAiBusy = aiJobSnapshot.status === 'running'
    && aiJobSnapshot.source === 'AI_GENERATOR'
    && (!aiJobSnapshot.workspaceId || aiJobSnapshot.workspaceId === activeWorkspaceId)
  const isAiReady = aiJobSnapshot.status === 'ready'
    && aiJobSnapshot.source === 'AI_GENERATOR'
    && (!aiJobSnapshot.workspaceId || aiJobSnapshot.workspaceId === activeWorkspaceId)
  const isGenerating = isAiBusy

  const isAiBusyInOtherWorkspace = aiJobSnapshot.status === 'running'
    && Boolean(aiJobSnapshot.workspaceId && activeWorkspaceId && aiJobSnapshot.workspaceId !== activeWorkspaceId)
  const busyOtherWorkspace = isAiBusyInOtherWorkspace
    ? workspaces.find((w) => w.id === aiJobSnapshot.workspaceId)
    : null
  const busyOtherWorkspaceName = busyOtherWorkspace?.name || busyOtherWorkspace?.slug || aiJobSnapshot.workspaceId || ''

  if (isAiBusyInOtherWorkspace) {
    if (!aiNoticeMessage) {
      aiNoticeMessage = t('NLSWorkspaceSwitchAiJobBusyInOtherWorkspace', { workspaceName: busyOtherWorkspaceName })
    }
    isAiBlocked = true
  }
  const [, setError] = useState<string | null>(null)
  const [, setSuccessMessage] = useState('')
  const [cooldownRemaining, setCooldownRemaining] = useState(0)
  const [isListening, setIsListening] = useState(false)
  // Up to 3 existing nodes (all within one goal) selected as edit targets.
  // Empty ⇒ "generate new"; non-empty ⇒ the message edits those nodes.
  const editTargets = currentModeState.editTargets
  const setEditTargets = useCallback((update: SetStateAction<EditTarget[]>) => {
    setCurrentModeState((current) => ({ ...current, editTargets: resolveStateAction(current.editTargets, update) }))
  }, [setCurrentModeState])
  const chatTurns = currentModeState.chatTurns
  const setChatTurns = useCallback((update: SetStateAction<AiMagicChatTurn[]>) => {
    setCurrentModeState((current) => ({ ...current, chatTurns: resolveStateAction(current.chatTurns, update) }))
  }, [setCurrentModeState])
  const [chatSessions, setChatSessions] = useState<AiChatSession[]>([])
  const [previewItem, setPreviewItem] = useState<AttachmentPreviewItem | null>(null)
  const [expandedChatContextId, setExpandedChatContextId] = useState<string | null>(null)
  const [isContextPickerOpen, setIsContextPickerOpen] = useState(false)
  const activeChatId = currentModeState.activeChatId
  const isDraftingNewChat = currentModeState.isDraftingNewChat

  const [historyLimitOpen, setHistoryLimitOpen] = useState(false)
  const [renameDialogSession, setRenameDialogSession] = useState<AiChatSession | null>(null)
  const [renameDialogValue, setRenameDialogValue] = useState('')
  const [deleteDialog, setDeleteDialog] = useState<PendingDeleteDialog | null>(null)
  const loadedChatIdByModeRef = useRef<Record<AiOperatingMode, string | null>>({ CREATE: null, EXECUTE: null })
  const pendingHistorySessionIdsRef = useRef(new Set<string>())
  const creatingChatRef = useRef(false)
  const legacyDraftCleanupRef = useRef(new Set<string>())
  const nextUserDisplayTextRef = useRef<string | null>(null)
  const mobileTextareaRef = useRef<HTMLTextAreaElement>(null)
  // Remembers which region (composer vs interview) of the mobile keyboard
  // dock was last actually focused, so that if the dock is active but focus
  // has landed on something in neither tagged region, we can deterministically
  // keep showing whichever one the user was last interacting with instead of
  // rendering both. Defaults to 'composer' since that is always present.
  const lastKeyboardDockRegionRef = useRef<'composer' | 'interview'>('composer')
  // Synchronous submit lock — isGenerating is React state and lags one render
  // behind a key/click, so rapid Enter presses can stack multiple requests
  // before the busy flag flips. This ref blocks the second press immediately.
  const submittingRef = useRef(false)
  const quickControls = currentModeState.quickControls
  const setQuickControls = useCallback((update: SetStateAction<AiMagicQuickControl[]>) => {
    setCurrentModeState((current) => ({ ...current, quickControls: resolveStateAction(current.quickControls, update) }))
  }, [setCurrentModeState])
  const [, setRoutingFeedback] = useState<{
    status: AIResultStatus
    confidence: number
    routing: RoutingPayload
    reasoning: string[]
  } | null>(null)
  const quickSelections = currentModeState.quickSelections
  const setQuickSelections = useCallback((update: SetStateAction<Record<string, string[]>>) => {
    setCurrentModeState((current) => ({ ...current, quickSelections: resolveStateAction(current.quickSelections, update) }))
  }, [setCurrentModeState])
  const clarificationTurnId = currentModeState.clarificationTurnId
  const setClarificationTurnId = useCallback((update: SetStateAction<string | null>) => {
    setCurrentModeState((current) => ({ ...current, clarificationTurnId: resolveStateAction(current.clarificationTurnId, update) }))
  }, [setCurrentModeState])
  const interviewAnswers = currentModeState.interviewAnswers
  const setInterviewAnswers = useCallback((update: SetStateAction<InterviewAnswers>) => {
    setCurrentModeState((current) => ({ ...current, interviewAnswers: resolveStateAction(current.interviewAnswers, update) }))
  }, [setCurrentModeState])
  const focusedQuestionId = currentModeState.focusedQuestionId
  const setFocusedQuestionId = useCallback((update: SetStateAction<string | null>) => {
    setCurrentModeState((current) => ({ ...current, focusedQuestionId: resolveStateAction(current.focusedQuestionId, update) }))
  }, [setCurrentModeState])
  type OffTopicPending = { status: 'CHECKING' | 'CONFIRM'; value: string } | null
  const [offTopicPending, setOffTopicPending] = useState<OffTopicPending>(null)
  const [snoozedSuggestionIds, setSnoozedSuggestionIds] = useState<Set<string>>(new Set())
  const [dismissedActionTurnIds, setDismissedActionTurnIds] = useState<Set<string>>(new Set())
  /**
   * Rule-chip state. `savedRuleTurnIds` = user accepted; `dismissedRuleTurnIds`
   * = user closed the chip; `savingRuleTurnId` = write in flight.
   */
  const [savedRuleTurnIds, setSavedRuleTurnIds] = useState<Set<string>>(new Set())
  const [dismissedRuleTurnIds, setDismissedRuleTurnIds] = useState<Set<string>>(new Set())
  const [savingRuleTurnId, setSavingRuleTurnId] = useState<string | null>(null)
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({})
  const [progressMessage, setProgressMessage] = useState('')
  const [progressSequence, setProgressSequence] = useState<string[]>([])
  const [progressOwner, setProgressOwner] = useState<AiMagicRunScope | null>(null)
  const updateVisibleRunState = useCallback((
    scope: AiMagicRunScope,
    updater: (current: AiMagicModeState) => AiMagicModeState,
  ) => {
    updateModeState(scope.mode, (current) => (
      isAiMagicRunVisible(current, scope) ? updater(current) : current
    ))
  }, [updateModeState])
  const setRunChatTurns = useCallback((scope: AiMagicRunScope, update: SetStateAction<AiMagicChatTurn[]>) => {
    updateVisibleRunState(scope, (current) => ({
      ...current,
      chatTurns: resolveStateAction(current.chatTurns, update),
    }))
  }, [updateVisibleRunState])
  const setRunProgress = useCallback((scope: AiMagicRunScope, message: string, sequence: string[] = []) => {
    setProgressOwner({ ...scope })
    setProgressMessage(message)
    setProgressSequence(sequence)
  }, [])
  const clearRunProgress = useCallback((scope: AiMagicRunScope) => {
    setProgressOwner((current) => {
      if (
        current?.mode === scope.mode
        && current.initialChatId === scope.initialChatId
        && current.userTurnId === scope.userTurnId
      ) {
        setProgressMessage('')
        setProgressSequence([])
        return null
      }
      return current
    })
  }, [])
  const attachRunChatId = useCallback((scope: AiMagicRunScope, chatId: string) => {
    const draftScope = { ...scope }
    scope.resolvedChatId = chatId
    setProgressOwner((current) => (
      current?.mode === scope.mode
      && current.initialChatId === scope.initialChatId
      && current.userTurnId === scope.userTurnId
        ? { ...current, resolvedChatId: chatId }
        : current
    ))
    updateModeState(scope.mode, (current) => {
      if (!canPromoteAiMagicDraftRun(current, draftScope)) return current
      loadedChatIdByModeRef.current[scope.mode] = chatId
      return {
        ...current,
        activeChatId: chatId,
        isDraftingNewChat: false,
      }
    })
  }, [updateModeState])
  const patchHistorySession = useCallback((chatId: string, patch: Partial<AiChatSession>) => {
    setChatSessions((current) => current.map((session) => (
      session.id === chatId ? { ...session, ...patch } : session
    )))
  }, [])
  const appendRunLineMessage = useCallback((scope: AiMagicRunScope, text: string, tone: 'ASSISTANT' | 'WARNING' = 'ASSISTANT') => {
    setRunChatTurns(scope, (current) => {
      const last = current[current.length - 1]
      if (last?.role === tone && last.text === text) return current
      return [...current, { id: createChatTurnId(), role: tone, text }]
    })
  }, [setRunChatTurns])
  const isProgressVisible = progressOwner ? isAiMagicRunVisible(currentModeState, progressOwner) : false
  const isLineThinking = isProgressVisible && (isAiBusy || Boolean(progressMessage))
  const [upgradeLocked, setUpgradeLocked] = useState(false)
  const speechRecognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const effectiveAiLimit = useCallback((feature: 'aiChatHistories' | 'aiContextAttachments') => effectiveEntitlementLimit([
    getTierLimit(plan, feature, activeWorkspace),
    ...entitlements.filter((entry) => entry.status === 'ACTIVE').map((entry) => LIFETIME_PASSES[entry.passId].limits[feature]).filter((value): value is number => typeof value === 'number'),
  ]), [activeWorkspace, entitlements, plan])
  const attachmentLimit = effectiveAiLimit('aiContextAttachments')
  const attachmentCap = attachmentLimit === -1 ? Number.MAX_SAFE_INTEGER : attachmentLimit
  const checklistCapabilityContext = useMemo(
    () => createAiChecklistCapabilityContext({
      workspace: activeWorkspace,
      attachmentContextLimit: attachmentLimit,
    }),
    [activeWorkspace, attachmentLimit],
  )
  const liveEditTargets = useMemo<EditTarget[]>(() => {
    const byId = new Map(boardElements.map((element) => [element.id, element]))
    return editTargets.map((target): EditTarget | null => {
      // P4: mention-only kinds pass through unchanged — they don't map to
      // a timeline element, so the goal/task/subtask normalization below
      // does not apply.
      if (!isEditableTarget(target)) return target
      const selectedId = target.kind === 'goal'
        ? target.goalId
        : target.kind === 'task'
          ? target.taskId
          : target.kind === 'subtask'
            ? target.subTaskId
            : target.itemId
      const element = byId.get(selectedId)
      if (!element) return target
      if (element.type === 'GOAL') return { kind: 'goal', goalId: element.id, label: element.title }
      if (element.type === 'TASK') {
        const goalId = element.parentId ?? target.goalId
        return goalId ? { kind: 'task', goalId, taskId: element.id, label: element.title } : target
      }
      const taskId = element.parentId ?? (target.kind === 'subtask' ? target.taskId : '')
      const task = taskId ? byId.get(taskId) : null
      const goalId = task?.parentId ?? target.goalId
      return taskId && goalId ? { kind: 'subtask', goalId, taskId, subTaskId: element.id, label: element.title } : target
    }).filter((target): target is EditTarget => target !== null)
  }, [boardElements, editTargets])

  const operationalTargetRefs = useMemo(
    () => liveEditTargets
      .map((target) => toOperationalTargetRef(target, boardElements, activeBoard))
      .filter((ref): ref is AiOperationTargetRef => ref !== null),
    [activeBoard, boardElements, liveEditTargets],
  )

  const localPulseSnapshot = useMemo(
    () => buildLocalWorkspacePulseSnapshot(activeWorkspaceId, boardElements),
    [activeWorkspaceId, boardElements],
  )
  const integrationRuntimeState = useIntegrationSubscriptions(
    activeWorkspaceId,
    Boolean(activeWorkspaceId && currentUser && !currentUser.isAnonymous && canPerform('integrations.view')),
  )
  const activeDataContexts = useMemo(
    () => [...sessionDataContexts, ...dataContexts],
    [dataContexts, sessionDataContexts],
  )
  const activeImageFiles = useMemo(
    () => [...sessionImageFiles, ...imageFiles],
    [imageFiles, sessionImageFiles],
  )
  const attachedContextFiles = useMemo<WorkspaceFileMetadata[]>(() => activeDataContexts.map((context) => ({
    id: context.fileId ?? context.id,
    workspaceId: activeWorkspaceId,
    fileName: context.label,
    source: 'LOCAL',
    status: 'ACTIVE',
    visibility: context.kind === 'upload' ? 'PRIVATE' : 'WORKSPACE_SHARED',
    sizeBytes: context.sizeBytes ?? 0,
    mimeType: context.mimeType ?? 'text/plain',
    uploaderId: currentUser?.uid ?? '',
    uploaderName: currentUser?.displayName ?? currentUser?.email ?? 'Workspace member',
    artifactType: context.kind === 'upload' ? 'AI_MAGIC_UPLOAD' : 'AI_CONTEXT',
    artifactId: context.fileId ?? context.id,
    version: 1,
  })), [activeDataContexts, activeWorkspaceId, currentUser?.displayName, currentUser?.email, currentUser?.uid])
  const integrationRuntimeRecords = useMemo(() => activeWorkspaceId
    ? buildIntegrationRuntimeRecords({
      workspaceId: activeWorkspaceId,
      connections: integrationRuntimeState.connections,
      files: attachedContextFiles,
    })
    : [], [activeWorkspaceId, attachedContextFiles, integrationRuntimeState.connections])
  const pulseOperationalHints = useMemo(
    () => workspacePulseToOperationalHints(localPulseSnapshot),
    [localPulseSnapshot],
  )

  const persistedSessionAttachments = useMemo<AiChatAttachment[]>(
    () => buildAiChatAttachmentPayloads(liveEditTargets, sessionDataContexts),
    [liveEditTargets, sessionDataContexts],
  )

  const sessionAttachments = useMemo<AiChatAttachment[]>(
    () => buildAiChatAttachmentPayloads(liveEditTargets, [...sessionDataContexts, ...dataContexts]),
    [dataContexts, liveEditTargets, sessionDataContexts],
  )

  const draftAttachmentPreviews = useMemo<AttachmentPreviewItem[]>(() => {
    const pendingByContextId = new Map<string, File>(pendingUploadFiles.map((entry) => [`pending:${entry.id}`, entry.file]))
    return dataContexts.map((context) => {
      const pendingFile = pendingByContextId.get(context.id)
      const localUrl = pendingFile && (
        pendingFile.type.startsWith('image/')
        || pendingFile.type.startsWith('audio/')
        || pendingFile.type.startsWith('video/')
        || pendingFile.type === 'application/pdf'
      )
        ? URL.createObjectURL(pendingFile)
        : undefined
      return {
        id: context.id,
        kind: previewKindForContext(context),
        label: context.label,
        summary: context.kind === 'gsheet' ? 'Linked sheet' : context.kind,
        mimeType: context.mimeType,
        sizeLabel: typeof context.sizeBytes === 'number' ? formatFileSize(context.sizeBytes) : undefined,
        fileId: context.fileId,
        url: localUrl,
        downloadUrl: localUrl,
      }
    }).slice(0, attachmentCap)
  }, [attachmentCap, dataContexts, pendingUploadFiles])
  const draftChatContextById = useMemo(
    () => new Map(dataContexts.filter((context) => context.kind === 'chat').map((context) => [context.id, context])),
    [dataContexts],
  )
  const expandedChatContext = expandedChatContextId ? draftChatContextById.get(expandedChatContextId) ?? null : null

  useEffect(() => () => {
    draftAttachmentPreviews.forEach((item) => {
      if (item.url?.startsWith('blob:')) URL.revokeObjectURL(item.url)
    })
  }, [draftAttachmentPreviews])

  const buildTurnAttachmentPreviews = useCallback((): AttachmentPreviewItem[] => {
    const pendingByContextId = new Map(pendingUploadFiles.map((entry) => [`pending:${entry.id}`, entry.file]))
    const previews: AttachmentPreviewItem[] = liveEditTargets.map((target) => ({
      id: targetKey(target),
      kind: 'timeline',
      label: target.label,
      summary: target.kind,
    }))
    dataContexts.forEach((context) => {
      const pendingFile = pendingByContextId.get(context.id)
      const localUrl = pendingFile && (
        pendingFile.type.startsWith('image/')
        || pendingFile.type.startsWith('audio/')
        || pendingFile.type.startsWith('video/')
        || pendingFile.type === 'application/pdf'
      )
        ? URL.createObjectURL(pendingFile)
        : undefined
      previews.push({
        id: context.id,
        kind: previewKindForContext(context),
        label: context.label,
        summary: context.kind === 'gsheet' ? 'Linked sheet' : context.kind,
        mimeType: context.mimeType,
        sizeLabel: typeof context.sizeBytes === 'number' ? formatFileSize(context.sizeBytes) : undefined,
        fileId: context.fileId,
        url: localUrl,
      })
    })
    return previews.slice(0, attachmentCap)
  }, [attachmentCap, dataContexts, liveEditTargets, pendingUploadFiles])

  const clearDraftUi = useCallback(() => {
    setActivePromptDraft('')
    loadedChatIdByModeRef.current[effectiveOperatingMode] = null
    setCurrentModeState((current) => ({
      ...current,
      activeChatId: null,
      isDraftingNewChat: true,
      chatTurns: [],
      prompt: '',
      imageFiles: [],
      sessionImageFiles: [],
      pendingUploadFiles: [],
      dataContexts: [],
      sessionDataContexts: [],
      editTargets: [],
      quickControls: [],
      quickSelections: {},
      clarificationTurnId: null,
      interviewAnswers: {},
      focusedQuestionId: null,
      attachmentDigest: null,
      attachmentDigestFingerprint: null,
      attachmentDigestStatus: 'idle',
      attachmentDigestError: null,
    }))
  }, [effectiveOperatingMode, setActivePromptDraft, setCurrentModeState])

  const loadSession = useCallback((session: AiChatSession) => {
    if (forcedOperatingMode && session.operatingMode !== forcedOperatingMode) return
    const rawTurns = Array.isArray(session.turns) ? session.turns : []
    const activePendingProposal = getActivePendingProposal(session.pendingProposal)
    // A session that has a pending mirror treats that mirror as canonical. Do
    // not let the older, turn-embedded snapshot resurrect an expired card.
    const turnsBeforeAttachmentHydration = session.pendingProposal && !activePendingProposal
      ? rawTurns.map((turn) => turn.id === session.pendingProposal?.turnId
        ? { ...turn, actions: [], proposalSummary: null, proposalPreview: null, proposalRevisionRequested: false }
        : turn)
      : withPendingProposalAnchor(rawTurns, activePendingProposal)
    const storedTopLevel = Array.isArray(session.attachments) ? session.attachments : []
    const storedFromTurns = turnsBeforeAttachmentHydration.flatMap((turn) => turnAttachmentContexts(turn))
    const stored = mergeUniqueAiChatAttachments(storedTopLevel, storedFromTurns)
    const turns = ensureTurnAttachmentContexts(turnsBeforeAttachmentHydration, stored)
    const restoredPreviews: Record<string, PendingPreviewRecord> = {}
    turns.forEach((turn) => {
      if (!turn.id || turn.role !== 'ASSISTANT') return
      const preview = previewFromTurn(turn)
      if (preview) restoredPreviews[turn.id] = preview
    })
    if (activePendingProposal) {
      restoredPreviews[activePendingProposal.turnId] = {
        goal: activePendingProposal.goal,
        meta: {
          ...activePendingProposal.meta,
          baseline: activePendingProposal.meta.baseline ?? null,
        },
      }
    }
    if (session.pendingProposal && !activePendingProposal) {
      pendingPreviewsRef.current.delete(session.pendingProposal.turnId)
      writePendingPreviewStore(activeWorkspaceId, (current) => {
        const next = { ...current }
        delete next[session.pendingProposal!.turnId]
        return next
      })
      // Best effort: this cleanup is deliberately client-side, with no cron.
      void saveAiChatSession({
        id: session.id,
        turns: turns.map(sanitizeTurnForStorage),
        attachments: storedTopLevel,
        operatingMode: session.operatingMode,
        status: session.status,
        pendingProposal: null,
        touchHistoryOrder: false,
      }).catch(() => undefined)
    }
    if (Object.keys(restoredPreviews).length > 0) {
      pendingPreviewsRef.current = new Map([
        ...Object.entries(readPendingPreviewStore(activeWorkspaceId)),
        ...Object.entries(restoredPreviews),
      ])
      writePendingPreviewStore(activeWorkspaceId, (current) => ({ ...current, ...restoredPreviews }))
    }
    const pendingClarification = findOpenClarificationTurn(turns)
    const sessionMode = forcedOperatingMode ?? session.operatingMode
    loadedChatIdByModeRef.current[sessionMode] = session.id
    if (!forcedOperatingMode) setOperatingMode(sessionMode)
    const knownContextKinds = new Set<DataContextKind>([
      'spreadsheet',
      'document',
      'gsheet',
      'link',
      'asset',
      'upload',
      'chat',
      'form',
      'form_responses',
      'design',
    ])
    const contexts: DataContext[] = stored
      .filter((item) => item.kind !== 'timeline')
      .map((item) => {
        let kind: DataContextKind = 'upload'
        if (knownContextKinds.has(item.kind as DataContextKind)) {
          kind = item.kind as DataContextKind
        } else if (import.meta.env.DEV) {
          console.warn('[AiGeneratorPanel] Unknown attachment kind in session hydration:', item.kind)
        }
        return {
          id: item.id,
          kind,
          label: item.label,
          text: item.text ?? '',
          fileId: item.fileId,
          mimeType: item.mimeType,
          channelId: item.channelId,
          channelName: item.channelName,
          messages: item.messages,
          formId: item.formId,
          formResponseIds: item.formResponseIds,
          formResponseCount: item.formResponseCount,
        }
      })
    const persistedAttachmentDigest = findLatestAttachmentIntelligence(turns)
    const persistedAttachmentKey = persistedAttachmentDigest ? buildAttachmentAnalysisKey(contexts) : null
    if (sessionMode === 'CREATE') {
      setCreatePromptDraft('')
    } else {
      setExecutePromptDraft('')
    }
    updateModeState(sessionMode, (current) => ({
      ...current,
      activeChatId: session.id,
      isDraftingNewChat: false,
      chatTurns: turns,
      prompt: '',
      imageFiles: [],
      sessionImageFiles: [],
      pendingUploadFiles: [],
      dataContexts: [],
      sessionDataContexts: contexts,
      editTargets: stored.filter((item) => item.kind === 'timeline' && item.target).map((item) => item.target as EditTarget),
      quickControls: pendingClarification?.quickControls ?? [],
      quickSelections: {},
      clarificationTurnId: pendingClarification?.id ?? null,
      interviewAnswers: {},
      focusedQuestionId: null,
      attachmentDigest: persistedAttachmentDigest,
      attachmentDigestFingerprint: persistedAttachmentKey,
      attachmentDigestStatus: persistedAttachmentDigest ? 'ready' : 'idle',
      attachmentDigestError: null,
    }))
    void Promise.all(contexts.filter((item) => item.kind === 'upload' && item.mimeType?.startsWith('image/') && item.fileId).map(async (item) => {
      const response = await fetch(await getStorageFileUrl(item.fileId!))
      const blob = await response.blob()
      return new File([blob], item.label, { type: item.mimeType })
    })).then((files) => {
      updateModeState(sessionMode, (current) => current.activeChatId === session.id
        ? { ...current, sessionImageFiles: files }
        : current)
    }).catch(() => {
      updateModeState(sessionMode, (current) => current.activeChatId === session.id
        ? { ...current, sessionImageFiles: [] }
        : current)
    })
  }, [activeWorkspaceId, forcedOperatingMode, setCreatePromptDraft, setExecutePromptDraft, setOperatingMode, updateModeState])

  useEffect(() => {
    if (!externalOpenSessionRequest) return
    loadSession(externalOpenSessionRequest.session)
  }, [externalOpenSessionRequest?.nonce, externalOpenSessionRequest, loadSession])

  const startNewChat = useCallback(async () => {
    if (!activeWorkspaceId || creatingChatRef.current) return
    creatingChatRef.current = true
    clearDraftUi()
    onNewChatStarted?.()
    creatingChatRef.current = false
  }, [activeWorkspaceId, clearDraftUi, onNewChatStarted])

  const openRenameDialog = useCallback((session: AiChatSession) => {
    setRenameDialogSession(session)
    setRenameDialogValue(session.title)
  }, [])

  const closeRenameDialog = useCallback(() => {
    setRenameDialogSession(null)
    setRenameDialogValue('')
  }, [])

  const submitRenameDialog = useCallback(() => {
    if (!renameDialogSession) return
    const nextTitle = renameDialogValue.trim()
    if (!nextTitle) return
    return renameAiChatSession(renameDialogSession.id, nextTitle)
      .then(() => closeRenameDialog())
      .catch((error) => {
        showToast(error instanceof Error ? error.message : t('NLSAiToastCouldNotRenameChat'), 'error')
      })
  }, [closeRenameDialog, renameDialogSession, renameDialogValue, showToast])

  const openDeleteDialog = useCallback((session: AiChatSession, options?: { closeHistoryLimitOnConfirm?: boolean }) => {
    setDeleteDialog({
      session,
      closeHistoryLimitOnConfirm: Boolean(options?.closeHistoryLimitOnConfirm),
    })
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog(null)
  }, [])

  const confirmDeleteDialog = useCallback(() => {
    if (!deleteDialog) return
    const { session, closeHistoryLimitOnConfirm } = deleteDialog
    return deleteAiChatSession(session.id)
      .then(() => {
        pendingHistorySessionIdsRef.current.delete(session.id)
        if (activeChatId === session.id || closeHistoryLimitOnConfirm) clearDraftUi()
        if (closeHistoryLimitOnConfirm) setHistoryLimitOpen(false)
        closeDeleteDialog()
      })
      .catch((error) => {
        showToast(error instanceof Error ? error.message : t('NLSAiToastCouldNotDeleteChat'), 'error')
      })
  }, [activeChatId, clearDraftUi, closeDeleteDialog, deleteDialog, showToast])

  useEffect(() => {
    if (!activeWorkspaceId || !currentUser?.uid) return
    return subscribeAiChatSessions(activeWorkspaceId, currentUser.uid, (sessions) => {
      const legacyEmptyDrafts = sessions.filter(isLegacyEmptyAiChatDraft)
      legacyEmptyDrafts.forEach((session) => {
        if (legacyDraftCleanupRef.current.has(session.id)) return
        legacyDraftCleanupRef.current.add(session.id)
        void deleteAiChatSession(session.id).catch(() => legacyDraftCleanupRef.current.delete(session.id))
      })
      const visibleSessions = getVisibleAiMagicSessions(sessions, forcedOperatingMode)
      const visibleIds = new Set(visibleSessions.map((session) => session.id))
      pendingHistorySessionIdsRef.current.forEach((sessionId) => {
        if (visibleIds.has(sessionId)) pendingHistorySessionIdsRef.current.delete(sessionId)
      })
      setChatSessions((current) => {
        const optimisticPending = current.filter((session) =>
          pendingHistorySessionIdsRef.current.has(session.id)
          && !visibleIds.has(session.id))
        return [...optimisticPending, ...visibleSessions]
      })
      const selected = visibleSessions.find((session) => session.id === activeChatId)
      const sessionToOpen = selectAiMagicSessionToAutoOpen(visibleSessions, {
        activeChatId,
        effectiveOperatingMode,
        isDraftingNewChat,
        loadedChatIdByMode: loadedChatIdByModeRef.current,
      })
      if (sessionToOpen) loadSession(sessionToOpen)
      if (!selected && activeChatId && !pendingHistorySessionIdsRef.current.has(activeChatId)) clearDraftUi()
    })
  }, [activeChatId, activeWorkspaceId, clearDraftUi, currentUser?.uid, effectiveOperatingMode, forcedOperatingMode, isDraftingNewChat, loadSession])

  useEffect(() => {
    if (!activeChatId || loadedChatIdByModeRef.current[effectiveOperatingMode] !== activeChatId) return
    const timer = window.setTimeout(() => {
      const visibleTurns = chatTurns.map(sanitizeTurnForStorage)
      void saveAiChatSession({
        id: activeChatId,
        turns: visibleTurns,
        attachments: persistedSessionAttachments.slice(0, attachmentCap),
        operatingMode: effectiveOperatingMode,
        status: 'ACTIVE',
        touchHistoryOrder: false,
      })
    }, 700)
    return () => window.clearTimeout(timer)
  }, [activeChatId, attachmentCap, chatTurns, effectiveOperatingMode, persistedSessionAttachments])
  const silenceTimerRef = useRef<number | null>(null)
  const chatThreadRef = useRef<HTMLDivElement | null>(null)
  const pendingPreviewsRef = useRef(new Map<string, PendingPreviewRecord>())

  useEffect(() => {
    pendingPreviewsRef.current = new Map(Object.entries(readPendingPreviewStore(activeWorkspaceId)))
  }, [activeWorkspaceId])

  const testingOverride = testingOverrideAllowsAllFeatures()
  // Plan/limit lift only (testing override OR workspace demo unlock, R35). `testingOverride` alone stays for the debug log.
  const limitsLifted = limitsLiftedForWorkspace(activeWorkspace)
  const hasPaidAccess = plan !== 'FREE_TIER' || limitsLifted
  const maxCharacters = hasPaidAccess ? PAID_CHARACTER_LIMIT : FREE_CHARACTER_LIMIT
  const promptLength = prompt.length
  // P4: only editable targets flip Send into Build/edit mode. Mention-only
  // targets (checklist items, uploads, meetings, reports, forms) are context
  // for chat/generation but never dispatched through the edit patch API.
  const promptCreatePhrase = findLineCreatePhrase(prompt, linePolicy.createPhrases)
  const createPlanTargets = liveEditTargets.filter(isCreatePlanReference)
  const editableTargets = liveEditTargets.filter((target): target is Extract<EditTarget, { kind: 'goal' | 'task' | 'subtask' | 'checklist_item' }> => isEditableTarget(target) && !isCreatePlanReference(target))
  const mentionTargets = liveEditTargets.filter((target) => !isEditableTarget(target))
  const isEditMode = editableTargets.length > 0 && createPlanTargets.length === 0 && !promptCreatePhrase
  const activeGoalId = editableTargets[0]?.goalId
  const isCoolingDown = !limitsLifted && !hasPaidAccess && cooldownRemaining > 0
  const isOverLimit = promptLength > maxCharacters
  const premiumAction: WalletReservationPurpose = isEditMode ? 'SIMPLE_EDIT' : activeDataContexts.length > 0 || activeImageFiles.length > 0 ? 'ADVANCED_GENERATION' : 'GENERATION'
  const estimatedBuildCredits = PREMIUM_ACTION_COSTS[premiumAction]
  /**
   * P1 auto-routed Send: the current turn is a Build (heavy) only if
   * (a) the user has picked edit targets, or
   * (b) the prompt itself is a short affirmation ("yes", "build it", "go").
   * Everything else is a Discuss turn and reserves the cheap AI_CHAT tier.
   * The composer's meta line reflects this in real time so the user always
   * sees what the current Send will cost.
   */
  const currentTurnIsAffirmation =
    parseAiMagicOperationalConfirmationResponse(prompt.trim()) === 'PREPARE_PROPOSAL'
  const currentTurnBuildIntent = !isPlanningMode || isEditMode || currentTurnIsAffirmation
  const estimatedCredits = currentTurnBuildIntent
    ? estimatedBuildCredits
    : PREMIUM_ACTION_COSTS.AI_CHAT
  const hasPromptPayload = Boolean(prompt.trim()) || activeImageFiles.length > 0 || activeDataContexts.length > 0
  const attachmentAnalysisKey = useMemo(
    () => buildAttachmentAnalysisKey(activeDataContexts),
    [activeDataContexts],
  )
  const canUseAttachmentIntelligence =
    effectiveOperatingMode === 'CREATE'
    && !isEditMode
    && hasPaidAccess
    && attachmentAnalysisKey.length > 0
  const pulsePremiumPanel = useMemo<WorkspacePulsePremiumPanel | null>(() => {
    if (effectiveOperatingMode !== 'CREATE' || !hasPaidAccess) return null
    if (!attachmentDigest && attachmentDigestStatus === 'idle' && !attachmentDigestError && activeDataContexts.length === 0) return null
    return {
      digest: attachmentDigest ? toAttachmentPlanningSnapshot(attachmentDigest) : null,
      status: attachmentDigestStatus,
      error: attachmentDigestError,
      digestFingerprint: attachmentDigestFingerprint,
      sourceCount: activeDataContexts.length,
    }
  }, [
    activeDataContexts.length,
    attachmentDigest,
    attachmentDigestError,
    attachmentDigestFingerprint,
    attachmentDigestStatus,
    effectiveOperatingMode,
    hasPaidAccess,
  ])
  // Cooldown gates new generation only — edits never cool down (matches prior
  // Manage behavior).
  const canGenerate =
    !isAiBlocked && hasPromptPayload && !isGenerating && !isOverLimit
  useEffect(() => {
    setCurrentModeState((current) => {
      if (!attachmentAnalysisKey) {
        return current.attachmentDigest
          || current.attachmentDigestFingerprint
          || current.attachmentDigestStatus !== 'idle'
          || current.attachmentDigestError
          ? {
              ...current,
              attachmentDigest: null,
              attachmentDigestFingerprint: null,
              attachmentDigestStatus: 'idle',
              attachmentDigestError: null,
            }
          : current
      }
      return current.attachmentDigestFingerprint === attachmentAnalysisKey
        ? current
        : {
            ...current,
            attachmentDigest: null,
            attachmentDigestFingerprint: null,
            attachmentDigestStatus: 'idle',
            attachmentDigestError: null,
          }
    })
  }, [attachmentAnalysisKey, setCurrentModeState])
  useEffect(() => {
    if (!activeWorkspaceId) return
    if (isNoSignInPreviewActive()) {
      setUpgradeLocked(false)
      return
    }
    const balance = credits.spendableTotal || (plan === 'FREE_TIER' ? 500 : 0)
    setUpgradeLocked(credits.workspaceWallet.trialCredits > 0 && balance <= 0)
    return undefined
  }, [activeWorkspaceId, credits.spendableTotal, credits.workspaceWallet.trialCredits, plan])
  useEffect(() => {
    setPhase(isAiBusy ? 'STREAMING' : 'IDLE')
  }, [isAiBusy, setPhase])
  useEffect(() => {
    if (limitsLifted || hasPaidAccess || cooldownRemaining <= 0) return

    const timer = window.setInterval(() => {
      setCooldownRemaining((current) => Math.max(0, current - 1))
    }, 1000)

    return () => window.clearInterval(timer)
  }, [cooldownRemaining, hasPaidAccess, limitsLifted])

  useEffect(() => () => {
    // Component teardown — make sure any in-flight recognition session is
    // released so the mic indicator clears and the OS-level capture stops.
    speechRecognitionRef.current?.abort()
    speechRecognitionRef.current = null
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    const thread = chatThreadRef.current
    if (!thread) return
    thread.scrollTop = thread.scrollHeight
  }, [chatTurns, quickControls])

  useEffect(() => {
    if (!handoffRequest?.attachments.length) return
    const contexts: DataContext[] = handoffRequest.attachments.map((attachment) => ({
      id: attachment.id,
      kind: 'chat',
      label: attachment.label,
      text: attachment.text ?? '',
      channelId: attachment.channelId,
      channelName: attachment.channelName,
      messages: attachment.messages,
    }))
    const sessionId = handoffRequest.targetSessionId
    if (sessionId) {
      const session = chatSessions.find((candidate) => candidate.id === sessionId)
      if (!session) return
      loadSession(session)
      updateModeState(session.operatingMode, (current) => ({
        ...current,
        dataContexts: mergeUniqueDataContexts(current.dataContexts, contexts),
      }))
    } else {
      const nextMode = handoffRequest.mode ?? effectiveOperatingMode
      if (!forcedOperatingMode) setOperatingMode(nextMode)
      loadedChatIdByModeRef.current[nextMode] = null
      updateModeState(nextMode, () => ({
        ...createEmptyModeState(),
        dataContexts: mergeUniqueDataContexts([], contexts),
      }))
    }
  }, [chatSessions, effectiveOperatingMode, forcedOperatingMode, handoffRequest, loadSession, setOperatingMode, updateModeState])

  const updateTextPrompt = (value: string) => {
    setPrompt(value.slice(0, maxCharacters))
    setSuccessMessage('')
    setError(null)
  }

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
  }

  const scheduleSilenceStop = () => {
    clearSilenceTimer()
    silenceTimerRef.current = window.setTimeout(() => {
      stopSpeechCapture()
    }, SPEECH_SILENCE_AUTO_STOP_MS)
  }

  const startSpeechCapture = () => {
    const SpeechRecognition = window.SpeechRecognition ?? window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      appendWarningMessage('Speech recognition is not available in this browser.')
    setError(t('NLSAiSpeechUnavailable'))
      return
    }

    try {
      speechRecognitionRef.current?.abort()
      const recognition = new SpeechRecognition()
      speechRecognitionRef.current = recognition
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = 'en-US'
      recognition.onresult = (event) => {
        let finalText = ''
        let interimText = ''

        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const result = event.results[index]
          if (result.isFinal) finalText += result[0].transcript
          else interimText += result[0].transcript
        }

        if (finalText) {
          // Append final chunks to the unified prompt. Insert a single space
          // between the existing text and the new chunk unless the prompt
          // already ends with whitespace.
          setPrompt((current) => {
            const trimmedFinal = finalText.trim()
            if (!trimmedFinal) return current
            const separator = current && !/\s$/.test(current) ? ' ' : ''
            const next = `${current}${separator}${trimmedFinal}`
            return next.slice(0, maxCharacters)
          })
          setSuccessMessage('')
          setError(null)
        }
        setInterimSpeech(interimText)
        scheduleSilenceStop()
      }
      recognition.onerror = () => {
        stopSpeechCapture()
      }
      recognition.onend = () => {
        // The browser fired `onend` (silence timeout, hardware loss, or our
        // own stop()) — make sure the UI returns to idle even if the user
        // never tapped the mic again.
        setIsListening(false)
        setInterimSpeech('')
        clearSilenceTimer()
      }
      recognition.start()
      setIsListening(true)
      scheduleSilenceStop()
    } catch (caughtError) {
      appendWarningMessage(caughtError instanceof Error ? caughtError.message : 'Could not start speech capture.')
      setError(caughtError instanceof Error ? caughtError.message : t('NLSAiSpeechCouldNotStart'))
      setIsListening(false)
    }
  }

  const stopSpeechCapture = () => {
    clearSilenceTimer()
    try {
      speechRecognitionRef.current?.stop()
    } catch {
      speechRecognitionRef.current?.abort()
    } finally {
      setIsListening(false)
      setInterimSpeech('')
    }
  }

  const toggleSpeechCapture = () => {
    if (isListening) {
      stopSpeechCapture()
    } else {
      startSpeechCapture()
    }
  }

  const addImageFiles = async (files: FileList | File[] | null) => {
    if (!files) return
    const available = Math.max(0, attachmentCap - sessionAttachments.length)
    const list = Array.from(files).slice(0, available)
    if (list.length === 0) {
      appendWarningMessage(`Your plan allows ${attachmentLimit} combined context attachments in one chat.`)
      return
    }
    try {
      const pending: Array<{ id: string; file: File }> = []
      const contexts: DataContext[] = []
      const images: File[] = []
      for (const file of list) {
        const id = crypto.randomUUID()
        pending.push({ id, file })
        if (file.type.startsWith('image/') || file.type.startsWith('audio/') || file.type.startsWith('video/')) {
          const mediaType = file.type.startsWith('audio/') ? 'audio' : file.type.startsWith('video/') ? 'video' : 'image'
          if (mediaType === 'image') images.push(file)
          contexts.push({ id: `pending:${id}`, kind: 'upload', label: file.name, sizeBytes: file.size, text: `[Attached ${mediaType}: ${file.name}]`, mimeType: file.type })
          continue
        }
        const spreadsheet = /\.(xlsx?|csv)$/i.test(file.name)
        const text = spreadsheet ? await extractSpreadsheetText(file) : await extractDocumentText(file)
        contexts.push({ id: `pending:${id}`, kind: spreadsheet ? 'spreadsheet' : 'document', label: file.name, sizeBytes: file.size, text, mimeType: file.type || 'application/octet-stream' })
      }
      setPendingUploadFiles((current) => [...current, ...pending])
      setImageFiles((current) => [...current, ...images])
      setDataContexts((current) => [...current, ...contexts])
      setSuccessMessage('')
      setError(null)
    } catch (error) {
      appendWarningMessage(error instanceof Error ? error.message : 'Could not upload that attachment.')
    }
  }

  const attachDataContext = (context: DataContext) => {
    if (sessionAttachments.length >= attachmentCap) {
      appendWarningMessage(`Your plan allows ${attachmentLimit} combined context attachments in one chat.`)
      return
    }
    setDataContexts((current) => [...current, context])
    setSuccessMessage('')
    setError(null)
  }

  const removeDataContext = (id: string) => {
    setDataContexts((current) => current.filter((context) => context.id !== id))
    if (id.startsWith('pending:')) {
      const pendingId = id.slice('pending:'.length)
      setPendingUploadFiles((current) => {
        const removed = current.find((entry) => entry.id === pendingId)
        if (removed) setImageFiles((images) => images.filter((image) => image !== removed.file))
        return current.filter((entry) => entry.id !== pendingId)
      })
    }
  }

  // Fold typed guidance and every attached data source into one plaintext brief
  // for the model. Extracted blocks are clearly fenced so the model can tell
  // ingested data apart from the user's own instructions.
  const buildMergedBrief = (
    sourcePrompt = prompt,
    attachmentBriefing?: string,
    contexts: DataContext[] = activeDataContexts,
    mentions: EditTarget[] = mentionTargets,
  ) => {
    const sourceText = sourcePrompt.trim()
    if (contexts.length === 0 && !attachmentBriefing && mentions.length === 0) return sourceText

    const blocks = contexts.map((context) => {
      const header =
        context.kind === 'chat'
          ? `[ATTACHED CHAT CONTEXT: ${context.label}]`
          : context.kind === 'gsheet'
          ? '[LINKED LIVE GOOGLE SHEET]'
          : context.kind === 'spreadsheet'
            ? `[ATTACHED SPREADSHEET DATA: ${context.label}]`
            : context.kind === 'form'
              ? `[ATTACHED FORM SCHEMA: ${context.label}]`
              : context.kind === 'form_responses'
                ? `[ATTACHED FORM RESPONSES: ${context.label}]`
                : context.kind === 'design'
                  ? `[ATTACHED DESIGN CONTEXT: ${context.label}]`
                  : context.kind === 'link'
                    ? `[ATTACHED PUBLIC LINK: ${context.label}]`
                    : context.kind === 'asset'
                      ? `[ATTACHED WORKSPACE ASSET CONTEXT: ${context.label}]`
                      : context.kind === 'upload'
                        ? `[ATTACHED UPLOAD CONTEXT: ${context.label}]`
            : `[ATTACHED DOCUMENT CONTEXT: ${context.label}]`
      return `${header}\n${context.text}\n[END DATA]`
    })

    // P4: mention-only targets flow into the prompt as a compact reference
    // block. The model treats them as context ("the user is referring to X")
    // rather than as an edit destination.
    const mentionBlock = mentions.length === 0
      ? null
      : [
          '[MENTIONED ITEMS — the user tagged these with @ for reference; do not modify them unless explicitly asked]',
          ...mentions.map((mention) => {
            const label = mention.label.replace(/\s+/g, ' ').trim()
            const kindLabel = mention.kind.replace(/_/g, ' ')
            return `- (${kindLabel}) ${label}`
          }),
          '[END MENTIONS]',
        ].join('\n')

    return [sourceText, attachmentBriefing, mentionBlock, ...blocks].filter(Boolean).join('\n\n')
  }

  const ensureAttachmentDigest = useCallback(async (
    sourcePrompt: string,
    options?: {
      signal?: AbortSignal
      contexts?: DataContext[]
      imageFiles?: File[]
      fingerprint?: string
      currentDigest?: AttachmentPlanningDigest | null
      currentFingerprint?: string | null
      runScope?: AiMagicRunScope
    },
  ): Promise<AttachmentPlanningDigest | null> => {
    const contexts = options?.contexts ?? activeDataContexts
    const imageFilesForRun = options?.imageFiles ?? activeImageFiles
    const fingerprint = options?.fingerprint ?? buildAttachmentAnalysisKey(contexts)
    const currentDigest = options?.currentDigest ?? attachmentDigest
    const currentFingerprint = options?.currentFingerprint ?? attachmentDigestFingerprint
    if (!canUseAttachmentIntelligence || !fingerprint) return null
    if (currentDigest && currentFingerprint === fingerprint) return currentDigest

    const updateDigestState = (updater: (current: AiMagicModeState) => AiMagicModeState) => {
      if (options?.runScope) {
        updateVisibleRunState(options.runScope, updater)
      } else {
        setCurrentModeState(updater)
      }
    }
    updateDigestState((current) => ({
      ...current,
      attachmentDigestStatus: 'analyzing',
      attachmentDigestError: null,
    }))
    try {
      const sources: AttachmentPlanningSourceInput[] = contexts.map((context) => ({
        id: context.id,
        kind: toAttachmentPlanningSourceKind(context.kind),
        label: context.label,
        text: context.text,
        mimeType: context.mimeType,
      }))
      const digest = await analyzeAttachmentPlanningContext({
        prompt: sourcePrompt,
        sources,
        imageFiles: imageFilesForRun,
        checklistCapabilityContext,
        signal: options?.signal,
      })
      updateDigestState((current) => ({
        ...current,
        attachmentDigest: digest,
        attachmentDigestFingerprint: fingerprint,
        attachmentDigestStatus: 'ready',
        attachmentDigestError: null,
      }))
      return digest
    } catch (error) {
      updateDigestState((current) => ({
        ...current,
        attachmentDigest: null,
        attachmentDigestFingerprint: null,
        attachmentDigestStatus: 'error',
        attachmentDigestError: error instanceof Error ? error.message : 'Attachment analysis fell back to the raw context.',
      }))
      return null
    }
  }, [
    attachmentAnalysisKey,
    attachmentDigest,
    attachmentDigestFingerprint,
    canUseAttachmentIntelligence,
    activeDataContexts,
    activeImageFiles,
    checklistCapabilityContext,
    setCurrentModeState,
    updateVisibleRunState,
  ])

  const finalizeSentDraftAttachments = useCallback(async ({
    chatId,
    turns,
    userTurnId,
    sourceChatTitle,
    draftDataContexts,
    draftPendingUploadFiles,
  }: {
    chatId: string
    turns: AiMagicChatTurn[]
    userTurnId: string
    sourceChatTitle: string | null
    draftDataContexts: DataContext[]
    draftPendingUploadFiles: Array<{ id: string; file: File }>
  }) => {
    let nextTurns = turns
    let resolvedDraftContexts = draftDataContexts
    if (draftPendingUploadFiles.length > 0) {
      const pendingSnapshot = draftPendingUploadFiles.slice(0, attachmentCap)
      const uploaded = await uploadWorkspaceFiles({
        workspaceId: activeWorkspaceId,
        files: pendingSnapshot.map((entry) => entry.file),
        visibility: 'PRIVATE',
        artifactType: 'AI_MAGIC_UPLOAD',
        artifactId: chatId,
        sourceChatTitle,
      })
      const fileIds = new Map(pendingSnapshot.map((entry, index) => [entry.id, uploaded[index]?.fileId]))
      nextTurns = nextTurns.map((turn) => turn.id === userTurnId ? {
        ...turn,
        attachmentPreviews: turn.attachmentPreviews?.map((preview) => {
          if (!preview.id.startsWith('pending:')) return preview
          const pendingId = preview.id.slice('pending:'.length)
          const fileId = fileIds.get(pendingId)
          return fileId ? { ...preview, id: `upload:${fileId}`, fileId, url: undefined } : preview
        }),
      } : turn)
      resolvedDraftContexts = draftDataContexts.map((context) => {
        if (!context.id.startsWith('pending:')) return context
        const pendingId = context.id.slice('pending:'.length)
        const fileId = fileIds.get(pendingId)
        return fileId ? { ...context, id: `upload:${fileId}`, fileId } : context
      })
      nextTurns = nextTurns.map((turn) => turn.id === userTurnId ? {
        ...turn,
        attachmentContexts: buildAiChatAttachmentPayloads(
          liveEditTargets,
          resolvedDraftContexts,
        ),
      } : turn)
    }

    const persistedAttachments = buildAiChatAttachmentPayloads(
      liveEditTargets,
      mergeUniqueDataContexts(sessionDataContexts, resolvedDraftContexts),
    ).slice(0, attachmentCap)

    return {
      turns: nextTurns,
      persistedAttachments,
      resolvedDraftContexts,
    }
  }, [
    activeWorkspaceId,
    attachmentCap,
    liveEditTargets,
    sessionDataContexts,
  ])

  const promoteDraftAttachmentsToSession = useCallback((scope: AiMagicRunScope, resolvedDraftContexts: DataContext[], draftImageFiles: File[]) => {
    updateVisibleRunState(scope, (current) => {
      const nextSessionDataContexts = mergeUniqueDataContexts(current.sessionDataContexts, resolvedDraftContexts)
      return {
        ...current,
        sessionDataContexts: nextSessionDataContexts,
        sessionImageFiles: mergeUniqueFiles(current.sessionImageFiles, draftImageFiles),
        attachmentDigestFingerprint: current.attachmentDigest
          ? buildAttachmentAnalysisKey(nextSessionDataContexts)
          : current.attachmentDigestFingerprint,
      }
    })
  }, [updateVisibleRunState])

  const buildWorkspaceSummary = () => {
    const lines: string[] = []
    for (const goal of goals.slice(0, 12)) {
      lines.push(`GOAL ${goal.id}: ${goal.title} (${goal.startDate || 'no start'} → ${goal.endDate || 'no end'})`)
      goal.tasks.slice(0, 20).forEach((task) => {
        lines.push(`  TASK ${task.id}: ${task.title} (${task.startDate || 'no start'} → ${task.endDate || 'no end'})`)
        task.subTasks.slice(0, 20).forEach((sub) => {
          lines.push(`    SUBTASK ${sub.id}: ${sub.title} (${sub.startDate || 'no start'} → ${sub.endDate || 'no end'})`)
        })
      })
    }
    return lines.join('\n')
  }

  const appendLineMessage = (text: string, tone: 'ASSISTANT' | 'WARNING' = 'ASSISTANT') => {
    setChatTurns((current) => {
      const last = current[current.length - 1]
      if (last?.role === tone && last.text === text) return current
      return [...current, { id: createChatTurnId(), role: tone, text }]
    })
  }

  const appendWarningMessage = (text: string) => appendLineMessage(text, 'WARNING')

  const submitQuickSelections = () => {
    const selectedControls = quickControls.filter((control) => {
      const group = control.type === 'button' ? 'quick-action' : control.group
      return (quickSelections[group] ?? []).includes(control.value)
    })
    if (selectedControls.length === 0) return
    const response = selectedControls.map((control) => control.value).join('\n')
    setQuickControls([])
    setQuickSelections({})
    if (clarificationTurnId) {
      dispatchClarificationEnvelope(response, selectedControls.map((control) => control.label).join(', '))
      return
    }
    void runGeneration(response)
  }

  // Timeline targets share the same plan limit as every other context source.
  const toggleEditTarget = (target: EditTarget) => {
    setEditTargets((current) => {
      const key = targetKey(target)
      if (current.some((existing) => targetKey(existing) === key)) {
        return current.filter((existing) => targetKey(existing) !== key)
      }
      if (sessionAttachments.length >= attachmentCap) {
        showToast(t('NLSAiToastAttachmentLimit', { limit: attachmentLimit }), 'warning')
        return current
      }
      return [...current, target]
    })
    setError(null)
  }

  const removeEditTarget = (key: string) =>
    setEditTargets((current) => current.filter((existing) => targetKey(existing) !== key))


  // EXECUTE-tab tour pipeline. Generation is free; the wallet burn happens on
  // the JourneyTriggerCard's Begin Journey click via reserveCredits →
  // commitCredits (purpose: TOUR_CONCIERGE_EXECUTE). Re-opening a session with
  // a purchased tour shows a free Replay button.
  const MUTATE_INTENT_REGEX = /\b(delete|remove|change|update|edit|modify|set|rename|move|create|add|generate|insert|transfer|grant|revoke|assign|invite)\b/i
  const AFFIRMATION_REGEX = /^(yes|do it|start journey|go|begin|confirm|start)\s*\.?\s*$/i

  const markTurnPurchased = useCallback((turnId: string) => {
    setChatTurns((current) => current.map((turn) => turn.id === turnId ? { ...turn, purchased: true } : turn))
  }, [setChatTurns])

  // resolveEditContext previously resolved a single target to {item, parent};
  // that work is now done inside aiService.generateValidatedTimelineEditBatch
  // so the panel just hands over BatchEditTarget descriptors.

  const expandEditTargets = (baseGoal: Goal, trimmedInstruction: string): BatchEditTarget[] => {
    const cascadeIntent = detectCascadeIntent(trimmedInstruction)
    const expandedTargets: BatchEditTarget[] = []
    const seenKeys = new Set<string>()
    const addBatchTarget = (target: BatchEditTarget) => {
      const key =
        target.kind === 'goal'
          ? `g:${target.goalId}`
          : target.kind === 'task'
            ? `t:${target.taskId}`
            : target.kind === 'subtask'
              ? `s:${target.subTaskId}`
              : `c:${target.itemId}`
      if (seenKeys.has(key)) return
      seenKeys.add(key)
      expandedTargets.push(target)
    }

    // Editable targets: goal, task, subtask, checklist_item.
    for (const target of liveEditTargets.filter(isEditableTarget)) {
      if (target.kind === 'goal') {
        addBatchTarget({ kind: 'goal', goalId: target.goalId })
        if (cascadeIntent !== 'none') {
          for (const task of baseGoal.tasks) {
            addBatchTarget({ kind: 'task', goalId: target.goalId, taskId: task.id })
            for (const sub of task.subTasks) {
              addBatchTarget({
                kind: 'subtask',
                goalId: target.goalId,
                taskId: task.id,
                subTaskId: sub.id,
              })
            }
          }
        }
      } else if (target.kind === 'task') {
        addBatchTarget({
          kind: 'task',
          goalId: target.goalId,
          taskId: target.taskId,
        })
        if (cascadeIntent !== 'none') {
          const parentTask = baseGoal.tasks.find((candidate) => candidate.id === target.taskId)
          parentTask?.subTasks.forEach((sub) => {
            addBatchTarget({
              kind: 'subtask',
              goalId: target.goalId,
              taskId: target.taskId,
              subTaskId: sub.id,
            })
          })
        }
      } else if (target.kind === 'subtask') {
        addBatchTarget({
          kind: 'subtask',
          goalId: target.goalId,
          taskId: target.taskId,
          subTaskId: target.subTaskId,
        })
      } else if (target.kind === 'checklist_item') {
        addBatchTarget({
          kind: 'checklist_item',
          goalId: target.goalId,
          taskId: target.taskId,
          subTaskId: target.subTaskId,
          itemId: target.itemId,
        })
      }
    }

    return expandedTargets
  }

  type AiPanelJobOutcome =
    | { kind: 'ASK'; runScope: AiMagicRunScope; turn: AiMagicChatTurn; quickControls: AiMagicQuickControl[] }
    | { kind: 'NEEDS_MORE_DETAIL'; runScope: AiMagicRunScope; message: string }
    | {
        kind: 'GOAL_READY'
        runScope: AiMagicRunScope
        turn: AiMagicChatTurn
        goal: Goal
        meta: NonNullable<Parameters<NonNullable<typeof onGeneratedGoal>>[1]>
        isFreshGenerate: boolean
        replacesTurnId?: string | null
      }

  /**
   * Run all the side-effects for a completed AI job (prepare Manage handoff, emit
   * gamification event, push the closing assistant message, cooldown). Pulled
   * out so it can fire both from the live-handoff subscriber (panel stayed
   * open) AND from the "Move to Manage" button on remount (panel was closed
   * mid-run).
   */
  const applyAiJobOutcome = useCallback((outcome: AiPanelJobOutcome) => {
    const scope = outcome.runScope
    if (outcome.kind === 'ASK') {
      const turn = { ...outcome.turn, quickControls: outcome.quickControls }
      updateVisibleRunState(scope, (current) => ({
        ...current,
        chatTurns: current.chatTurns.some((entry) => entry.id === turn.id)
          ? current.chatTurns
          : [...current.chatTurns, turn],
        clarificationTurnId: turn.status === 'PENDING_CLARIFICATION' ? turn.id ?? null : null,
        quickControls: outcome.quickControls,
      }))
      return
    }
    if (outcome.kind === 'NEEDS_MORE_DETAIL') {
      appendRunLineMessage(scope, outcome.message, 'WARNING')
      return
    }
    setRunChatTurns(scope, (current) => {
      const withoutSupersededDraft = outcome.replacesTurnId
        ? current.map((turn) => turn.id === outcome.replacesTurnId ? collapseRevisedProposalTurn(turn) : turn)
        : current
      return withoutSupersededDraft.some((turn) => turn.id === outcome.turn.id)
        ? withoutSupersededDraft
        : [...withoutSupersededDraft, outcome.turn]
    })
    if (outcome.replacesTurnId) {
      pendingPreviewsRef.current.delete(outcome.replacesTurnId)
      writePendingPreviewStore(activeWorkspaceId, (current) => {
        const next = { ...current }
        delete next[outcome.replacesTurnId!]
        return next
      })
    }
    if (outcome.turn.id) {
      const preview = { goal: outcome.goal, meta: outcome.meta }
      pendingPreviewsRef.current.set(outcome.turn.id, preview)
      writePendingPreviewStore(activeWorkspaceId, (current) => ({ ...current, [outcome.turn.id!]: preview }))
      const pendingProposal = outcome.turn.proposalSummary
        ? pendingProposalForTurn(outcome.turn.id, preview, outcome.turn.proposalSummary, linePolicy.pendingProposalTtlMs)
        : null
      const chatId = aiMagicRunChatId(scope)
      const historySession = chatId ? chatSessions.find((session) => session.id === chatId) : null
      if (historySession && pendingProposal) {
        const historyTurns = outcome.replacesTurnId
          ? historySession.turns.map((turn) => turn.id === outcome.replacesTurnId ? collapseRevisedProposalTurn(turn) : turn)
          : historySession.turns
        const nextHistoryTurns = historyTurns.some((turn) => turn.id === outcome.turn.id)
          ? historyTurns
          : [...historyTurns, outcome.turn]
        void saveAiChatSession({
          id: historySession.id,
          turns: nextHistoryTurns.map(sanitizeTurnForStorage),
          attachments: historySession.attachments,
          operatingMode: historySession.operatingMode,
          status: historySession.status,
          pendingProposal,
        }).catch(() => undefined)
        patchHistorySession(historySession.id, { turns: nextHistoryTurns, pendingProposal })
      }
    }
    emitTrackEvent(outcome.isFreshGenerate ? 'TRACK_AI_SPAWN' : 'TRACK_AI_REFINE')
    if (activeWorkspaceId && currentUser?.uid) {
      fireAutomationEvent(
        {
          type: outcome.isFreshGenerate ? 'AI_ESSAY_GENERATED' : 'AI_REFINE_USED',
          workspaceId: activeWorkspaceId,
          eventId: `ai-${outcome.isFreshGenerate ? 'spawn' : 'refine'}-${outcome.turn.id ?? Date.now()}`,
          actorId: currentUser.uid,
          payload: { creditsSpent: outcome.isFreshGenerate ? 5 : 2, sourceId: outcome.turn.id },
        },
        { id: currentUser.uid },
      )
    }
    if (outcome.isFreshGenerate && !hasPaidAccess && !limitsLifted) setCooldownRemaining(FREE_COOLDOWN_SECONDS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, appendRunLineMessage, chatSessions, hasPaidAccess, patchHistorySession, setRunChatTurns, updateVisibleRunState])

  // Auto-handoff: while the panel is mounted, finished jobs hand back to us
  // immediately. If the panel was unmounted at completion, the context
  // transitions to `ready` instead and the Move to Manage button claims it.
  useEffect(() => {
    return subscribeAutoHandoff<AiPanelJobOutcome>('AI_GENERATOR', applyAiJobOutcome)
  }, [subscribeAutoHandoff, applyAiJobOutcome])

  useEffect(() => {
    if (!isAiReady) return
    const result = claimAiJob<AiPanelJobOutcome>('AI_GENERATOR', activeWorkspaceId)
    if (result) applyAiJobOutcome(result)
  }, [activeWorkspaceId, applyAiJobOutcome, claimAiJob, isAiReady])

  const focusComposer = () => {
    if (typeof window === 'undefined') return
    window.setTimeout(() => {
      const textarea = mobileTextareaRef.current
        ?? document.querySelector<HTMLTextAreaElement>('.aiGeneratorComposer textarea')
      textarea?.focus()
      textarea?.setSelectionRange(textarea.value.length, textarea.value.length)
    }, 0)
  }

  const runExecuteTourTurn = async (overridePrompt?: string) => {
    const userText = overridePrompt?.trim() || prompt.trim()
    if (!userText) return
    const displayText = nextUserDisplayTextRef.current?.trim() || userText
    nextUserDisplayTextRef.current = null
    const userTurn: AiMagicChatTurn = {
      id: createChatTurnId(),
      role: 'USER',
      text: displayText,
      ...(userText && userText !== displayText ? { requestText: userText } : {}),
    }
    const runScope: AiMagicRunScope = {
      mode: effectiveOperatingMode,
      initialChatId: activeChatId,
      resolvedChatId: activeChatId,
      userTurnId: userTurn.id ?? null,
    }
    const nextThread = [...chatTurns, userTurn]
    setActivePromptDraft('')
    updateVisibleRunState(runScope, (current) => ({
      ...current,
      chatTurns: nextThread,
      prompt: '',
    }))
    setPhase('PREFLIGHT')
    let chatId = activeChatId
    const finishExecuteThread = async (
      finalThread: AiMagicChatTurn[],
      options: {
        quickControls?: AiMagicQuickControl[]
        clarificationTurnId?: string | null
        historyFailureMessage?: string
      } = {},
    ) => {
      const visibleQuickControls = options.quickControls ?? []
      updateVisibleRunState(runScope, (current) => ({
        ...current,
        chatTurns: finalThread,
        clarificationTurnId: options.clarificationTurnId ?? null,
        quickControls: visibleQuickControls,
      }))
      try {
        if (chatId) {
          const turns = finalThread.map(sanitizeTurnForStorage)
          await saveAiChatSession({ id: chatId, turns, attachments: [], operatingMode: effectiveOperatingMode, status: 'ACTIVE' })
          patchHistorySession(chatId, {
            turns,
            attachments: [],
            operatingMode: effectiveOperatingMode,
            status: 'ACTIVE',
          })
        }
      } catch (historyError) {
        appendRunLineMessage(
          runScope,
          historyError instanceof Error
            ? `${options.historyFailureMessage ?? 'Line replied, but the chat history could not be saved'}: ${historyError.message}`
            : `${options.historyFailureMessage ?? 'Line replied, but the chat history could not be saved'}.`,
          'WARNING',
        )
      }
    }

    // Line Execute — confirm-resume path. If the previous assistant turn
    // carries a lineExecuteConfirmPayload AND the user replied affirmatively,
    // resolve the payload directly instead of running detection again.
    const lastAssistant = [...chatTurns].reverse().find((turn) => turn.role === 'ASSISTANT')
    const pendingConfirmPayload = lastAssistant?.lineExecuteConfirmPayload ?? null
    const pendingTourFallbackPrompt = lastAssistant?.lineExecuteTourFallbackPrompt ?? null
    // "Show me how instead" — user prefers a tour to AI acting. Runs
    // BEFORE the affirmation branch so a plain "yes" still means "do it"
    // but any tour-choice phrasing hijacks straight to the tour path.
    if (pendingTourFallbackPrompt && isShowMeHowReply(userText)) {
      if (!currentUser || currentUser.isAnonymous) {
        appendRunLineMessage(runScope, 'Sign in with a non-anonymous account to use AI Magic.', 'WARNING')
        setPhase('IDLE')
        return
      }
      try {
        const { tour } = await generateAndRegisterTour({
          prompt: pendingTourFallbackPrompt,
          workspaceId: activeWorkspaceId,
          canPerform,
        })
        const assistantTurn: AiMagicChatTurn = {
          id: createChatTurnId(),
          role: 'ASSISTANT',
          text: `Here's a walkthrough for "${pendingTourFallbackPrompt}". Follow the guided steps and stop me any time.`,
          status: 'PENDING_APPROVAL',
          tourDefinition: tour,
          preambleKind: 'CONFIDENT',
          purchased: false,
          lineExecuteConfirmPayload: null,
          lineExecuteTourFallbackPrompt: null,
        }
        const finalThread = [...nextThread, assistantTurn]
        await finishExecuteThread(finalThread)
      } catch (error) {
        appendRunLineMessage(runScope, error instanceof Error ? error.message : 'I could not build that walkthrough.', 'WARNING')
      }
      setPhase('IDLE')
      return
    }
    if (pendingConfirmPayload && AFFIRMATION_REGEX.test(userText)) {
      if (!currentUser || currentUser.isAnonymous) {
        appendRunLineMessage(runScope, 'Sign in with a non-anonymous account to use AI Magic.', 'WARNING')
        setPhase('IDLE')
        return
      }
      const outcome = await resolveLineExecuteConfirmation(pendingConfirmPayload, {
        workspaceId: activeWorkspaceId,
        workspaceSlug: activeWorkspace?.slug || activeWorkspaceId,
        actor: {
          uid: currentUser.uid,
          role: activeRole,
          displayName: currentUser.displayName ?? currentUser.email ?? null,
        },
        prompt: userText,
        displayPrompt: displayText,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        boardElements,
        checklistItems,
        selectedTargets: operationalTargetRefs,
        webIntents: lineExecuteWebIntents,
        canPerform,
        showToast,
        switchedOffModules: switchedOffModulesFor(activeWorkspace),
      })
      const assistantTurn: AiMagicChatTurn = {
        id: createChatTurnId(),
        role: outcome.kind === 'FAILED' || outcome.kind === 'BLOCKED_BY_PERMISSION' || outcome.kind === 'BLOCKED_BY_QUOTA' ? 'WARNING' : 'ASSISTANT',
        text: outcome.message,
        status: outcome.kind === 'COMPLETED' ? 'CONFIRMED' : 'REJECTED',
        lineExecuteConfirmPayload: null,
      }
      if (outcome.kind === 'COMPLETED' && outcome.toast) {
        showToast(outcome.toast.message, outcome.toast.level)
      }
      if (outcome.kind === 'COMPLETED' && outcome.navigateTo && typeof window !== 'undefined') {
        window.location.assign(outcome.navigateTo)
      }
      const finalThread = [...nextThread, assistantTurn]
      await finishExecuteThread(finalThread)
      setPhase('IDLE')
      return
    }
    if (!currentUser || currentUser.isAnonymous) {
      appendRunLineMessage(runScope, 'Sign in with a non-anonymous account to use AI Magic.', 'WARNING')
      setPhase('IDLE')
      return
    }
    if (!chatId) {
      try {
        const created = await createAiChatSession({
          workspaceId: activeWorkspaceId,
          operatingMode: effectiveOperatingMode,
          title: userText.slice(0, 60),
          turns: nextThread.map(sanitizeTurnForStorage),
          attachments: [],
        })
        chatId = created.id
        pendingHistorySessionIdsRef.current.add(chatId)
        setChatSessions((current) => [{
          id: chatId!, workspaceId: activeWorkspaceId, ownerId: currentUser.uid,
          title: userText.slice(0, 60), status: 'ACTIVE', operatingMode: effectiveOperatingMode,
          turns: nextThread.map(sanitizeTurnForStorage), attachments: [],
        }, ...current.filter((session) => session.id !== chatId)])
        attachRunChatId(runScope, chatId)
      } catch (historyError) {
        appendRunLineMessage(runScope, historyError instanceof Error ? `Could not create the chat history: ${historyError.message}` : 'Could not create the chat history.', 'WARNING')
      }
    }
    // Line Execute — Action Registry precedence. If a registered action
    // matches this prompt, it runs first and the legacy Automate compile /
    // tour pipeline is skipped. This is the entry point wiring for the
    // multi-phase execute expansion; Create mode never reaches this call.
    const dispatchResult = await dispatchLineExecute({
      workspaceId: activeWorkspaceId,
      workspaceSlug: activeWorkspace?.slug || activeWorkspaceId,
      actor: {
        uid: currentUser.uid,
        role: activeRole,
        displayName: currentUser.displayName ?? currentUser.email ?? null,
      },
      prompt: userText,
      displayPrompt: displayText,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      boardElements,
      checklistItems,
      selectedTargets: operationalTargetRefs,
      webIntents: lineExecuteWebIntents,
      canPerform,
      showToast,
      switchedOffModules: switchedOffModulesFor(activeWorkspace),
    })
    if (dispatchResult.kind === 'DISPATCHED') {
      const { outcome, intent } = dispatchResult
      const supportsTour = adapterKindSupportsTourFallback(intent.kind)
      const baseControls = outcome.kind === 'NEEDS_CONFIRMATION'
        ? outcome.quickControls.map((control) => ({
            type: 'radio' as const,
            group: 'line-execute-adapter',
            label: control.label,
            value: control.value,
          }))
        : []
      // Universal tour choice: every adapter that could be walked through
      // gets a "Show me how" chip on NEEDS_CONFIRMATION and FAILED turns.
      const quickControls = (!isMobileLayout && supportsTour && (outcome.kind === 'NEEDS_CONFIRMATION' || outcome.kind === 'FAILED'))
        ? [...baseControls, SHOW_ME_HOW_CONTROL]
        : baseControls
      const status: AiMagicChatTurn['status'] =
        outcome.kind === 'COMPLETED' ? 'CONFIRMED'
        : outcome.kind === 'NEEDS_CONFIRMATION' ? 'PENDING_CLARIFICATION'
        : outcome.kind === 'FAILED' ? 'REJECTED'
        : outcome.kind === 'BLOCKED_BY_PERMISSION' ? 'REJECTED'
        : outcome.kind === 'BLOCKED_BY_QUOTA' ? 'REJECTED'
        : 'CONFIRMED'
      // U5: resolve the registry tourId for COMPLETED turns so LineChatBubble
      // can render the "Show me how" secondary button without re-querying.
      const registryActionId = LINE_EXECUTE_KIND_TO_ACTION_ID[intent.kind]
      const registryTourId = (!isMobileLayout && outcome.kind === 'COMPLETED' && featureFlag('ff_action_teach') && registryActionId)
        ? (getAction(registryActionId)?.tourId ?? null)
        : null
      const assistantTurn: AiMagicChatTurn = {
        id: createChatTurnId(),
        role: outcome.kind === 'FAILED' || outcome.kind === 'BLOCKED_BY_PERMISSION' || outcome.kind === 'BLOCKED_BY_QUOTA'
          ? 'WARNING'
          : 'ASSISTANT',
        text: outcome.message,
        status,
        quickControls,
        lineExecuteConfirmPayload: outcome.kind === 'NEEDS_CONFIRMATION'
          ? (outcome.confirmPayload as import('../services/ai/execute/confirmPayloadHandler').LineExecuteConfirmPayload)
          : null,
        lineExecuteTourFallbackPrompt: (!isMobileLayout && supportsTour && (outcome.kind === 'NEEDS_CONFIRMATION' || outcome.kind === 'FAILED'))
          ? userText
          : null,
        lineExecuteRegistryTourId: registryTourId,
      }
      const finalThread = [...nextThread, assistantTurn]
      if (outcome.kind === 'COMPLETED' && outcome.toast) {
        showToast(outcome.toast.message, outcome.toast.level)
      }
      if (outcome.kind === 'COMPLETED' && outcome.navigateTo && typeof window !== 'undefined') {
        window.location.assign(outcome.navigateTo)
      }
      await finishExecuteThread(finalThread, {
        quickControls,
        clarificationTurnId: outcome.kind === 'NEEDS_CONFIRMATION' ? assistantTurn.id ?? null : null,
      })
      setPhase('IDLE')
      return
    }
    if (dispatchResult.kind === 'ADAPTER_ERROR') {
      appendRunLineMessage(runScope, `Line's ${dispatchResult.intent.kind} adapter failed: ${dispatchResult.error}`, 'WARNING')
      // fall through to legacy pipeline so the user still gets *something*
    }
    // NO_MATCH and ADAPTER_MISSING both fall through to legacy pipeline.

    const targetBoard = resolveAiTargetBoard(userText, activeBoard)
    const timelineRuleContext = buildTimelineRuleContext({
      elements: boardElements,
      checklistItems,
      selectedTargets: operationalTargetRefs,
      board: targetBoard,
    })
    const operationalPulseHints = mergeOperationalPulseHints(timelineRuleContext.pulseHints, pulseOperationalHints)
    const operationalPlan = createLineOperationalPlan({
      workspaceId: activeWorkspaceId,
      actor: {
        uid: currentUser.uid,
        role: activeRole,
        displayName: currentUser.displayName ?? currentUser.email ?? null,
      },
      surface: 'AI_MAGIC',
      prompt: userText,
      mode: 'EXECUTE',
      selectedTargets: timelineRuleContext.selectedTargets,
      elements: boardElements,
      checklistItems,
      pulseHints: operationalPulseHints,
      supplementalRecords: integrationRuntimeRecords,
    })
    const operationalProposal = metadataFromOperationalPlan(operationalPlan)
    const operationalRoute = routeAiMagicOperationalPlan(operationalPlan, null, {
      executePathResponse: parseAiMagicExecutePathResponse(userText),
    })
    const executionPreview = buildExecutionPreview(operationalPlan)
    const executionTheater = buildExecutionTheaterOutline(operationalPlan)
    if (operationalRoute.kind !== 'CONTINUE_MODEL') {
      const assistantTurnId = createChatTurnId()
      const assistantTurn: AiMagicChatTurn = {
        id: assistantTurnId,
        role: operationalRoute.kind === 'BLOCK' ? 'WARNING' : 'ASSISTANT',
        text: operationalRoute.message,
        status: operationalRoute.kind === 'BLOCK'
          ? 'REJECTED'
          : operationalRoute.kind === 'CONFIRM'
            ? 'PENDING_CLARIFICATION'
            : 'CONFIRMED',
        routing: {
          decision: operationalRoute.kind === 'BLOCK'
            ? 'REJECT'
            : operationalRoute.kind === 'CONFIRM'
              ? 'CLARIFY'
              : 'ROUTE',
          confidenceScore: operationalPlan.envelope.risk === 'LOW' ? 0.9 : 0.8,
          reasoning: operationalPlan.envelope.audit.summary,
          requiresUserApproval: operationalRoute.kind === 'CONFIRM',
          ...(operationalRoute.kind === 'CONFIRM'
            ? {
                clarification: {
                  field: operationalRoute.clarificationField ?? 'operational_confirmation',
                  inputType: 'SELECT',
                  label: operationalRoute.message,
                  options: operationalRoute.quickControls.map((control) => control.label),
                },
              }
            : {}),
        },
        quickControls: operationalRoute.kind === 'CONFIRM' ? operationalRoute.quickControls : [],
        operationalProposal,
        executionTheater,
      }
      const finalThread = [...nextThread, assistantTurn]
      await finishExecuteThread(finalThread, {
        quickControls: operationalRoute.kind === 'CONFIRM' ? operationalRoute.quickControls : [],
        clarificationTurnId: operationalRoute.kind === 'CONFIRM' ? assistantTurnId : null,
      })
      setPhase('IDLE')
      return
    }
    if (operationalRoute.executionPath === 'CREATE_AUTOMATION') {
      const assistantTurnId = createChatTurnId()
      try {
        if (!canPerform('automation.manage')) {
          throw new Error('You do not have permission to create automations in this workspace.')
        }
        const draft = compileAutomationDraftFromPrompt({
          prompt: userText,
          selectedTargets: operationalTargetRefs,
          actorId: currentUser.uid,
        })
        const graphId = await createAutomationGraph({
          workspaceId: activeWorkspaceId,
          name: draft.name,
          description: draft.description,
          triggers: draft.triggers,
          then: draft.then,
          actor: {
            id: currentUser.uid,
            name: currentUser.displayName ?? currentUser.email ?? 'Workspace member',
          },
        })
        const assistantTurn: AiMagicChatTurn = {
          id: assistantTurnId,
          role: 'ASSISTANT',
          text: [
            `I created a paused automation draft: "${draft.name}".`,
            draft.warnings.length ? `Review notes: ${draft.warnings.join(' ')}` : '',
            'I moved it into Automate so you can review and activate it.',
          ].filter(Boolean).join(' '),
          status: 'PENDING_APPROVAL',
          operationalProposal,
          executionTheater,
        }
        const finalThread = [...nextThread, assistantTurn]
        await finishExecuteThread(finalThread)
        showToast(t('NLSAiToastAutomationDraftCreated'), 'success')
        onOpenAutomate?.(graphId)
      } catch (error) {
        const assistantTurn: AiMagicChatTurn = {
          id: assistantTurnId,
          role: 'WARNING',
          text: error instanceof Error ? error.message : 'I could not create the automation draft.',
          status: 'REJECTED',
          operationalProposal,
          executionTheater,
        }
        const finalThread = [...nextThread, assistantTurn]
        await finishExecuteThread(finalThread)
      }
      setPhase('IDLE')
      return
    }
    if (!executionPreview.allowed) {
      const assistantTurn: AiMagicChatTurn = {
        id: createChatTurnId(),
        role: 'WARNING',
        text: executionPreview.previewText,
        status: 'REJECTED',
        operationalProposal,
        executionTheater,
      }
      const finalThread = [...nextThread, assistantTurn]
      await finishExecuteThread(finalThread)
      setPhase('IDLE')
      return
    }
    try {
      const bundle = compileAutomationActionBundleFromPrompt({
        prompt: userText,
        selectedTargets: operationalTargetRefs,
        actorId: currentUser.uid,
      })
      const result = await executeAutomationActionBundle({
        workspaceId: activeWorkspaceId,
        name: bundle.name,
        description: bundle.description,
        sourcePrompt: userText,
        actions: bundle.actions,
      })
      const automationExecution = buildAiMagicExecutionResultDetail(bundle, result)
      const successfulActions = result.entries.filter((entry) => entry.outcome === 'SUCCESS')
      const failedEntry = result.entries.find((entry) => entry.outcome === 'FAILED')
      const assistantTurn: AiMagicChatTurn = {
        id: createChatTurnId(),
        role: result.outcome === 'FAILED' ? 'WARNING' : 'ASSISTANT',
        text: result.outcome === 'FAILED'
          ? [
              'I ran that through Automate, but it stopped before finishing.',
              failedEntry ? `It failed on ${failedEntry.type.toLowerCase().replaceAll('_', ' ')}.` : '',
              failedEntry?.errorMessage ?? result.errorMessage ?? '',
            ].filter(Boolean).join(' ')
          : [
              `I ran that through Automate and completed ${successfulActions.length} immediate action${successfulActions.length === 1 ? '' : 's'}.`,
              successfulActions.length
                ? `Completed: ${successfulActions.map((entry) => entry.type.toLowerCase().replaceAll('_', ' ')).join(', ')}.`
                : '',
              bundle.warnings.length ? `Review notes: ${bundle.warnings.join(' ')}` : '',
            ].filter(Boolean).join(' '),
        status: result.outcome === 'FAILED' ? 'REJECTED' : 'CONFIRMED',
        operationalProposal,
        executionTheater,
        automationExecution,
      }
      const finalThread = [...nextThread, assistantTurn]
      await finishExecuteThread(finalThread)
      showToast(result.outcome === 'FAILED' ? 'Automate execution stopped before completion.' : 'Automate ran the request immediately.', result.outcome === 'FAILED' ? 'error' : 'success')
      setPhase('IDLE')
      return
    } catch (error) {
      const message = error instanceof Error ? error.message : 'I could not run that request immediately.'
      if (!/could not identify the automation action yet/i.test(message)) {
        const assistantTurn: AiMagicChatTurn = {
          id: createChatTurnId(),
          role: 'WARNING',
          text: message,
          status: 'REJECTED',
          operationalProposal,
          executionTheater,
        }
        const finalThread = [...nextThread, assistantTurn]
        await finishExecuteThread(finalThread)
        setPhase('IDLE')
        return
      }
    }
    try {
      const { tour } = await generateAndRegisterTour({ prompt: userText, workspaceId: activeWorkspaceId, canPerform })
      const preambleKind: 'CONFIDENT' | 'APOLOGY' = MUTATE_INTENT_REGEX.test(userText) ? 'APOLOGY' : executionPreview.preambleKind
      const assistantTurn: AiMagicChatTurn = {
        id: createChatTurnId(),
        role: 'ASSISTANT',
        text: '',
        status: 'PENDING_APPROVAL',
        tourDefinition: tour,
        preambleKind,
        executionPreview: executionPreview.previewText,
        executionTheater,
        executionGraph: operationalPlan.envelope.executionGraph,
        executionSimulation: operationalPlan.envelope.simulation,
        operationalProposal,
        purchased: false,
      }
      const finalThread = [...nextThread, assistantTurn]
      updateVisibleRunState(runScope, (current) => ({
        ...current,
        chatTurns: finalThread,
        clarificationTurnId: null,
        quickControls: [],
      }))
      try {
        if (chatId) {
          const title = tour.title?.slice(0, 60) || userText.slice(0, 60)
          await saveAiChatSession({ id: chatId, turns: finalThread.map(sanitizeTurnForStorage), attachments: [], operatingMode: effectiveOperatingMode, status: 'ACTIVE' })
          setChatSessions((current) => current.map((session) => session.id === chatId ? { ...session, title, turns: finalThread.map(sanitizeTurnForStorage) } : session))
          void renameAiChatSession(chatId, title).catch(() => undefined)
        }
      } catch (historyError) {
        appendRunLineMessage(runScope, historyError instanceof Error ? `Tour generated, but the chat history could not be saved: ${historyError.message}` : 'Tour generated, but the chat history could not be saved.', 'WARNING')
      }
    } catch (error) {
      appendRunLineMessage(runScope, error instanceof Error ? error.message : 'I could not build that walkthrough.', 'WARNING')
    } finally {
      setPhase('IDLE')
    }
  }

  const runGeneration = async (overridePrompt?: string) => {
    if (submittingRef.current || isGenerating || isAiBlocked) return
    submittingRef.current = true
    if (effectiveOperatingMode === 'EXECUTE') {
      try { await runExecuteTourTurn(overridePrompt) } finally { submittingRef.current = false }
      return
    }
    if (!canGenerate && !overridePrompt?.trim()) { submittingRef.current = false; return }

    const userText = overridePrompt?.trim() || prompt.trim()
    const keywordCreatePhrase = findLineCreatePhrase(userText, linePolicy.createPhrases)
    const structuralTargets = liveEditTargets.filter((target) => (
      target.kind === 'goal' || target.kind === 'task' || target.kind === 'subtask'
    ))
    const toggleCreatesPlan = structuralTargets.some(isCreatePlanReference)
    const useCreateReference = Boolean(keywordCreatePhrase || toggleCreatesPlan)
    const runEditTargets = liveEditTargets.filter((target) => isEditableTarget(target) && !isCreatePlanReference(target))
    const runIsEditMode = !useCreateReference && runEditTargets.length > 0
    // A phrase explicitly switches every selected structural item into
    // reference material. Without one, only rows the user toggled to CREATE
    // seed the separate plan; adjacent EDIT rows cannot leak into it.
    const seedTargets = keywordCreatePhrase
      ? structuralTargets
      : structuralTargets.filter(isCreatePlanReference)
    const seedReferenceTree = useCreateReference ? buildSeedReferenceTree(goals, seedTargets) : null
    const runOperationalTargetRefs = runIsEditMode ? operationalTargetRefs : []
    // Structural nodes in a create turn travel only through REFERENCE_TREE.
    // Keep non-structural context attachments unchanged.
    const runAttachmentTargets = runIsEditMode
      ? liveEditTargets
      : liveEditTargets.filter((target) => !(
        target.kind === 'goal' || target.kind === 'task' || target.kind === 'subtask'
      ))
    if (import.meta.env.DEV && useCreateReference) {
      console.info('[Line plan mode]', {
        toggleCreatesPlan,
        keywordCreatePhrase,
        winner: keywordCreatePhrase ? 'keyword' : 'toggle',
      })
    }

    if (!runIsEditMode && isCoolingDown) {
      appendWarningMessage(
        `Free generation is cooling down for ${formatCountdown(cooldownRemaining)}. You can wait, or use the cooldown bypass from account credits when that control is available.`,
      )
      submittingRef.current = false
      return
    }

    // Tier-access pre-flight. Blocks locked-tier submits before they touch the
    // wire (defense in depth — server enforcement lives in quotaGuardService).
    // Skips when the flag is OFF or the plan is legacy (no aiTierAccess map)
    // so pre-rollout users see zero change. See integration plan §7d.
    if (lineFeatureFlags.aiTierSwitcher && !isTierSwitcherLegacyPlan) {
      const tierCheck = evaluateTierAccess({
        tier: activeAiTier,
        availableTiers: aiAvailableTiers,
        prompt: userText,
        planName: tierPlanName,
      })
      if (tierCheck.code !== 'ok') {
        appendWarningMessage(tierCheck.reason ?? 'This tier is not available on your plan.')
        if (tierCheck.code === 'tier_locked' && !isTierUpsellOnCooldown(activeAiTier)) {
          setTierUpsell({ tier: activeAiTier, trigger: 'locked_chip' })
        }
        submittingRef.current = false
        return
      }
      // === [AI-TIER-DEBUG] pre-flight — gated on ENABLE_ALL_FEATURES_FOR_TESTING.
      // See docs/pricing/line-tier-integration-plan.md Phase K for removal checklist. ===
      if (testingOverride) {
        console.log(
          `[AI-TIER-DEBUG] preflight     · tier=${activeAiTier} · mode=${runIsEditMode ? 'edit' : 'generate'} · estCredits=${tierCheck.estimatedCredits} · plan=${plan ?? 'unknown'}`,
        )
      }
      // === /AI-TIER-DEBUG ===
    }
    const displayText = nextUserDisplayTextRef.current?.trim() || userText
    nextUserDisplayTextRef.current = null
    /**
     * Recompute build-intent at send time from the ACTUAL text being sent
     * (may differ from `prompt` if runGeneration was called with an
     * overridePrompt, e.g. a suggestion acceptance or a summary Build chip).
     * Everything below reserves and commits credits against this decision.
     */
    const sendAffirmation =
      parseAiMagicOperationalConfirmationResponse(userText) === 'PREPARE_PROPOSAL'
    const sendBuildIntent = !isPlanningMode || runIsEditMode || sendAffirmation
    const sendPremiumAction: WalletReservationPurpose = sendBuildIntent ? premiumAction : 'AI_CHAT'
    const draftDataContexts = dataContexts
    const draftImageFiles = imageFiles
    const draftPendingUploadFiles = pendingUploadFiles
    const runDataContexts = [...sessionDataContexts, ...draftDataContexts]
    const runImageFiles = [...sessionImageFiles, ...draftImageFiles]
    const contextEstimate = estimateContextCost(
      runDataContexts,
      sendPremiumAction === 'SIMPLE_EDIT' ? 'EDIT' : sendBuildIntent ? 'GENERATION' : 'AI_CHAT',
      runImageFiles.length,
    )
    const sendEstimatedCredits = contextEstimate.upperBound
    const totalFixedIngestCr = contextEstimate.fixedIngestCr
    const runAttachmentAnalysisKey = buildAttachmentAnalysisKey(runDataContexts)
    const turnAttachmentPreviews = buildTurnAttachmentPreviews()
    // Stop any active dictation so the textarea snapshot is final before we
    // call the model — otherwise late-arriving recognition results could
    // race the API call.
    if (isListening) stopSpeechCapture()
    if (!currentUser || currentUser.isAnonymous) {
      appendWarningMessage('Sign in with a non-anonymous account to use AI Magic.')
      setPhase('IDLE')
      submittingRef.current = false
      return
    }

    const userTurn: AiMagicChatTurn = {
      id: createChatTurnId(),
      role: 'USER',
      text: displayText || '[attached context]',
      ...(userText && userText !== (displayText || '[attached context]') ? { requestText: userText } : {}),
      attachmentPreviews: turnAttachmentPreviews,
      attachmentContexts: buildAiChatAttachmentPayloads(runAttachmentTargets, draftDataContexts).slice(0, attachmentCap),
      ruleCandidate: detectRuleCandidate(displayText || ''),
    }
    const runScope: AiMagicRunScope = {
      mode: effectiveOperatingMode,
      initialChatId: activeChatId,
      resolvedChatId: activeChatId,
      userTurnId: userTurn.id ?? null,
    }
    const pendingRevision = findLatestProposalRevisionTurn(chatTurns)
    const pendingRevisionTurn = pendingRevision
      ? chatTurns.find((turn) => turn.id === pendingRevision.turnId) ?? null
      : null
    const revisionOf: ProposalRevisionContext | null = pendingRevision
      ? {
          turnId: pendingRevision.turnId,
          summary: pendingRevisionTurn?.proposalSummary
            ?? buildProposalSummaryForGoal(pendingRevision.preview.goal, pendingRevision.preview.meta.aiNote ?? null, t, pendingRevisionTurn?.text),
          goal: pendingRevision.preview.goal,
        }
      : null
    const nextThread = [...chatTurns, userTurn]
    const boardIntentText = nextThread
      .filter((turn) => turn.role === 'USER')
      .map((turn) => turn.text)
      .join('\n')
    const targetBoard = resolveAiTargetBoard(boardIntentText, activeBoard)
    const timelineRuleContext = buildTimelineRuleContext({
      elements: boardElements,
      checklistItems,
      selectedTargets: runOperationalTargetRefs,
      board: targetBoard,
    })
    const operationalPulseHints = mergeOperationalPulseHints(timelineRuleContext.pulseHints, pulseOperationalHints)
    const operationalPlan = createLineOperationalPlan({
      workspaceId: activeWorkspaceId,
      actor: {
        uid: currentUser.uid,
        role: activeRole,
        displayName: currentUser.displayName ?? currentUser.email ?? null,
      },
      surface: 'AI_MAGIC',
      prompt: userText,
      mode: isPlanningMode ? 'PLAN' : 'EXECUTE',
      selectedTargets: timelineRuleContext.selectedTargets,
      elements: boardElements,
      checklistItems,
      pulseHints: operationalPulseHints,
      supplementalRecords: integrationRuntimeRecords,
    })
    const operationalProposal = metadataFromOperationalPlan(operationalPlan)
    // On the Create tab, ignore the operational-confirmation parse entirely.
    // Users typing phrases like "just explain read-only" mid-interview
    // otherwise flip the router into LOCAL_ANSWER (workspace summary), which
    // bypasses the interview. The router's execute-path chip is separately
    // suppressed below, so together the whole Create tab is model-driven.
    const confirmationForRouter = effectiveOperatingMode === 'CREATE'
      ? null
      : parseAiMagicOperationalConfirmationResponse(userText)
    const operationalRoute = routeAiMagicOperationalPlan(
      operationalPlan,
      confirmationForRouter,
      {
        proposalAlreadyConfirmed: hasPreparedProposalIntent(nextThread),
        // Suppress pre-model "do it now / automation / explain" chips on the
        // Create tab regardless of plan-mode toggle. The Create tab must
        // ALWAYS interview first (context questions, dates, cadence, workspace
        // rules) before drafting; those chips belong AFTER the draft when the
        // user is deciding what to do with it.
        suppressClarificationChips: effectiveOperatingMode === 'CREATE',
        createMode: effectiveOperatingMode === 'CREATE',
        detailedCreatePrompt: effectiveOperatingMode === 'CREATE' && isDetailedCreatePrompt(userText, {
          hasAttachments: runDataContexts.length > 0 || runImageFiles.length > 0,
        }),
      },
    )
    const operationalGuidance = [
      '[OPERATIONAL_CONTEXT]',
      timelineRuleContext.summary,
      operationalPlan.envelope.contextDigest?.summary
        ? `Retrieved context slices:\n${operationalPlan.envelope.contextDigest.summary}`
        : 'Retrieved context slices: retrieval fell back to timeline rules only.',
      operationalPulseHints.length
        ? `Pulse signals:\n- ${operationalPulseHints.slice(0, 5).map((hint) => hint.summary).join('\n- ')}`
        : 'Pulse signals: no local Pulse risks detected.',
      (() => {
        const diagnostics = operationalPlan.envelope.contextDigest?.retrievalDiagnostics as {
          sourceCounts?: Record<string, number>
          compression?: { rawCharacterCount?: number; compressedCharacterCount?: number; sliceCount?: number }
        } | undefined
        if (!diagnostics) return ''
        return [
          'Retrieval diagnostics:',
          `Sources: ${Object.entries(diagnostics.sourceCounts ?? {}).map(([scope, count]) => `${scope}=${count}`).join(', ') || 'none'}.`,
          `Compression: ${diagnostics.compression?.rawCharacterCount ?? 0} -> ${diagnostics.compression?.compressedCharacterCount ?? 0} chars across ${diagnostics.compression?.sliceCount ?? 0} slices.`,
        ].join('\n')
      })(),
      timelineRuleContext.issues.length
        ? `Relevant constraints:\n- ${timelineRuleContext.issues.map((issue) => issue.message).join('\n- ')}`
        : 'Relevant constraints: none found in the currently loaded timeline/checklist graph.',
      `Operation risk: ${operationalPlan.envelope.risk}. Manage approval required: ${operationalPlan.envelope.reviewRequired ? 'yes' : 'no'}.`,
      'Respect Goal -> Task -> Subtask containment. Checklist dates must remain inside the owning node. Checklist proof/location/co-sign/dependency gates cannot be bypassed.',
    ].join('\n')
    const modeGuidance = isPlanningMode
      ? '[OPERATING_MODE:PLANNING]\nRemain read-only. Interview, plan, audit, and prepare Manage drafts only — never claim that data was written.'
      : '[OPERATING_MODE:EXECUTION]\nPrepare requested changes for the existing Manage draft and Save approval flow.'
    const buildRunBrief = (sourcePrompt: string, digest?: AttachmentPlanningDigest | null) =>
      `${modeGuidance}\n\n${operationalGuidance}\n\n${buildMergedBrief(
        sourcePrompt,
        digest ? renderAttachmentPlanningBrief(digest) : undefined,
        runDataContexts,
      )}`
    setActivePromptDraft('')
    updateVisibleRunState(runScope, (current) => ({
      ...current,
      chatTurns: nextThread,
      prompt: '',
      dataContexts: [],
      imageFiles: [],
      pendingUploadFiles: [],
      quickControls: [],
      quickSelections: {},
      attachmentDigest: null,
      attachmentDigestFingerprint: null,
      attachmentDigestStatus: 'idle',
      attachmentDigestError: null,
    }))
    const thinkingSequence = buildLineThinkingStatuses({
      userText,
      isEdit: runIsEditMode,
      hasImages: runImageFiles.length > 0,
      hasAttachments: runDataContexts.length > 0,
    })
    setRunProgress(runScope, thinkingSequence[0] ?? 'Line is thinking', thinkingSequence)

    setError(null)
    setSuccessMessage('')
    setPhase('PREFLIGHT')

    let chatIdForRun = activeChatId
    if (!chatIdForRun) {
      try {
        const initialTitle = (displayText || userText || 'New chat').slice(0, 60)
        const initialAttachments = sessionAttachments.slice(0, attachmentCap)
        const initialTurns = nextThread.map(sanitizeTurnForStorage)
        const created = await createAiChatSession({
          workspaceId: activeWorkspaceId,
          operatingMode: effectiveOperatingMode,
          title: initialTitle,
          turns: initialTurns,
          attachments: initialAttachments,
        })
        chatIdForRun = created.id
        pendingHistorySessionIdsRef.current.add(chatIdForRun)
        setChatSessions((current) => [{
          id: chatIdForRun!, workspaceId: activeWorkspaceId, ownerId: currentUser.uid,
          title: initialTitle, status: 'ACTIVE', operatingMode: effectiveOperatingMode,
          turns: initialTurns, attachments: initialAttachments,
        }, ...current.filter((session) => session.id !== chatIdForRun)])
        attachRunChatId(runScope, chatIdForRun)
      } catch (historyError) {
        appendRunLineMessage(runScope, historyError instanceof Error ? `Could not create the chat history: ${historyError.message}` : 'Could not create the chat history.', 'WARNING')
      }
    }

    if (operationalRoute.kind !== 'CONTINUE_MODEL') {
      const assistantTurnId = createChatTurnId()
      const assistantTurn: AiMagicChatTurn = {
        id: assistantTurnId,
        role: operationalRoute.kind === 'BLOCK' ? 'WARNING' : 'ASSISTANT',
        text: operationalRoute.message,
        status: operationalRoute.kind === 'BLOCK'
          ? 'REJECTED'
          : operationalRoute.kind === 'CONFIRM'
            ? 'PENDING_CLARIFICATION'
            : 'CONFIRMED',
        routing: {
          decision: operationalRoute.kind === 'BLOCK'
            ? 'REJECT'
            : operationalRoute.kind === 'CONFIRM'
              ? 'CLARIFY'
              : 'ROUTE',
          confidenceScore: operationalPlan.envelope.risk === 'LOW' ? 0.9 : 0.8,
          reasoning: operationalPlan.envelope.audit.summary,
          requiresUserApproval: operationalRoute.kind === 'CONFIRM',
          ...(operationalRoute.kind === 'CONFIRM'
            ? {
                clarification: {
                  field: operationalRoute.clarificationField ?? 'operational_confirmation',
                  inputType: 'SELECT',
                  label: operationalRoute.message,
                  options: operationalRoute.quickControls.map((control) => control.label),
                },
              }
            : {}),
        },
        quickControls: operationalRoute.kind === 'CONFIRM' ? operationalRoute.quickControls : [],
        operationalProposal,
      }
      const finalThread = [...nextThread, assistantTurn]
      updateVisibleRunState(runScope, (current) => ({
        ...current,
        chatTurns: finalThread,
        clarificationTurnId: operationalRoute.kind === 'CONFIRM' ? assistantTurnId : null,
        quickControls: operationalRoute.kind === 'CONFIRM' ? operationalRoute.quickControls : [],
      }))
      try {
        if (chatIdForRun) {
          const finalized = await finalizeSentDraftAttachments({
            chatId: chatIdForRun,
            turns: finalThread,
            userTurnId: userTurn.id!,
            sourceChatTitle: chatSessions.find((session) => session.id === chatIdForRun)?.title ?? null,
            draftDataContexts,
            draftPendingUploadFiles,
          })
          setRunChatTurns(runScope, finalized.turns)
          await saveAiChatSession({
            id: chatIdForRun,
            turns: finalized.turns.map(sanitizeTurnForStorage),
            attachments: finalized.persistedAttachments,
            operatingMode: effectiveOperatingMode,
            status: 'ACTIVE',
          })
          patchHistorySession(chatIdForRun, {
            turns: finalized.turns.map(sanitizeTurnForStorage),
            attachments: finalized.persistedAttachments,
            operatingMode: effectiveOperatingMode,
            status: 'ACTIVE',
          })
          promoteDraftAttachmentsToSession(runScope, finalized.resolvedDraftContexts, draftImageFiles)
        }
      } catch (historyError) {
        appendRunLineMessage(runScope, historyError instanceof Error ? `Line replied, but the chat history could not be saved: ${historyError.message}` : 'Line replied, but the chat history could not be saved.', 'WARNING')
      }
      clearRunProgress(runScope)
      setPhase('IDLE')
      submittingRef.current = false
      return
    }

    let walletReservationId: string | null = null
    try {
      const reservation = await walletService.reserveCredits(activeWorkspaceId, sendEstimatedCredits, { purpose: sendPremiumAction, targetId: activeGoalId ?? activeWorkspaceId })
      walletReservationId = reservation.reservationId
    }
    catch (error) {
      const code = typeof error === 'object' && error && 'code' in error ? String(error.code) : ''
      if (code.includes('resource-exhausted')) setUpgradeLocked(true)
      appendRunLineMessage(runScope, error instanceof Error ? error.message : `This action requires ${sendEstimatedCredits} credits.`, 'WARNING')
      clearRunProgress(runScope)
      setPhase('IDLE')
      submittingRef.current = false
      return
    }

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      await new Promise((resolve) => window.setTimeout(resolve, 300))
    }

    const escapeHintChip = operationalRoute.kind === 'CONTINUE_MODEL' ? operationalRoute.escapeHint : undefined
    const appendEscapeChip = (chips: AiMagicQuickControl[]) =>
      escapeHintChip ? [...chips, escapeHintChip] : chips

    let modelResponseReceived = false
    let actualCredits = totalFixedIngestCr
    let mergedBriefForUsage = `${modeGuidance}\n\n${operationalGuidance}\n\n${buildMergedBrief(userText, undefined, runDataContexts)}`
    let latestAttachmentPlanningDigest: AttachmentPlanningDigest | null = null
    const accountUsage = (usage: Parameters<typeof normalizeGeminiUsage>[0], input: string) => {
      if (!usage) return
      actualCredits += quoteAiCredits('better', normalizeGeminiUsage(usage, input)).credits
    }
    let responseChatTitle = ''
    const runAiPanelJob = async (signal: AbortSignal): Promise<AiPanelJobOutcome> => {
        let attachmentPlanningDigest: AttachmentPlanningDigest | null = null
        let attachmentInventory: AttachmentInventory | null = null
        const priorAttachmentApproval = findLatestAttachmentApproval(
          nextThread.filter((turn) => turn.role === 'USER').map((turn) => turn.text),
        )
        if (canUseAttachmentIntelligence) {
          setRunProgress(runScope, 'Reading attachments, extracting dates, resources, and checklist signals…', thinkingSequence)
          attachmentPlanningDigest = await ensureAttachmentDigest(userText, {
            signal,
            contexts: runDataContexts,
            imageFiles: runImageFiles,
            fingerprint: runAttachmentAnalysisKey,
            currentDigest: attachmentDigest,
            currentFingerprint: attachmentDigestFingerprint,
            runScope,
          })
          // The inventory is intentionally a second, separately-metered model
          // pass. Once the user submits a curation answer (including none),
          // do not re-extract or reopen the gate on later interview turns.
          if (linePolicy.attachmentInventoryEnabled && !priorAttachmentApproval) {
            try {
              setRunProgress(runScope, 'Finding source-grounded work items for you to curate…', thinkingSequence)
              const sources: AttachmentPlanningSourceInput[] = runDataContexts.map((context) => ({
                id: context.id,
                kind: toAttachmentPlanningSourceKind(context.kind),
                label: context.label,
                text: context.text,
                mimeType: context.mimeType,
              }))
              attachmentInventory = await analyzeAttachmentInventory({
                prompt: userText,
                sources,
                imageFiles: runImageFiles,
                signal,
              })
            } catch (error) {
              // Inventory curation is additive. Keep the established digest
              // path alive if its extra extraction call is unavailable.
              console.warn('[Line attachment inventory] extraction skipped', error)
            }
          }
        }
        latestAttachmentPlanningDigest = attachmentPlanningDigest
        mergedBriefForUsage = buildRunBrief(userText, attachmentPlanningDigest)
        if (attachmentInventory?.extractedCount) {
          accountUsage(attachmentPlanningDigest?.usageMetadata, userText || '[attachment intelligence]')
          accountUsage(attachmentInventory.usageMetadata, userText || '[attachment inventory]')
          modelResponseReceived = true
          const inventoryPrompt = t('NLSAiInterviewAttachmentIntro', { count: attachmentInventory.extractedCount })
          const questionSet = buildAttachmentInventoryQuestionSet(attachmentInventory, inventoryPrompt)
          const routing: RoutingPayload = {
            decision: 'INTERVIEW_MODE',
            reasoning: 'Line needs the user to curate attachment-derived work before drafting.',
            confidenceScore: 0,
            interviewMode: true,
            requiresUserApproval: true,
            operatingMode: isPlanningMode ? 'PLANNING' : 'EXECUTION',
            clarification: {
              field: 'attachment_inventory',
              inputType: 'TEXT',
              label: inventoryPrompt,
              questionSet,
            },
          }
          return {
            kind: 'ASK',
            runScope,
            turn: {
              id: createChatTurnId(),
              role: 'ASSISTANT',
              text: inventoryPrompt,
              status: 'PENDING_CLARIFICATION',
              confidence: 0,
              routing,
              quickControls: [],
              questionSet,
            },
            quickControls: [],
          }
        }
        const constraintContext = buildTimelineConstraintContext(boardElements)
        const chatExecution = await runAiMagicChatTurn({
          workspaceId: activeWorkspaceId,
          uid: currentUser.uid,
          thread: nextThread,
          currentBrief: mergedBriefForUsage,
          mode: runIsEditMode ? 'edit' : 'generate',
          selectedTargets: runIsEditMode ? runEditTargets.map((target) => `${target.kind}: ${target.label}`) : [],
          workspaceSummary: buildWorkspaceSummary(),
          seedReferenceTree,
          imageFiles: runImageFiles,
          signal,
          constraintContext,
          operatingMode: isPlanningMode ? 'PLANNING' : 'EXECUTION',
          targetBoard,
          attachmentDigest: attachmentPlanningDigest,
          checklistCapabilityContext,
          tier: activeAiTier,
          revisionOf,
        })
        modelResponseReceived = chatExecution.modelResponseReceived === true
        setRoutingFeedback({
          status: chatExecution.status,
          confidence: chatExecution.confidence,
          routing: chatExecution.routing,
          reasoning: chatExecution.reasoning,
        })
        chatExecution.softWarnings.forEach((warning) => appendRunLineMessage(runScope, `System Constraint Warning: ${warning}`, 'WARNING'))
        const chatResult = chatExecution.data
        accountUsage(attachmentPlanningDigest?.usageMetadata, userText || '[attachment intelligence]')
        accountUsage(attachmentInventory?.usageMetadata, userText || '[attachment inventory]')
        accountUsage(chatResult?.usageMetadata, mergedBriefForUsage)
        responseChatTitle = chatResult?.chatTitle?.trim().slice(0, 60) ?? ''
        if (!chatResult) {
          const turnId = createChatTurnId()
          const chips = appendEscapeChip([])
          return {
            kind: 'ASK',
            runScope,
            turn: {
              id: turnId,
              role: 'ASSISTANT',
              text: chatExecution.reasoning.join(' ') || chatExecution.message || 'Please clarify the request.',
              status: chatExecution.status,
              confidence: chatExecution.confidence,
              routing: chatExecution.routing.clarification || chatExecution.routing.interviewMode ? chatExecution.routing : {
                ...chatExecution.routing,
                clarification: {
                  field: 'clarification', inputType: 'TEXT',
                  label: chatExecution.message || 'What detail should Line use?',
                },
              },
              reasoning: chatExecution.reasoning,
              suggestions: chatExecution.suggestions,
              quickControls: chips,
            },
            quickControls: chips,
          }
        }
        if (chatResult.status === 'ASK') {
          const chips = appendEscapeChip(chatResult.quickControls)
          // Merge the model-emitted questionSet into the routing.clarification
          // so the panel's activeQuestionSet computation picks it up on the
          // very next render — and mirror it on the turn itself so history
          // reload can hydrate the InterviewQuestionSet from Firestore too.
          const emittedQuestionSet = chatResult.questionSet
            ?? (chatResult.interviewEscalation
              ? buildLineInterviewEscalationQuestionSet(chatResult.interviewEscalation, t)
              : undefined)
          const displayMessage = chatResult.interviewEscalation
            ? emittedQuestionSet?.questions.map((question) => question.prompt).join('\n\n') ?? chatResult.message
            : chatResult.message
          const baseRouting = chatExecution.routing.clarification || chatExecution.routing.interviewMode ? chatExecution.routing : {
            ...chatExecution.routing,
            clarification: { field: 'clarification', inputType: 'TEXT' as const, label: displayMessage },
          }
          const routing = emittedQuestionSet && baseRouting.clarification
            ? { ...baseRouting, clarification: { ...baseRouting.clarification, questionSet: emittedQuestionSet } }
            : baseRouting
          const turnId = createChatTurnId()
          chatResult.interviewEscalation?.triggers.forEach((trigger) => {
            const preScaleCounts: Record<string, number> = trigger.kind === 'scale'
              ? { tasks: trigger.taskCount, checklists: trigger.checklistCount }
              : { attachmentItems: trigger.attachmentCount, promptItems: trigger.promptCount }
            emitLineSignal('INTERVIEW_ESCALATION_FIRED', turnId, {
              trigger: trigger.kind,
              preScaleCounts,
            })
          })
          return {
            kind: 'ASK',
            runScope,
            turn: {
              id: turnId, role: 'ASSISTANT', text: displayMessage,
              status: 'PENDING_CLARIFICATION', confidence: chatExecution.confidence,
              routing,
              suggestions: chatExecution.suggestions,
              quickControls: chips,
              questionSet: emittedQuestionSet,
            },
            quickControls: chips,
          }
        }

        if (chatResult.status === 'READY_GENERATE' && chatResult.goal) {
          const turnId = createChatTurnId()
          const summary = buildProposalSummaryForGoal(chatResult.goal, chatResult.message, t, chatResult.message)
          const meta: GeneratedGoalMeta = {
            mode: pendingRevision?.preview.meta.mode ?? 'generate',
            baseline: pendingRevision?.preview.meta.baseline ?? null,
            issues: pendingRevision?.preview.meta.issues ?? [],
            aiNote: chatResult.message,
            confidence: chatExecution.confidence,
            reasoning: chatExecution.reasoning,
            targetBoard: pendingRevision?.preview.meta.targetBoard ?? targetBoard,
            operationalProposal: pendingRevision?.preview.meta.operationalProposal ?? operationalProposal,
            aiChangedIds: pendingRevision?.preview.meta.aiChangedIds,
            agentActions: pendingRevision?.preview.meta.agentActions,
          }
          return {
            kind: 'GOAL_READY',
            runScope,
            turn: {
              id: turnId,
              role: 'ASSISTANT',
              text: summary
                ? (chatResult.message || 'Here is what I heard. Ready to build when you say the word.')
                : (chatResult.message || 'I built the draft. Move it to Manage before anything is saved.'),
              status: 'PENDING_APPROVAL',
              confidence: chatExecution.confidence,
              routing: chatExecution.routing,
              reasoning: chatExecution.reasoning,
              // Summary gate: withhold the auto-apply action until the user
              // confirms via the Build chip on the summary turn.
              actions: [],
              suggestions: chatExecution.suggestions,
              operationalProposal,
              proposalSummary: summary,
              proposalPreview: { goal: chatResult.goal, meta } satisfies AiMagicProposalPreview,
            },
            goal: chatResult.goal,
            meta,
            isFreshGenerate: !pendingRevision,
            replacesTurnId: pendingRevision?.turnId ?? null,
          }
        }

        if (chatResult.status === 'READY_EDIT' && chatResult.editInstruction) {
          const selectedIds = new Set(liveEditTargets.filter(isEditableTarget).map((target) => (
            target.kind === 'subtask' ? target.subTaskId : target.kind === 'task' ? target.taskId : target.kind === 'checklist_item' ? target.itemId : target.goalId
          )))
          const selectedElements = boardElements.filter((element) => selectedIds.has(element.id))
          if (selectedElements.some((element) => !canPerformOnNode('timeline.edit', element.id)) || selectedElements.some((element) => !canEditElement(element))) {
            return { kind: 'NEEDS_MORE_DETAIL', runScope, message: 'You do not have permission to approve changes for every selected item.' }
          }
          const baseGoal = goals.find((candidate) => candidate.id === activeGoalId)
          if (!baseGoal) {
            return { kind: 'NEEDS_MORE_DETAIL', runScope, message: 'That goal no longer exists. Pick another target and send the change again.' }
          }
          const batchResult = await generateValidatedTimelineEditBatch({
            uid: currentUser.uid,
            baseGoal,
            targets: expandEditTargets(baseGoal, chatResult.editInstruction),
            instruction: chatResult.editInstruction,
            signal,
            constraintContext,
          })
          const editResult = batchResult.data
          accountUsage(editResult?.usageMetadata, chatResult.editInstruction)
          if (!editResult) {
            return {
              kind: 'ASK',
              runScope,
              turn: {
                id: createChatTurnId(), role: 'ASSISTANT',
                text: batchResult.reasoning.join(' ') || 'Please clarify the requested edit.',
                status: 'PENDING_CLARIFICATION', confidence: batchResult.confidence,
                routing: batchResult.routing.clarification ? batchResult.routing : {
                  ...batchResult.routing,
                  clarification: {
                    field: 'clarification', inputType: 'TEXT',
                    label: batchResult.reasoning.join(' ') || 'What should Line change?',
                  },
                },
                quickControls: [],
              },
              quickControls: [],
            }
          }
          const changedIds = Array.from(collectChangedNodeIds(editResult.goal, editResult.baseline))
          const editSummary = buildProposalSummaryForGoal(editResult.goal, editResult.note ?? chatResult.message ?? null, t, chatResult.message)
          const meta: GeneratedGoalMeta = {
            mode: 'edit',
            baseline: editResult.baseline,
            issues: editResult.issues,
            aiNote: [chatResult.message, editResult.note].filter(Boolean).join('\n'),
            aiChangedIds: changedIds,
            agentActions: batchResult.actions,
            confidence: batchResult.confidence,
            reasoning: batchResult.reasoning,
            targetBoard,
            operationalProposal,
          }
          return {
            kind: 'GOAL_READY',
            runScope,
            turn: {
              id: createChatTurnId(), role: 'ASSISTANT',
              text: editSummary
                ? (editResult.note || chatResult.message || 'Here is the edit I have in mind. Say the word when you want me to apply it.')
                : (editResult.note || 'I prepared the edit as a Manage draft. Nothing is saved until you approve it there.'),
              status: 'PENDING_APPROVAL', confidence: batchResult.confidence,
              routing: batchResult.routing, reasoning: batchResult.reasoning,
              // Same gate as READY_GENERATE — hide the auto-apply action
              // until the user confirms via the Build chip.
              actions: [],
              suggestions: batchResult.suggestions,
              operationalProposal,
              proposalSummary: editSummary,
              proposalPreview: { goal: editResult.goal, meta } satisfies AiMagicProposalPreview,
            },
            goal: editResult.goal,
            meta,
            isFreshGenerate: false,
            replacesTurnId: pendingRevision?.turnId ?? null,
          }
        }

        return { kind: 'NEEDS_MORE_DETAIL', runScope, message: chatResult.message || 'I need one more detail before I can build this safely.' }
      }

    const { outcome } = beginJob<AiPanelJobOutcome>({
      source: 'AI_GENERATOR',
      label: runIsEditMode ? 'Applying scoped edit…' : 'Generating timeline…',
      workspaceId: activeWorkspaceId,
      run: runAiPanelJob,
    })

    try {
      const completed = await outcome
      if (!completed) {
        if (walletReservationId) await walletService.releaseCredits(activeWorkspaceId, walletReservationId)
        return
      }
      const committedCredits = Math.max(1, Math.min(sendEstimatedCredits, actualCredits || sendEstimatedCredits))
      if (walletReservationId) await walletService.commitCredits(activeWorkspaceId, walletReservationId, committedCredits)

      if (modelResponseReceived) {
        try {
          const modelCredits = Math.max(0, committedCredits - totalFixedIngestCr)
          const creditSummary = totalFixedIngestCr > 0
            ? `spent ${committedCredits} Cr (${totalFixedIngestCr} fixed + ${modelCredits} model)`
            : `spent ${committedCredits} Cr`
          const baseTurn: AiMagicChatTurn = completed.kind === 'NEEDS_MORE_DETAIL'
            ? { id: createChatTurnId(), role: 'WARNING', text: completed.message }
            : completed.turn
          const responseTurn: AiMagicChatTurn = {
            ...baseTurn,
            creditSummary,
          }
          let persistedTurns = [...nextThread, responseTurn]
          if (completed.kind === 'GOAL_READY' && completed.replacesTurnId) {
            persistedTurns = persistedTurns.map((turn) => (
              turn.id === completed.replacesTurnId ? collapseRevisedProposalTurn(turn) : turn
            ))
          }
          const attachmentIntelligenceSnapshot = latestAttachmentPlanningDigest
            ? toAttachmentPlanningSnapshot(latestAttachmentPlanningDigest)
            : null
          if (attachmentIntelligenceSnapshot) {
            persistedTurns = persistedTurns.map((turn) => turn.id === userTurn.id
              ? { ...turn, attachmentIntelligence: attachmentIntelligenceSnapshot }
              : turn)
          }
          setRunChatTurns(runScope, (current) => {
            const withUser = current.some((turn) => turn.id === userTurn.id) ? current : nextThread
            const nextTurnsWithDigest = attachmentIntelligenceSnapshot
              ? withUser.map((turn) => turn.id === userTurn.id ? { ...turn, attachmentIntelligence: attachmentIntelligenceSnapshot } : turn)
              : withUser
            return nextTurnsWithDigest.some((turn) => turn.id === responseTurn.id) ? nextTurnsWithDigest : [...nextTurnsWithDigest, responseTurn]
          })
          let chatId = chatIdForRun
          if (!chatId) {
            const created = await createAiChatSession({
              workspaceId: activeWorkspaceId,
              operatingMode: effectiveOperatingMode,
              title: responseChatTitle,
              turns: persistedTurns,
              attachments: sessionAttachments.slice(0, attachmentCap),
            })
            chatId = created.id
            pendingHistorySessionIdsRef.current.add(chatId)
            setChatSessions((current) => [{
              id: chatId!, workspaceId: activeWorkspaceId, ownerId: currentUser?.uid ?? '',
              title: responseChatTitle, status: 'ACTIVE', operatingMode: effectiveOperatingMode,
              turns: persistedTurns, attachments: sessionAttachments.slice(0, attachmentCap),
            }, ...current.filter((session) => session.id !== chatId)])
            attachRunChatId(runScope, chatId)
          } else if (responseChatTitle) {
            const activeTitle = chatSessions.find((session) => session.id === chatId)?.title ?? ''
            if (!activeTitle.trim() || activeTitle === 'New chat') await renameAiChatSession(chatId, responseChatTitle)
          }

          const finalized = await finalizeSentDraftAttachments({
            chatId,
            turns: persistedTurns,
            userTurnId: userTurn.id!,
            sourceChatTitle: responseChatTitle || chatSessions.find((session) => session.id === chatId)?.title || null,
            draftDataContexts,
            draftPendingUploadFiles,
          })
          persistedTurns = finalized.turns
          setRunChatTurns(runScope, (current) => current.map((turn) => turn.id === userTurn.id ? persistedTurns.find((candidate) => candidate.id === userTurn.id) ?? turn : turn))
          const pendingProposal = completed.kind === 'GOAL_READY' && responseTurn.id && responseTurn.proposalSummary
            ? pendingProposalForTurn(responseTurn.id, { goal: completed.goal, meta: completed.meta }, responseTurn.proposalSummary, linePolicy.pendingProposalTtlMs)
            : undefined
          await saveAiChatSession({ id: chatId, turns: persistedTurns.map(sanitizeTurnForStorage), attachments: finalized.persistedAttachments, operatingMode: effectiveOperatingMode, status: 'ACTIVE', pendingProposal })
          patchHistorySession(chatId, {
            ...(responseChatTitle ? { title: responseChatTitle } : {}),
            turns: persistedTurns.map(sanitizeTurnForStorage),
            attachments: finalized.persistedAttachments,
            operatingMode: effectiveOperatingMode,
            status: 'ACTIVE',
            ...(pendingProposal ? { pendingProposal } : {}),
          })
          promoteDraftAttachmentsToSession(runScope, finalized.resolvedDraftContexts, draftImageFiles)
        } catch (historyError) {
          appendRunLineMessage(runScope, historyError instanceof Error ? `Line replied, but the chat history could not be saved: ${historyError.message}` : 'Line replied, but the chat history could not be saved.', 'WARNING')
        }
      }
    } catch (caughtError) {
      if (walletReservationId) await walletService.releaseCredits(activeWorkspaceId, walletReservationId).catch(() => undefined)
      // AbortError is expected during user-triggered cancel; everything else
      // surfaces back into the chat.
      const isAbort = caughtError instanceof DOMException && caughtError.name === 'AbortError'
      if (!isAbort) {
        appendRunLineMessage(runScope,
          caughtError instanceof Error ? caughtError.message : 'I could not generate a timeline from that input.',
          'WARNING',
        )
      }
    } finally {
      clearRunProgress(runScope)
      submittingRef.current = false
    }
  }

  const activeClarificationTurn = clarificationTurnId
    ? chatTurns.find((turn) => turn.id === clarificationTurnId)
    : undefined
  const activeClarification = activeClarificationTurn?.routing?.clarification

  // Compute the effective QuestionSet for the currently-open clarification:
  // prefer the model-emitted `questionSet` (new schema), and auto-upgrade
  // legacy `quickControls` chips into a synthesized set when no explicit set
  // was provided. Result drives whether the composer enters interview mode.
  const activeQuestionSet: AiMagicQuestionSet | null = useMemo(() => {
    if (!activeClarification) return null
    const explicit = activeClarification.questionSet ?? activeClarificationTurn?.questionSet
    if (explicit) return explicit
    return upgradeQuickControlsToQuestionSet(
      activeClarificationTurn?.id,
      quickControls,
      activeClarificationTurn?.text,
    )
  }, [activeClarification, activeClarificationTurn, quickControls])
  const interviewModeActive = Boolean(activeQuestionSet)
  // When the user starts typing in the composer during interview mode, the
  // carousel is visually blocked and the Send button dispatches the free
  // text as a normal clarification reply. Empty textarea → carousel is fully
  // interactive again. Both states share the same rendered composer.
  const interviewFreeTextMode = interviewModeActive && prompt.trim().length > 0

  // A loaded historical chat is "continuing" when we have a chat id, at least
  // one existing turn, and the user hasn't explicitly reset to a fresh draft.
  // Drives the composer's placeholder, the ↩ continuing chip, and the send
  // button's aria label so the user has a visible signal that follow-ups go
  // into the same thread rather than starting a new one.
  const isContinuingLoadedChat = Boolean(activeChatId) && chatTurns.length > 0 && !isDraftingNewChat
  // Anchor the follow-up placeholder on the last exchange (Line's most recent
  // reply, or the user's last message if Line hasn't answered yet). This is
  // what the user actually wants to build on — the chat title is just the
  // first prompt, which reads as "Continue with <what I opened with>" and
  // ignores everything that has since happened in the thread.
  const followUpAnchor = isContinuingLoadedChat ? composeFollowUpAnchor(chatTurns) : { snippet: '', source: 'none' as const }
  // Single source of truth for the placeholder text the composer displays.
  // In interview mode the textarea is an ESCAPE HATCH — typing here bypasses
  // the carousel and sends a free-text reply to Line. The placeholder must
  // signal that role, not falsely imply it answers the currently-focused
  // question (which would confuse the user).
  const composerPlaceholder = interviewModeActive
    ? t('NLSAiComposerReplyFreely')
    : activeClarification
    ? t('NLSAiComposerReplyToOpenQuestion')
    : effectiveOperatingMode === 'EXECUTE'
      ? isContinuingLoadedChat
        ? followUpAnchor.snippet
          ? followUpAnchor.source === 'assistant'
            ? t('NLSAiComposerFollowUpAssistant', { snippet: followUpAnchor.snippet })
            : t('NLSAiComposerFollowUpUser', { snippet: followUpAnchor.snippet })
          : t('NLSAiComposerContinueExecute')
        : t('NLSAiComposerDescribeExecute')
      : isContinuingLoadedChat
        ? followUpAnchor.snippet
          ? followUpAnchor.source === 'assistant'
            ? t('NLSAiComposerRefineAssistant', { snippet: followUpAnchor.snippet })
            : t('NLSAiComposerFollowUpUser', { snippet: followUpAnchor.snippet })
          : t('NLSAiComposerContinueCreate')
        : dataContexts.length > 0
          ? t('NLSAiComposerGuidanceForData')
          : imageFiles.length > 0
            ? t('NLSAiComposerImageGuidance')
            : t('NLSAiComposerDefaultPromptHint')
  // Tab-to-fill inserts THIS string, not the full placeholder — the placeholder
  // reads as a hint ("Follow up on X — ask more, or dig deeper…") which is not
  // a usable prompt. The tab-fill is a natural prompt starter that leaves the
  // caret ready for the user to type the actual follow-up.
  const composerTabFill = isContinuingLoadedChat
    ? followUpAnchor.snippet
      ? `Following up on “${followUpAnchor.snippet}…”: `
      : 'Follow up: '
    : composerPlaceholder
  // The subtle `⇥ Tab` hint only surfaces when a loaded chat is open, the
  // composer is empty, and Line isn't waiting for a clarification (where Tab
  // must retain its default focus behavior).
  const showContinuingTabHint = isContinuingLoadedChat && !activeClarification && prompt.length === 0

  // Build and lagging-outcome events deliberately share one coarsened context
  // factory. It never includes a title, prompt, or attachment name.
  const lineBuildSignalContext = (turnId: string) => {
    const pending = resolvePendingPreview(turnId, pendingPreviewsRef.current, readPendingPreviewStore(activeWorkspaceId), chatTurns)
    const goal = pending?.goal
    return {
      workspaceId: activeWorkspaceId,
      planType: prompt,
      workspaceType: activeWorkspaceId ? 'team' : 'personal',
      attachmentCount: persistedSessionAttachments.length,
      timelineStart: goal?.startDate,
      timelineEnd: goal?.endDate,
      language: typeof navigator === 'undefined' ? 'en' : navigator.language,
    }
  }

  const openPendingPreview = async (turnId: string) => {
    const pending = resolvePendingPreview(turnId, pendingPreviewsRef.current, readPendingPreviewStore(activeWorkspaceId), chatTurns)
    if (!pending) {
      setActionErrors((current) => ({ ...current, [turnId]: t('NLSAiPreviewUnavailable') }))
      return
    }
    pendingPreviewsRef.current.set(turnId, pending)
    // Cover the click→Manage handoff with the constellation loader. The
    // provider's built-in 300ms delayMs keeps instant transitions instant —
    // the overlay only surfaces when Firestore (or any other await here)
    // actually stalls.
    await runWithGlobalLoading(async () => {
    try {
      if (pending.meta.mode === 'generate' && !canPerformOnNode('timeline.create', null)) {
        throw new Error('Your workspace role cannot create timeline items.')
      }
      if (pending.meta.mode === 'edit') {
        const actionTargets = pending.meta.agentActions
          ?.filter((action) => action.type === 'DELTA_PATCH')
          .map((action) => action.targetId) ?? []
        const targetElements = boardElements.filter((element) => actionTargets.includes(element.id))
        if (targetElements.some((element) => !canPerformOnNode('timeline.edit', element.id)) || targetElements.some((element) => !canEditElement(element))) {
          throw new Error('Your permissions changed. You can no longer edit every proposed item.')
        }
      }
      setActionErrors((current) => ({ ...current, [turnId]: '' }))

      let goalForHandoff = pending.goal
      let metaForHandoff = pending.meta
      if (pending.meta.mode === 'generate') {
        // Persist the whole tree up-front so Firestore holds real element +
        // checklist docs. Without this, the review surface only has the
        // in-memory Goal — every checklist row looks unresolved because the
        // flat `checklists` collection is empty and `checklistItems`
        // projections have nothing to bridge from. See ManageChecklistDetailSurface.
        const board = pending.meta.targetBoard ?? activeBoard
        const inputs = flattenGoalToElementInputs(pending.goal, board)
        const savedElements = await createElements(inputs)
        // createElements returns saved elements in input order; zip them back
        // to translate any downstream reference from the AI's local ids to the
        // real Firestore ids.
        const idTranslation = new Map<string, string>()
        inputs.forEach((input, index) => {
          const saved = savedElements[index]
          if (input.id && saved?.id) idTranslation.set(input.id, saved.id)
        })
        const rebuiltGoals = buildGoalsFromTimelineElements(savedElements)
        const savedGoalId = idTranslation.get(pending.goal.id)
        const rebuiltGoal = rebuiltGoals.find((candidate) => candidate.id === savedGoalId) ?? rebuiltGoals[0]
        if (rebuiltGoal) {
          goalForHandoff = rebuiltGoal
          const remappedChangedIds = pending.meta.aiChangedIds
            ?.map((id) => idTranslation.get(id) ?? id)
          metaForHandoff = {
            ...pending.meta,
            aiChangedIds: remappedChangedIds,
          }
        }
        // Post-generation board-level spread pass. `createElements` fans out
        // `syncChecklistBoardItems` per element in parallel, so two elements
        // whose checklists land on the same INDIVIDUAL layout can each read a
        // pre-write snapshot and write positions that don't see each other's
        // additions — a race that can leave visual overlaps even though each
        // call ran its own per-item collision avoidance. This one-shot spread
        // walks the merged today-layout after every write has committed and
        // relocates only the nodes that still collide, then persists once.
        // Best-effort (never throws) — the Move-to-Manage handoff must not be
        // blocked by a cosmetic cleanup pass.
        if (activeWorkspaceId && currentUser) {
          await spreadIndividualBoardCanvas(activeWorkspaceId, {
            uid: currentUser.uid,
            displayName: currentUser.displayName ?? currentUser.email ?? null,
          })
          invalidateChecklistBoardSessionSnapshot(activeWorkspaceId)
        }
      }

      onGeneratedGoal?.(goalForHandoff, metaForHandoff)
      if (pending.meta.mode === 'generate') {
        watchLineBuildOutcome(goalForHandoff.id, turnId, lineBuildSignalContext(turnId))
      }
      onOpenManage?.()
      // Resolve the durable draft only after the Manage handoff succeeds.
      pendingPreviewsRef.current.delete(turnId)
      writePendingPreviewStore(activeWorkspaceId, (current) => {
        const next = { ...current }
        delete next[turnId]
        return next
      })
      setChatTurns((current) => {
        const next = current.map((turn) => turn.id === turnId
          ? { ...turn, actions: [], proposalSummary: null, proposalPreview: null, proposalRevisionRequested: false }
          : turn)
        if (activeChatId) {
          void saveAiChatSession({
            id: activeChatId,
            turns: next.map(sanitizeTurnForStorage),
            attachments: persistedSessionAttachments.slice(0, attachmentCap),
            operatingMode: effectiveOperatingMode,
            status: 'ACTIVE',
            pendingProposal: null,
            touchHistoryOrder: false,
          }).catch(() => undefined)
        }
        return next
      })
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : 'The preview could not be opened.'
      // Firestore surfaces both a code and a message on permission failures.
      // The `hasStripboardFields` rule (firestore.rules) rejects any element
      // missing startDate/endDate/priority/etc, and the resulting cryptic
      // "Missing or insufficient permissions" toast has been misleading —
      // the actual root cause is almost always Line drafting an item without
      // required dates. Detect the shape and rewrite the toast to say so.
      const errCode = (error as { code?: string } | null)?.code
      const isPermissionDenied = errCode === 'permission-denied'
        || /missing or insufficient permissions/i.test(rawMessage)
      const displayMessage = isPermissionDenied
        ? 'The workspace rejected the write. This usually means Line drafted an item without required dates or metadata — ask Line to include start and end dates for every goal, task, and subtask, then retry.'
        : `${rawMessage} Retry after resolving the conflict.`
      setActionErrors((current) => ({ ...current, [turnId]: displayMessage }))
      showToast(displayMessage, 'error')
    }
    }, {
      label: 'Opening Manage',
      messages: ['Preparing your workspace...', 'Opening Manage...'],
    })
  }

  const rejectPendingPreview = (turnId: string) => {
    const turnSnapshot = chatTurns.find((turn) => turn.id === turnId)
    const previewSnapshot = pendingPreviewsRef.current.get(turnId)
      ?? readPendingPreviewStore(activeWorkspaceId)[turnId]
      ?? previewFromTurn(turnSnapshot ?? { role: 'ASSISTANT', text: '' })
    const hasProposalSnapshot = Boolean(previewSnapshot || turnSnapshot?.proposalSummary)
    const pendingProposalSnapshot = previewSnapshot && turnSnapshot?.proposalSummary
      ? pendingProposalForTurn(turnId, previewSnapshot, turnSnapshot.proposalSummary, linePolicy.pendingProposalTtlMs)
      : null

    pendingPreviewsRef.current.delete(turnId)
    writePendingPreviewStore(activeWorkspaceId, (current) => {
      const next = { ...current }
      delete next[turnId]
      return next
    })
    setDismissedActionTurnIds((current) => new Set(current).add(turnId))
    setActionErrors((current) => ({ ...current, [turnId]: '' }))
    setChatTurns((current) => {
      const next = current.map((turn) => turn.id === turnId
        ? {
            ...turn,
            actions: [],
            proposalSummary: null,
            proposalPreview: null,
            proposalRevisionRequested: false,
          }
        : turn)
      if (activeChatId) {
        void saveAiChatSession({
          id: activeChatId,
          turns: next.map(sanitizeTurnForStorage),
          attachments: persistedSessionAttachments.slice(0, attachmentCap),
          operatingMode: effectiveOperatingMode,
          status: 'ACTIVE',
          pendingProposal: null,
          touchHistoryOrder: false,
        })
      }
      return next
    })

    if (!hasProposalSnapshot) return

    const restoreDiscardedProposal = () => {
      if (previewSnapshot) {
        pendingPreviewsRef.current.set(turnId, previewSnapshot)
        writePendingPreviewStore(activeWorkspaceId, (current) => ({ ...current, [turnId]: previewSnapshot }))
      }
      setDismissedActionTurnIds((current) => {
        const next = new Set(current)
        next.delete(turnId)
        return next
      })
      setActionErrors((current) => ({ ...current, [turnId]: '' }))
      setChatTurns((current) => {
        const next = current.map((turn) => turn.id === turnId && turnSnapshot
          ? {
              ...turn,
              actions: turnSnapshot.actions,
              proposalSummary: turnSnapshot.proposalSummary,
              proposalPreview: turnSnapshot.proposalPreview,
              proposalRevisionRequested: turnSnapshot.proposalRevisionRequested,
            }
          : turn)
        if (activeChatId) {
          void saveAiChatSession({
            id: activeChatId,
            turns: next.map(sanitizeTurnForStorage),
            attachments: persistedSessionAttachments.slice(0, attachmentCap),
            operatingMode: effectiveOperatingMode,
            status: 'ACTIVE',
            pendingProposal: pendingProposalSnapshot,
            touchHistoryOrder: false,
          })
        }
        return next
      })
    }

    showToast(
      t('NLSAiProposalDiscardedToast'),
      'info',
      { label: t('NLSAiProposalDiscardedUndo'), run: restoreDiscardedProposal },
      'normal',
      { durationMs: linePolicy.discardUndoToastMs },
    )
  }

  const handleChangeProposal = (turnId: string) => {
    const currentPreview = pendingPreviewsRef.current.get(turnId)
      ?? readPendingPreviewStore(activeWorkspaceId)[turnId]
      ?? previewFromTurn(chatTurns.find((turn) => turn.id === turnId) ?? { role: 'ASSISTANT', text: '' })
    if (!currentPreview) {
      setActionErrors((current) => ({ ...current, [turnId]: t('NLSAiPreviewUnavailable') }))
      return
    }
    const revisionRound = chatTurns.reduce(
      (highest, turn) => Math.max(highest, turn.proposalRevisionRound ?? 0),
      0,
    ) + 1
    const currentTurn = chatTurns.find((turn) => turn.id === turnId)
    const pendingProposal = currentTurn?.proposalSummary
      ? pendingProposalForTurn(turnId, currentPreview, currentTurn.proposalSummary, linePolicy.pendingProposalTtlMs)
      : null
    pendingPreviewsRef.current.set(turnId, currentPreview)
    writePendingPreviewStore(activeWorkspaceId, (current) => ({ ...current, [turnId]: currentPreview }))
    setActionErrors((current) => ({ ...current, [turnId]: '' }))
    setChatTurns((current) => {
      const next = current.map((turn) => turn.id === turnId
        ? {
            ...turn,
            proposalPreview: currentPreview,
            proposalRevisionRequested: true,
            proposalRevisionRound: revisionRound,
          }
        : turn)
      if (activeChatId) {
        void saveAiChatSession({
          id: activeChatId,
          turns: next.map(sanitizeTurnForStorage),
          attachments: persistedSessionAttachments.slice(0, attachmentCap),
          operatingMode: effectiveOperatingMode,
          status: 'ACTIVE',
          ...(pendingProposal ? { pendingProposal } : {}),
          touchHistoryOrder: false,
        })
      }
      return next
    })
    emitLineSignal('PROPOSAL_REVISED', turnId, { round: revisionRound })
    focusComposer()
  }

  const dispatchClarificationEnvelope = (value: string, displayValue = value) => {
    const clarification = activeClarification
    if (!clarification) return
    const packet = [
      '[LINE_CLARIFICATION_RESPONSE]',
      `field: ${clarification.field}`,
      `value: ${value}`,
      'Treat this value as the user\'s explicit answer and rerun the original intent through the router.',
    ].join('\n')
    nextUserDisplayTextRef.current = displayValue
    setClarificationTurnId(null)
    setQuickControls([])
    setQuickSelections({})
    setInterviewAnswers({})
    setFocusedQuestionId(null)
    setOffTopicPending(null)
    // submitClarification holds the synchronous lock through the classifier
    // round-trip; hand it off to runGeneration's own lock here.
    submittingRef.current = false
    void runGeneration(packet)
  }

  const dispatchFreshAfterClarification = (value: string) => {
    setClarificationTurnId(null)
    setQuickControls([])
    setQuickSelections({})
    setInterviewAnswers({})
    setFocusedQuestionId(null)
    setOffTopicPending(null)
    submittingRef.current = false
    void runGeneration(value)
  }

  const submitClarification = (value: string) => {
    if (submittingRef.current) return
    const trimmed = value.trim()
    const clarification = activeClarification
    if (!clarification || !trimmed) return
    // Lock immediately so a second Enter while the classifier round-trips
    // can't fire a duplicate clarification dispatch. dispatchClarification* →
    // runGeneration's finally releases the lock.
    submittingRef.current = true
    // Take an optimistic snapshot of the prompt so the textarea can clear now
    // while we wait on the classifier verdict. If the user later cancels the
    // off-topic confirm chip we restore it.
    setPrompt('')
    setOffTopicPending({ status: 'CHECKING', value: trimmed })
    void classifyClarificationRelevance(clarification.label, trimmed)
      .then((verdict) => {
        if (verdict === 'OFF_TOPIC') {
          // User now has to choose via the off-topic chip — release the lock
          // so the buttons (and the × cancel) are interactive.
          submittingRef.current = false
          setOffTopicPending({ status: 'CONFIRM', value: trimmed })
          return
        }
        dispatchClarificationEnvelope(trimmed)
      })
      .catch(() => dispatchClarificationEnvelope(trimmed))
  }

  /**
   * Interview submit: wraps the collected question-set answers into the
   * LINE_CLARIFICATION_RESPONSE envelope (with an added `structured:` line the
   * assistant can parse deterministically) and dispatches through the existing
   * clarification path. Keeps chat history clean by using the human sentence
   * as the visible user-turn text.
   */
  const submitInterview = useCallback(() => {
    if (!activeQuestionSet) return
    if (!activeClarification) return
    if (submittingRef.current) return
    submittingRef.current = true
    const built = buildInterviewSubmission(activeQuestionSet, interviewAnswers)
    const envelope = buildInterviewEnvelope(activeClarification.field, built)
    const inventoryQuestion = activeQuestionSet.questions.find((question) => question.type === 'ATTACHMENT_INVENTORY')
    if (inventoryQuestion?.attachmentInventory) {
      emitLineSignal('ATTACHMENT_INVENTORY_CURATED', activeClarificationTurn?.id ?? 'attachment-inventory', {
        'attachment.extractedCount': inventoryQuestion.attachmentInventory.extractedCount,
        'attachment.keptCount': built.structured.attachmentApprovedItems?.length ?? 0,
      })
    }
    nextUserDisplayTextRef.current = built.humanSentence
    setClarificationTurnId(null)
    setQuickControls([])
    setQuickSelections({})
    setInterviewAnswers({})
    setFocusedQuestionId(null)
    setOffTopicPending(null)
    // Release the synchronous lock before handing off — runGeneration owns
    // its own lock, and would otherwise short-circuit on line ~2475 seeing
    // our lock still held (silent no-op = "Submit does nothing").
    submittingRef.current = false
    void runGeneration(envelope)
  }, [
    activeClarification, activeClarificationTurn, activeQuestionSet, interviewAnswers,
    setClarificationTurnId, setQuickControls, setQuickSelections,
    setInterviewAnswers, setFocusedQuestionId, setOffTopicPending, runGeneration,
  ])

  /**
   * Interview skip: the user chose to discard the current question set. Per
   * design decision, we PAUSE — the AI is told to wait rather than restart —
   * so this posts a lightweight "questions discarded" WARNING-style turn and
   * clears the interview state without kicking off a new generation.
   */
  const skipInterview = useCallback(() => {
    if (!activeQuestionSet) return
    setClarificationTurnId(null)
    setQuickControls([])
    setQuickSelections({})
    setInterviewAnswers({})
    setFocusedQuestionId(null)
    setOffTopicPending(null)
    appendWarningMessage('Questions discarded — waiting for your response.')
  }, [
    activeQuestionSet,
    setClarificationTurnId, setQuickControls, setQuickSelections,
    setInterviewAnswers, setFocusedQuestionId, setOffTopicPending, appendWarningMessage,
  ])

  const acceptSuggestion = (suggestion: ProactiveSuggestionPayload) => {
    const acceptance: SuggestionAcceptancePayload = {
      suggestionId: suggestion.id,
      kind: suggestion.kind,
      explicitResponse: suggestion.acceptancePrompt,
    }
    setSnoozedSuggestionIds((current) => new Set(current).add(suggestion.id))
    void runGeneration(`[LINE_SUGGESTION_ACCEPTED]\n${JSON.stringify(acceptance)}`)
  }

  /**
   * Persist a save-rule chip acceptance. Guards against double-save via
   * `savingRuleTurnId`. On success flips to "Saved" (`savedRuleTurnIds`);
   * on failure surfaces a toast and leaves the chip in place so the user
   * can retry.
   */
  const handleSaveRule = async (turnId: string) => {
    if (!currentUser || currentUser.isAnonymous) {
      showToast(t('NLSAiToastSignInToSaveRules'), 'warning')
      return
    }
    const turn = chatTurns.find((entry) => entry.id === turnId)
    const candidate = turn?.ruleCandidate
    if (!candidate) return
    if (savingRuleTurnId) return
    setSavingRuleTurnId(turnId)
    try {
      const rule = await appendUserRule(currentUser.uid, {
        text: candidate.text,
        scope: candidate.suggestedScope,
        source: 'DETECTED',
      })
      setSavedRuleTurnIds((current) => new Set(current).add(turnId))
      showToast(rule ? t('NLSAiToastRuleSaved') : t('NLSAiToastAlreadySaved'), 'success')
    } catch (error) {
      console.error('[ai-rules] save failed', error)
      showToast(t('NLSAiToastCouldNotSaveRule'), 'error')
    } finally {
      setSavingRuleTurnId(null)
    }
  }

  const handleDismissRule = (turnId: string) => {
    setDismissedRuleTurnIds((current) => new Set(current).add(turnId))
  }

  const hasSpeechRecognition = typeof window !== 'undefined' && Boolean(window.SpeechRecognition ?? window.webkitSpeechRecognition)

  const canSubmitMobile = !isAiBlocked && (isGenerating
    ? true
    : Boolean(
        prompt.trim() ||
        dataContexts.length > 0 ||
        imageFiles.length > 0
      ))

  const handleMobileSubmit = () => {
    if (interviewFreeTextMode) {
      submitClarification(prompt)
      return
    }
    if (interviewModeActive) return
    if (effectiveOperatingMode === 'CREATE' && activeClarification) {
      if (prompt.trim()) submitClarification(prompt)
      return
    }
    void runGeneration()
  }

  if (isMobileLayout) {
    return (
      <AnimatePresence>
        {isOpen && (
          <div className="memberAiChatContainer" data-ui-name="UI-MPAI-CHAT">
            {chatTurns.length > 0 && (
              <Fab
                icon={<AutoAwesomeRoundedIcon fontSize="small" />}
                label={t('NLSMemberAiHistoryFabAriaLabel')}
                className="memberAiChatNewChatFab"
                onClick={() => void startNewChat()}
              />
            )}
            <CitationDispatchProvider
              surface="desktop"
              onFocusTimelineNode={(nodeId) => {
                const target = elements.find((element) => element.id === nodeId)
                if (target) console.info('[Line][cite] focus node', target.title)
              }}
              onScrollToChatTurn={(turnId) => {
                const node = chatThreadRef.current?.querySelector<HTMLElement>(`[data-turn-id="${turnId}"]`)
                node?.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }}
            >
              <div ref={chatThreadRef} className="memberAiChatThread" aria-label={t('NLSAiAriaLineTranscript')}>
                {chatTurns.length > 0 ? (
                  chatTurns.map((turn, index) => {
                    const turnId = turn.id ?? `${turn.role}-${index}`
                    if (turn.tourDefinition) {
                      return (
                        <JourneyTriggerCard
                          key={turnId}
                          turnId={turnId}
                          workspaceId={activeWorkspaceId}
                          tour={turn.tourDefinition}
                          preambleKind={turn.preambleKind ?? 'CONFIDENT'}
                          executionPreview={turn.executionPreview}
                          executionTheater={turn.executionTheater}
                          executionGraph={turn.executionGraph}
                          executionSimulation={turn.executionSimulation}
                          purchased={Boolean(turn.purchased)}
                          onPurchased={() => turn.id && markTurnPurchased(turn.id)}
                          onError={(message) => showToast(message, 'warning')}
                        />
                      )
                    }

                    const isUser = turn.role === 'USER'
                    const isSameSpeakerAsPrevious = index > 0 && chatTurns[index - 1].role === turn.role
                    const isSameSpeakerAsNext = index < chatTurns.length - 1 && chatTurns[index + 1].role === turn.role
                    const isTail = isUser && !isSameSpeakerAsNext

                    if (isUser) {
                      const userText = stripClarificationEnvelope(turn.text)
                      return (
                        <div
                          key={turnId}
                          data-turn-id={turnId}
                          className={`memberAiTurnWrap memberAiTurnWrap--user${isSameSpeakerAsPrevious ? ' memberAiTurnWrap--consecutive' : ''}`}
                        >
                          <div className={`memberAiUserBubble${isTail ? ' memberAiUserBubble--tail' : ''}`}>
                            {userText}
                          </div>
                        </div>
                      )
                    }

                    const storedPreview = pendingPreviewsRef.current.get(turnId) ?? previewFromTurn(turn)
                    const summary = turn.role === 'ASSISTANT'
                      ? turn.proposalSummary ?? (storedPreview
                        ? buildProposalSummaryForGoal(storedPreview.goal, storedPreview.meta.aiNote ?? null, t, turn.text)
                        : null)
                      : null
                    const collapsedEarlierProposal = turn.proposalRevisionSuperseded
                    const showProposal = Boolean(summary || collapsedEarlierProposal) && !dismissedActionTurnIds.has(turnId)
                    const revisionState = turn.proposalRevisionRequested
                      ? isGenerating ? 'awaiting-line' as const : 'awaiting-user' as const
                      : 'idle' as const
                    const prevUserTurn = [...chatTurns.slice(0, index)].reverse().find((t) => t.role === 'USER')

                    return (
                      <div
                        key={turnId}
                        data-turn-id={turnId}
                        className={`memberAiTurnWrap memberAiTurnWrap--assistant${isSameSpeakerAsPrevious ? ' memberAiTurnWrap--consecutive' : ''}`}
                      >
                        <article className="memberAiLineCard">
                          <div className="memberAiLineCard__text">
                            {renderLineCardText(turn.text)}
                          </div>

                          {showProposal && (
                            <ProposalSummaryCard
                              turnId={turnId}
                              summary={summary}
                              actionError={actionErrors[turnId]}
                              onRetry={() => {
                                emitLineSignal('PROPOSAL_BUILT', turnId, lineBuildSignalContext(turnId))
                                void openPendingPreview(turnId)
                              }}
                              collapsedEarlierProposal={collapsedEarlierProposal}
                              revisionState={revisionState}
                              onBuild={() => {
                                emitLineSignal('PROPOSAL_BUILT', turnId, lineBuildSignalContext(turnId))
                                void openPendingPreview(turnId)
                              }}
                              onChange={() => handleChangeProposal(turnId)}
                              onDiscard={() => {
                                emitLineSignal('PROPOSAL_DISCARDED', turnId, { layout: 'mobile' })
                                rejectPendingPreview(turnId)
                              }}
                            />
                          )}

                          <div className="memberAiLineActions">
                            <Button
                              variant="tertiary"
                              size="sm"
                              icon={<ContentCopyRoundedIcon fontSize="inherit" />}
                              onClick={() => {
                                if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                                  void navigator.clipboard.writeText(turn.text)
                                }
                                showToast('Copied to clipboard', 'info')
                              }}
                              aria-label="Copy reply"
                            >
                              Copy
                            </Button>

                            {prevUserTurn && (
                              <Button
                                variant="tertiary"
                                size="sm"
                                icon={<RefreshRoundedIcon fontSize="inherit" />}
                                onClick={() => {
                                  const promptToRun = stripClarificationEnvelope(prevUserTurn.text)
                                  if (promptToRun.trim()) void runGeneration(promptToRun)
                                }}
                                aria-label="Regenerate reply"
                              >
                                Regen
                              </Button>
                            )}
                          </div>
                        </article>
                      </div>
                    )
                  })
                ) : (
                  <div className="memberAiEmptyState">
                    <div className="memberAiEmptyGlyph" aria-hidden="true">
                      {effectiveOperatingMode === 'CREATE' ? (
                        <AutoAwesomeRoundedIcon fontSize="inherit" />
                      ) : (
                        <MapRoundedIcon fontSize="inherit" />
                      )}
                    </div>
                    <h3 className="memberAiEmptyGreeting">
                      {effectiveOperatingMode === 'CREATE'
                        ? 'Ready when you are — what should we make?'
                        : "Point Line at something and let's move it forward."}
                    </h3>
                    <div className="memberAiEmptyChips" role="group" aria-label="Suggested prompts">
                      {(effectiveOperatingMode === 'CREATE' ? CREATE_CHIPS : EXECUTE_CHIPS).map((chip) => (
                        <Pressable
                          key={chip}
                          className="memberAiEmptyChip"
                          onClick={() => {
                            updateTextPrompt(chip)
                            mobileTextareaRef.current?.focus()
                          }}
                        >
                          {chip}
                        </Pressable>
                      ))}
                    </div>
                  </div>
                )}

                {isLineThinking && (
                  <AgentThinkingIndicator
                    label={progressMessage || undefined}
                    labels={progressSequence.length > 0 ? progressSequence : undefined}
                    onAbort={isAiBusy ? abortAiJob : undefined}
                  />
                )}
              </div>

              {(() => {
                const mobileDock = (
                  <div className="memberAiMobileDock">
                    <KeyboardDock
                      className="memberAiMobileComposer__dock"
                      label={t('NLSAiComposerDockLabel', { mode: effectiveOperatingMode === 'CREATE' ? t('NLSAiModeCreate') : t('NLSAiModeExecute') })}
                    >
                      {({ isDocked, focusedRegion }) => {
                        // While a dock is active (keyboard open), show only the
                        // region the user is actually interacting with so the
                        // interview carousel and composer never both occupy
                        // layout at once. When the keyboard is closed, render
                        // both exactly as before.
                        if (focusedRegion === 'composer' || focusedRegion === 'interview') {
                          lastKeyboardDockRegionRef.current = focusedRegion
                        }
                        // Docked but focus is in neither tagged region (e.g. the
                        // "+" composer menu trigger, which blurs the textarea
                        // without leaving the dock's DOM subtree) — deterministically
                        // fall back to whichever region was last actually focused,
                        // rather than rendering both.
                        const effectiveRegion = focusedRegion ?? lastKeyboardDockRegionRef.current
                        const showInterview = Boolean(activeQuestionSet) && (!isDocked || effectiveRegion !== 'composer')
                        const showComposer = !isDocked || effectiveRegion !== 'interview'
                        return (
                          <>
                      {draftAttachmentPreviews.length > 0 && (
                        <div className="aiGeneratorDraftAttachments aiGeneratorDraftAttachments--mobile" style={{ padding: '0 0 6px', margin: 0 }}>
                          {draftAttachmentPreviews.map((item) => (
                            <div key={item.id} className={`aiGeneratorDraftAttachmentCard aiGeneratorDraftAttachmentCard--${item.kind}`}>
                              <Pressable
                                className="aiGeneratorDraftAttachmentRemove"
                                aria-label={t('NLSAiAriaRemoveItem', { label: item.label })}
                                title={t('NLSAiAriaRemoveItem', { label: item.label })}
                                onClick={() => removeDataContext(item.id)}
                              >
                                <CloseRoundedIcon fontSize="inherit" />
                              </Pressable>
                              <Pressable
                                className="aiGeneratorDraftAttachmentBody"
                                onClick={() => {
                                  if (draftChatContextById.has(item.id)) {
                                    setExpandedChatContextId(item.id)
                                    return
                                  }
                                  setPreviewItem(item)
                                }}
                                aria-label={t('NLSCommonAriaPreviewAttachment', { label: item.sizeLabel ? `${item.label} (${item.sizeLabel})` : item.label })}
                                title={item.sizeLabel ? `${item.label} (${item.sizeLabel})` : item.label}
                              >
                                {draftChatContextById.has(item.id) ? (
                                  <span className="aiGeneratorDraftAttachmentGlyph" aria-hidden="true">
                                    <ChatBubbleOutlineRoundedIcon fontSize="inherit" />
                                  </span>
                                ) : item.kind === 'image' && item.url ? (
                                  <img src={item.url} alt="" className="aiGeneratorDraftAttachmentThumb" />
                                ) : (
                                  <span className="aiGeneratorDraftAttachmentGlyph" aria-hidden="true">
                                    {item.mimeType === 'application/pdf'
                                      ? 'PDF'
                                      : item.kind === 'audio'
                                        ? 'A'
                                        : item.kind === 'video'
                                          ? 'V'
                                          : item.kind === 'spreadsheet' || item.kind === 'gsheet'
                                            ? 'S'
                                            : item.kind === 'document'
                                              ? 'D'
                                              : item.kind === 'link'
                                                ? 'L'
                                                : 'F'}
                                  </span>
                                )}
                              </Pressable>
                            </div>
                          ))}
                        </div>
                      )}

                      {showInterview && activeQuestionSet && (
                        <div className="memberAiInterviewDock" data-keyboard-dock-region="interview">
                          <InterviewQuestionSet
                            set={activeQuestionSet}
                            answers={interviewAnswers}
                            onAnswersChange={setInterviewAnswers}
                            focusedQuestionId={focusedQuestionId}
                            onFocusQuestion={setFocusedQuestionId}
                            onSubmit={submitInterview}
                            onSkip={skipInterview}
                            disabled={isGenerating}
                            blocked={interviewFreeTextMode}
                          />
                        </div>
                      )}

                      {/* Sticky Mobile Composer */}
                      {showComposer && (
                      <div className="memberAiMobileComposer" data-keyboard-dock-region="composer">
                        {aiNoticeMessage ? (
                          <ControlNotice
                            tone="warning"
                            message={aiNoticeMessage}
                            testId="ai-control-notice"
                            className="aiGeneratorControlNotice aiGeneratorControlNotice--mobile"
                          />
                        ) : null}
                        {(isListening || interimSpeech) && (
                          <p className="aiGeneratorLiveTranscript" aria-live="polite" style={{ margin: '0 4px 6px' }}>
                            {isListening && !interimSpeech ? t('NLSAiListening') : `… ${interimSpeech}`}
                          </p>
                        )}
                        <div className="memberAiMobileComposer__inner">
                          <AiComposerMenu
                            goals={goals}
                            selectedTargets={liveEditTargets}
                            onToggleTarget={toggleEditTarget}
                            attachmentCount={sessionAttachments.length}
                            attachmentLimit={attachmentLimit}
                            onFiles={(files) => void addImageFiles(files)}
                            onAttach={attachDataContext}
                            onOpenContextPicker={() => setIsContextPickerOpen(true)}
                            canViewWorkspaceAssets={canPerform('forms.view')}
                            showPlanModeToggle={effectiveOperatingMode === 'CREATE'}
                            isPlanningMode={isPlanningMode}
                            onPlanModeChange={setIsPlanningMode}
                            disabled={isGenerating || isAiBlocked}
                            triggerClassName="memberAiMobileComposer__btn"
                            isMobile
                          />

                          <Textarea
                            ref={mobileTextareaRef}
                            value={prompt}
                            onChange={(next) => {
                              updateTextPrompt(next)
                              if (mobileTextareaRef.current) {
                                mobileTextareaRef.current.style.height = 'auto'
                                mobileTextareaRef.current.style.height = `${Math.min(mobileTextareaRef.current.scrollHeight, 120)}px`
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                e.preventDefault()
                                if (canSubmitMobile) void handleMobileSubmit()
                              }
                            }}
                            placeholder={composerPlaceholder}
                            rows={1}
                            className="memberAiMobileComposer__textarea"
                            disabled={isGenerating || isAiBlocked}
                          />

                          {hasSpeechRecognition && (
                            <Pressable
                              className={`memberAiMobileComposer__btn memberAiMobileComposer__btn--mic${isListening ? ' isListening' : ''}`}
                              onClick={toggleSpeechCapture}
                              disabled={isGenerating}
                              aria-pressed={isListening}
                              aria-label={isListening ? t('NLSAiActionStopDictation') : t('NLSAiActionStartDictation')}
                              title={isListening ? t('NLSAiTitleListeningTapToStop') : t('NLSAiTitleTapToDictate')}
                            >
                              <MicNoneRoundedIcon fontSize="small" />
                            </Pressable>
                          )}

                          <Pressable
                            className="memberAiMobileComposer__btn memberAiMobileComposer__btn--send"
                            onClick={() => {
                              if (isGenerating) {
                                abortAiJob()
                              } else {
                                void handleMobileSubmit()
                              }
                            }}
                            disabled={isGenerating ? false : !canSubmitMobile}
                            aria-label={isGenerating ? t('NLSAiActionStop') : t('NLSAiActionSend')}
                          >
                            {isGenerating ? (
                              <StopRoundedIcon fontSize="small" />
                            ) : (
                              <ArrowUpwardRoundedIcon fontSize="small" />
                            )}
                          </Pressable>
                        </div>
                      </div>
                      )}
                          </>
                        )
                      }}
                    </KeyboardDock>
                  </div>
                )

                return memberLayout?.bottomAddonTarget
                  ? createPortal(mobileDock, memberLayout.bottomAddonTarget)
                  : mobileDock
              })()}
            </CitationDispatchProvider>

            <WorkspaceItemPicker
              open={isContextPickerOpen}
              onClose={() => setIsContextPickerOpen(false)}
              onCommit={(contexts) => {
                contexts.forEach((ctx) => attachDataContext(ctx))
              }}
              workspaceId={activeWorkspaceId}
              userId={currentUser?.uid ?? ''}
              initialContexts={activeDataContexts}
              onFiles={(files) => void addImageFiles(files)}
              mode="aiContext"
            />
            <AttachmentPreviewDialog open={Boolean(previewItem)} item={previewItem} onClose={() => setPreviewItem(null)} />
          </div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/*
           * AI composer (single unified prompt). No mode tabs: the
           * write area is always visible, image references attach as chips
           * above it, and the toolbar at the bottom carries model-tier +
           * `+` (attach images) + tap-to-toggle mic + green Send-style
           * Generate. The mic flips to green while listening and back to
           * neutral when the user taps it off or the silence timer fires.
           */}
          <motion.div
            className="aiManagerPageHost"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <RailPageShell
              variant="two-column"
              ariaLabel={t('NLSAiIdentityTitle')}
              identity={{ badge: 'AI', title: t('NLSAiIdentityTitle'), subtitle: t('NLSAiIdentitySubtitle') }}
              useCollapsedInitials
              leftRail={(
                <AiChatHistoryPanel
                  sessions={chatSessions.filter((session) => session.operatingMode === effectiveOperatingMode)}
                  activeId={activeChatId}
                  onOpen={loadSession}
                  onNew={() => void startNewChat()}
                  onRename={openRenameDialog}
                  onDelete={openDeleteDialog}
                />
              )}
              rightStage={(
                <div className="aiManagerConversation">
                <IconButton
                  variant="tertiary"
                  size="md"
                  className="aiManagerMobileMenuFloater"
                  onClick={() => setIsMobileMenuOpen(true)}
                  aria-label={t('NLSAiAriaOpenChatHistory')}
                  label={t('NLSAiAriaOpenChatHistory')}
                  icon={<MenuRoundedIcon fontSize="inherit" />}
                />
                <CitationDispatchProvider
              surface="desktop"
              onFocusTimelineNode={(nodeId) => {
                const target = elements.find((element) => element.id === nodeId)
                if (target) console.info('[Line][cite] focus node', target.title)
              }}
              onScrollToChatTurn={(turnId) => {
                const node = chatThreadRef.current?.querySelector<HTMLElement>(`[data-turn-id="${turnId}"]`)
                node?.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }}
            >
            <div className="aiGeneratorBody">
            {isAccessRestricted ? (
              <div className="aiPulseRestrictedNotice">{t('NLSAiPulseRestricted')}</div>
            ) : (
              <WorkspacePulse workspaceId={activeWorkspaceId} premiumPanel={pulsePremiumPanel} />
            )}
            <section className="aiComposer aiGeneratorChatCard">
              <div ref={chatThreadRef} className="aiMagicChatThread" aria-label={t('NLSAiAriaLineTranscript')}>
                {chatTurns.length > 0 ? (
                  (() => {
                    let lastAssistantTurnIndex = -1
                    for (let i = chatTurns.length - 1; i >= 0; i--) {
                      if (chatTurns[i].role !== 'USER') { lastAssistantTurnIndex = i; break }
                    }
                    return chatTurns.map((turn, index) => {
                    const turnId = turn.id ?? `${turn.role}-${index}`
                    if (turn.tourDefinition) {
                      return (
                        <JourneyTriggerCard
                          key={turnId}
                          turnId={turnId}
                          workspaceId={activeWorkspaceId}
                          tour={turn.tourDefinition}
                          preambleKind={turn.preambleKind ?? 'CONFIDENT'}
                          executionPreview={turn.executionPreview}
                          executionTheater={turn.executionTheater}
                          executionGraph={turn.executionGraph}
                          executionSimulation={turn.executionSimulation}
                          purchased={Boolean(turn.purchased)}
                          onPurchased={() => turn.id && markTurnPurchased(turn.id)}
                          onError={(message) => showToast(message, 'warning')}
                        />
                      )
                    }
                    const visibleTurn = turn.role === 'USER'
                      ? { ...turn, text: stripClarificationEnvelope(turn.text) }
                      : turn
                    const renderedTurn = dismissedActionTurnIds.has(turnId) ? { ...visibleTurn, actions: [] } : visibleTurn
                    const showRuleChip =
                      turn.role === 'USER'
                      && Boolean(turn.ruleCandidate)
                      && !dismissedRuleTurnIds.has(turnId)
                    const ruleAlreadySaved = savedRuleTurnIds.has(turnId)
                    const storedPreview = pendingPreviewsRef.current.get(turnId) ?? previewFromTurn(turn)
                    const summary = turn.role === 'ASSISTANT'
                      ? turn.proposalSummary ?? (storedPreview
                        ? buildProposalSummaryForGoal(storedPreview.goal, storedPreview.meta.aiNote ?? null, t, turn.text)
                        : null)
                      : null
                    const collapsedEarlierProposal = turn.proposalRevisionSuperseded
                    const showSummary = Boolean(summary || collapsedEarlierProposal) && !dismissedActionTurnIds.has(turnId)
                    const revisionState = turn.proposalRevisionRequested
                      ? isGenerating ? 'awaiting-line' as const : 'awaiting-user' as const
                      : 'idle' as const
                    return (
                      <div key={turnId} className="aiChatTurnGroup">
                        <LineChatBubble
                          turn={renderedTurn}
                          isLatestAssistantTurn={index === lastAssistantTurnIndex}
                          highlightClarification={clarificationTurnId === turn.id}
                          hiddenSuggestionIds={snoozedSuggestionIds}
                          actionError={actionErrors[turnId]}
                          onApply={() => openPendingPreview(turnId)}
                          onReject={() => rejectPendingPreview(turnId)}
                          onRetry={() => openPendingPreview(turnId)}
                          onAcceptSuggestion={acceptSuggestion}
                          onSnoozeSuggestion={(suggestionId) => setSnoozedSuggestionIds((current) => new Set(current).add(suggestionId))}
                          onShowMeHow={handleShowMeHow}
                        />
                        {showSummary && (
                          <ProposalSummaryCard
                            turnId={turnId}
                            summary={summary}
                            actionError={actionErrors[turnId]}
                            onRetry={() => {
                              emitLineSignal('PROPOSAL_BUILT', turnId, lineBuildSignalContext(turnId))
                              void openPendingPreview(turnId)
                            }}
                            collapsedEarlierProposal={collapsedEarlierProposal}
                            revisionState={revisionState}
                            onBuild={() => {
                              emitLineSignal('PROPOSAL_BUILT', turnId, lineBuildSignalContext(turnId))
                              void openPendingPreview(turnId)
                            }}
                            onChange={() => handleChangeProposal(turnId)}
                            onDiscard={() => {
                              emitLineSignal('PROPOSAL_DISCARDED', turnId, { layout: 'desktop' })
                              rejectPendingPreview(turnId)
                            }}
                          />
                        )}
                        {showRuleChip && turn.ruleCandidate && (
                          <div className="aiRuleChip" role="status" aria-live="polite">
                            <span className="aiRuleChipLabel">
                              {ruleAlreadySaved
                                ? <>{t('NLSAiRuleSavedPrefix')} <em>“{turn.ruleCandidate.text}”</em></>
                                : <>{t('NLSAiRuleSavePrompt')} <em>“{turn.ruleCandidate.text}”</em> <small>({turn.ruleCandidate.suggestedScope.toLowerCase()})</small></>}
                            </span>
                            {!ruleAlreadySaved && (
                              <>
                                <Pressable
                                  className="aiRuleChipPrimary"
                                  disabled={savingRuleTurnId === turnId}
                                  onClick={() => void handleSaveRule(turnId)}
                                >
                                  {savingRuleTurnId === turnId ? t('NLSAiActionSaving') : t('NLSAiActionSaveRule')}
                                </Pressable>
                                <Pressable
                                  className="aiRuleChipDismiss"
                                  aria-label={t('NLSAiActionDismiss')}
                                  onClick={() => handleDismissRule(turnId)}
                                ><CloseRoundedIcon fontSize="inherit" /></Pressable>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })
                  })()
                ) : (
                  <LineChatBubble turn={{ role: 'ASSISTANT', text: effectiveOperatingMode === 'EXECUTE'
                    ? 'Describe a task you want walked through (e.g. "show me how to transfer credits") and I will spin up a guided tour.'
                    : 'Tell me what you want to plan or change. New timelines begin with a short interview unless you explicitly ask me to generate directly.' }} />
                )}
                {isLineThinking && <AgentThinkingIndicator label={progressMessage || undefined} labels={progressSequence.length > 0 ? progressSequence : undefined} onAbort={isAiBusy ? abortAiJob : undefined} />}
              </div>
            </section>
            <MergedComposer
              tabs={[
                { key: 'CREATE'  as const, label: t('NLSAiModeCreate'),  icon: 'sparkle' },
                { key: 'EXECUTE' as const, label: t('NLSAiModeExecute'), icon: 'journey' },
              ]}
              activeKey={effectiveOperatingMode}
              onTabChange={handleOperatingModeChange}
              ariaLabel={t('NLSAiModeAria')}
              hideTabBar={hideModeTabs}
              lockActiveTab={isContinuingLoadedChat}
              composerClassName={`aiComposer aiGeneratorComposer aiMagicTabbedCard${hideModeTabs ? ' aiMagicTabbedCard--singleMode' : ''}${isContinuingLoadedChat ? ' aiMagicTabbedCard--continuing aiMagicTabbedCard--lockedMode' : ''}${interviewModeActive ? ' isInterviewMode' : ''}`}
            >
              {offTopicPending?.status === 'CHECKING' && (
                <div
                  className="aiMagicCheckingPill"
                  role="status"
                  aria-live="polite"
                  aria-label={t('NLSAiCheckingReplyAria')}
                >
                  <span className="aiMagicCheckingPillSpinner" aria-hidden="true" />
                </div>
              )}
              {lineFeatureFlags.aiTierSwitcher && !isTierSwitcherLegacyPlan && (
                <AiTierCoachmark />
              )}
              <div className={`aiMagicComposerRegion${activeClarification ? ' isClarificationLocked' : ''}`}>

              {offTopicPending?.status === 'CONFIRM' && (
                <div className="aiMagicOffTopicChip" role="status">
                  <span className="aiMagicOffTopicChipText">
                    {t('NLSAiOffTopicMessage')}
                  </span>
                  <Pressable
                    className="aiMagicOffTopicChipPrimary"
                    onClick={() => dispatchFreshAfterClarification(offTopicPending.value)}
                  >{t('NLSAiActionDropSendFresh')}</Pressable>
                  <Pressable
                    className="aiMagicOffTopicChipSecondary"
                    onClick={() => dispatchClarificationEnvelope(offTopicPending.value)}
                  >{t('NLSAiActionTreatAsAnswer')}</Pressable>
                  <Pressable
                    className="aiMagicOffTopicChipDismiss"
                    aria-label={t('NLSCommonActionCancel')}
                    onClick={() => {
                      setPrompt(offTopicPending.value)
                      setOffTopicPending(null)
                    }}
                  ><CloseRoundedIcon fontSize="inherit" /></Pressable>
                </div>
              )}
              <LineComposerAmbientChip
                suppressed={Boolean(offTopicPending) || Boolean(prompt.trim()) || Boolean(activeClarification) || !aiSuggestionsEnabled}
                onAccept={(nextPrompt) => setPrompt(nextPrompt)}
                onModify={(nextPrompt) => setPrompt(nextPrompt)}
              />

              {effectiveOperatingMode === 'CREATE' && liveEditTargets.length > 0 && (
                <div className="aiGeneratorAttachments" aria-label={t('NLSAiAriaEditingTargets')}>
                  {liveEditTargets.map((target) => {
                    const icon: ReactNode = target.kind === 'goal' ? <GpsFixedRoundedIcon fontSize="inherit" />
                      : target.kind === 'task' ? <AssignmentRoundedIcon fontSize="inherit" />
                      : target.kind === 'subtask' ? '•'
                      : target.kind === 'checklist_item' ? <CheckBoxOutlinedIcon fontSize="inherit" />
                      : target.kind === 'upload' ? <AttachFileRoundedIcon fontSize="inherit" />
                      : target.kind === 'meeting' ? <EventRoundedIcon fontSize="inherit" />
                      : target.kind === 'report' ? <BarChartRoundedIcon fontSize="inherit" />
                      : target.kind === 'form' ? <EditNoteRoundedIcon fontSize="inherit" />
                      : '•'
                    return (
                      <TagChip
                        key={targetKey(target)}
                        className="aiGeneratorAttachmentChip"
                        removeLabel={t('NLSAiAriaRemoveItem', { label: target.label })}
                        onRemove={() => removeEditTarget(targetKey(target))}
                      >
                        <span aria-hidden="true">{icon}</span>
                        <span className="aiGeneratorAttachmentChipName">{target.label}</span>
                      </TagChip>
                    )
                  })}
                </div>
              )}

              {aiNoticeMessage ? (
                <ControlNotice
                  tone="warning"
                  message={aiNoticeMessage}
                  testId="ai-control-notice"
                  className="aiGeneratorControlNotice"
                />
              ) : null}
              <div className="aiGeneratorTextareaWrap">
                <Textarea
                  data-tour-id="ai-composer-prompt-input"
                  value={prompt}
                  onChange={(next) => updateTextPrompt(next)}
                  onKeyDown={(event) => {
                    // Tab-to-fill: only when the composer is empty AND we're
                    // continuing a loaded chat AND no clarification is open.
                    // Everywhere else, Tab keeps native focus-move behavior.
                    if (
                      event.key === 'Tab'
                      && !event.shiftKey
                      && showContinuingTabHint
                      && prompt.length === 0
                    ) {
                      event.preventDefault()
                      updateTextPrompt(composerTabFill)
                      return
                    }
                    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
                    event.preventDefault()
                    // EXECUTE affirmation interceptor: when the latest assistant
                    // turn is an unpurchased tour card, "yes / do it / start"
                    // hijacks Enter and fires Begin Journey on that card.
                    if (effectiveOperatingMode === 'EXECUTE' && AFFIRMATION_REGEX.test(prompt.trim())) {
                      const latestCardTurn = [...chatTurns].reverse().find((turn) => turn.tourDefinition)
                      if (latestCardTurn && !latestCardTurn.purchased) {
                        setPrompt('')
                        const button = chatThreadRef.current?.querySelector<HTMLButtonElement>(`[data-journey-card-id="${latestCardTurn.id}"] .journeyTriggerCardButton`)
                        button?.click()
                        return
                      }
                    }
                    if (effectiveOperatingMode === 'EXECUTE') {
                      if (prompt.trim()) void runGeneration()
                      return
                    }
                    // Interview mode + textarea has content: the textarea acts
                    // as an ESCAPE HATCH. Enter dispatches ONLY the free text
                    // as a clarification reply — Line receives it as the sole
                    // answer, the interview state is cleared, and Line decides
                    // whether the text embeds question answers (use them) or
                    // is a fresh reply (start again). Empty textarea → Enter
                    // is a no-op (no free text to send; user should use the
                    // carousel's ↵ instead).
                    if (interviewFreeTextMode) {
                      submitClarification(prompt)
                      return
                    }
                    if (interviewModeActive) {
                      // Empty textarea while carousel is active — swallow so
                      // Enter doesn't fall through to a generic dispatch.
                      return
                    }
                    // CREATE: while a clarification is pending, the user's reply
                    // gets wrapped as a [LINE_CLARIFICATION_RESPONSE] envelope so
                    // the router treats it as an answer rather than a fresh prompt.
                    // submitClarification handles the wrap + dispatch.
                    if (activeClarification) {
                      if (prompt.trim()) submitClarification(prompt)
                      return
                    }
                    if (canGenerate) void runGeneration()
                  }}
                  disabled={isGenerating || isAiBlocked}
                  maxLength={maxCharacters}
                  showCount={false}
                  placeholder={composerPlaceholder}
                  className="aiGeneratorTextarea"
                />
                {showContinuingTabHint && (
                  <span className="aiGeneratorTabHint" aria-hidden="true">
                    <kbd>⇥ Tab</kbd>
                  </span>
                )}
              </div>

              {/* Live transcript preview — shown only while dictating so the
                  user sees what the recognizer hears before it commits. */}
              {(isListening || interimSpeech) && (
                <p className="aiGeneratorLiveTranscript" aria-live="polite">
                  {isListening && !interimSpeech
                    ? t('NLSAiListening')
                    : `… ${interimSpeech}`}
                </p>
              )}

              {effectiveOperatingMode === 'CREATE' && draftAttachmentPreviews.length > 0 && (
                <div className="aiGeneratorDraftAttachments" aria-label={t('NLSAiAriaAttachedFiles')}>
                  {draftAttachmentPreviews.map((item) => {
                    const ctx = dataContexts.find((c) => c.id === item.id)
                    const costDelta = ctx
                      ? estimateContextCost([ctx], 'GENERATION').upperBound - BASE_ACTION_COSTS.GENERATION
                      : null
                    const titleText = costDelta !== null
                      ? t('NLSAiContextPickerCostTooltip', { cr: costDelta })
                      : undefined

                    return (
                      <div
                        key={item.id}
                        className={`aiGeneratorDraftAttachmentCard aiGeneratorDraftAttachmentCard--${item.kind}`}
                        title={titleText}
                      >
                        <Pressable
                          className="aiGeneratorDraftAttachmentRemove"
                          aria-label={t('NLSAiAriaRemoveItem', { label: item.label })}
                          onClick={() => removeDataContext(item.id)}
                        >
                          <CloseRoundedIcon fontSize="inherit" />
                        </Pressable>
                        <Pressable
                          className="aiGeneratorDraftAttachmentBody"
                          onClick={() => {
                            if (draftChatContextById.has(item.id)) {
                              setExpandedChatContextId(item.id)
                              return
                            }
                            setPreviewItem(item)
                          }}
                        >
                          {draftChatContextById.has(item.id) ? (
                            <span className="aiGeneratorDraftAttachmentGlyph" aria-hidden="true">
                              <ChatBubbleOutlineRoundedIcon fontSize="inherit" />
                            </span>
                          ) : item.kind === 'image' && item.url ? (
                            <img src={item.url} alt="" className="aiGeneratorDraftAttachmentThumb" />
                          ) : (
                            <span className="aiGeneratorDraftAttachmentGlyph" aria-hidden="true">
                              {item.kind === 'audio'
                                ? 'A'
                                : item.kind === 'video'
                                  ? 'V'
                                  : item.kind === 'spreadsheet'
                                    ? 'S'
                                    : item.kind === 'document'
                                      ? 'D'
                                      : item.kind === 'link'
                                        ? 'L'
                                        : 'F'}
                            </span>
                          )}
                          <span className="aiGeneratorDraftAttachmentMeta">
                            <strong>
                              {item.label}
                              {(item.kind === 'spreadsheet' || item.kind === 'gsheet') && (
                                <StatusChip size="sm" tone="info" className="aiGeneratorDraftAttachmentFeeBadge">+1 Cr</StatusChip>
                              )}
                            </strong>
                            {(item.sizeLabel || item.summary) && <small>{item.sizeLabel ?? item.summary}</small>}
                          </span>
                        </Pressable>
                      </div>
                    )
                  })}
                </div>
              )}

              {activeQuestionSet && (
                <InterviewQuestionSet
                  set={activeQuestionSet}
                  answers={interviewAnswers}
                  onAnswersChange={setInterviewAnswers}
                  focusedQuestionId={focusedQuestionId}
                  onFocusQuestion={setFocusedQuestionId}
                  onSubmit={submitInterview}
                  onSkip={skipInterview}
                  disabled={isGenerating}
                  blocked={interviewFreeTextMode}
                />
              )}

              {!activeQuestionSet && quickControls.length > 0 && (
                <div className="aiMagicQuickControls" aria-label={t('NLSAiAriaSuggestedReplies')}>
                  {quickControls.map((control, index) => {
                    const group = control.type === 'button' ? 'quick-action' : control.group
                    const selected = (quickSelections[group] ?? []).includes(control.value)
                    return (
                      <label key={`${group}-${control.value}-${index}`} className="aiMagicQuickChoice">
                        {/* eslint-disable-next-line no-restricted-syntax -- quick-choice pill input uses visually hidden input inside pill label without Common equivalent */}
                        <input
                          type={control.type === 'checkbox' ? 'checkbox' : 'radio'}
                          name={`quick-${group}`}
                          checked={selected}
                          onChange={(event) => {
                            setQuickSelections((current) => {
                              if (control.type !== 'checkbox') return { ...current, [group]: [control.value] }
                              const currentValues = current[group] ?? []
                              return {
                                ...current,
                                [group]: event.target.checked
                                  ? [...currentValues, control.value]
                                  : currentValues.filter((value) => value !== control.value),
                              }
                            })
                          }}
                        />
                        <span>{control.label}</span>
                      </label>
                    )
                  })}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={submitQuickSelections}
                    disabled={!quickControls.some((control) => {
                      const group = control.type === 'button' ? 'quick-action' : control.group
                      return (quickSelections[group] ?? []).includes(control.value)
                    })}
                  >
                    {t('NLSAiActionSubmit')}
                  </Button>
                </div>
              )}

              <div className="aiComposerToolbar">
                <div className="aiComposerToolbarLeft">
                  <AiComposerMenu
                    goals={goals}
                    selectedTargets={liveEditTargets}
                    onToggleTarget={toggleEditTarget}
                    attachmentCount={sessionAttachments.length}
                    attachmentLimit={attachmentLimit}
                    onFiles={(files) => void addImageFiles(files)}
                    onAttach={attachDataContext}
                    onOpenContextPicker={() => setIsContextPickerOpen(true)}
                    canViewWorkspaceAssets={canPerform('forms.view')}
                    showPlanModeToggle={effectiveOperatingMode === 'CREATE'}
                    isPlanningMode={isPlanningMode}
                    onPlanModeChange={setIsPlanningMode}
                    disabled={isGenerating}
                  />
                  {lineFeatureFlags.aiTierSwitcher && !isTierSwitcherLegacyPlan && !isMobileLayout && (
                    <AiTierPickerButton
                      tier={activeAiTier}
                      onChange={handleTierChange}
                      availableTiers={aiAvailableTiers}
                      lockedTiers={aiLockedTiers}
                      composerContent={prompt}
                      planName={tierPlanName}
                      onLockedClick={handleLockedTierClick}
                      disabled={isGenerating}
                    />
                  )}
                </div>
                {/* Meta line now lives INSIDE the toolbar row between + and
                 *  mic/send — was its own row above the toolbar before. */}
                <div className="aiComposerToolbarMeta">
                  <span>{t('NLSAiComposerChars', { count: promptLength.toLocaleString(), max: maxCharacters.toLocaleString() })}</span>
                  {effectiveOperatingMode === 'CREATE' && dataContexts.length > 0 && (
                    <span> · {t(dataContexts.length === 1 ? 'NLSAiComposerDataSourceOne' : 'NLSAiComposerDataSourceMany', { count: dataContexts.length })}</span>
                  )}
                  {effectiveOperatingMode === 'CREATE' && imageFiles.length > 0 && (
                    <span> · {t(imageFiles.length === 1 ? 'NLSAiComposerImageOne' : 'NLSAiComposerImageMany', { count: imageFiles.length })}</span>
                  )}
                  <span aria-hidden="true"> · </span>
                  <span>
                    {t('NLSAiComposerEstimatedCreditsPrefix')} <strong>{(effectiveOperatingMode === 'EXECUTE' ? PREMIUM_ACTION_COSTS.TOUR_GENERATION : estimatedCredits).toLocaleString()}</strong> {t('NLSAiComposerCreditsSuffix')}
                    {effectiveOperatingMode === 'CREATE' && (
                      <> · <em>{isPlanningMode ? t('NLSAiComposerPlanModeShort') : t('NLSAiComposerBuildDirect')}</em></>
                    )}
                  </span>
                  <span aria-hidden="true"> · </span>
                  <span>
                    {credits.isPersonalWorkspace ? t('NLSAiComposerPersonalWallet') : t('NLSAiComposerWorkspaceBalance')}{' '}
                    <CreditBalance
                      value={credits.contextualBalance}
                      variant="bare"
                      scope={credits.contextualScope}
                      label={null}
                      title={
                        credits.isPersonalWorkspace
                          ? t('NLSAiComposerPersonalWalletTitle', { credits: formatCreditNumber(credits.personalCredits) })
                          : t('NLSAiComposerWorkspaceBalanceTitle', {
                              workspace: formatCreditNumber(credits.workspaceWallet.availableCredits),
                              personal: formatCreditNumber(credits.personalCredits),
                            })
                      }
                    />
                  </span>
                  {upgradeLocked && effectiveOperatingMode === 'CREATE' && (
                    <UpgradeLockBadge feature="AI_GENERATION" label={t('NLSAiComposerTrialExhausted')} />
                  )}
                </div>
                <div className="aiComposerToolbarRight">
                  <Pressable
                    className={`aiComposerIconButton ${isListening ? 'isListening' : ''}`}
                    onClick={toggleSpeechCapture}
                    disabled={isGenerating}
                    aria-pressed={isListening}
                    aria-label={isListening ? t('NLSAiActionStopDictation') : t('NLSAiActionStartDictation')}
                    title={isListening ? t('NLSAiTitleListeningTapToStop') : t('NLSAiTitleTapToDictate')}
                  >
                    <MicNoneRoundedIcon fontSize="inherit" />
                  </Pressable>
                  <Pressable
                    data-tour-id="ai-composer-generate-button"
                    className={`aiComposerSendButton${isGenerating ? ' isStopState' : ''}`}
                    onClick={() => {
                      if (isGenerating) {
                        abortAiJob()
                        return
                      }
                      // Interview mode: the toolbar Send is the ESCAPE HATCH.
                      // It only fires when the textarea has content (disabled
                      // otherwise via the `disabled` prop below), and dispatches
                      // the raw free text as a clarification reply. The carousel
                      // owns its own ↵ submit for the interview answers path.
                      if (interviewFreeTextMode) {
                        submitClarification(prompt)
                        return
                      }
                      if (interviewModeActive) return
                      // CREATE clarification path: wrap prompt as envelope.
                      if (effectiveOperatingMode === 'CREATE' && activeClarification) {
                        if (prompt.trim()) submitClarification(prompt)
                        return
                      }
                      void runGeneration()
                    }}
                    disabled={isAiBlocked ? true : (isGenerating
                      ? false
                      : effectiveOperatingMode === 'EXECUTE'
                        ? !prompt.trim()
                        : interviewModeActive
                          ? !prompt.trim()
                          : activeClarification
                            ? !prompt.trim()
                            : !canGenerate)}
                    aria-label={isGenerating
                      ? t('NLSAiActionStopInFlightGeneration')
                      : isContinuingLoadedChat
                        ? t('NLSAiActionSendFollowUpToLine')
                        : t('NLSAiActionSendMessageToLine')}
                    title={isGenerating
                      ? t('NLSAiActionStop')
                      : isContinuingLoadedChat
                        ? t('NLSAiActionSendFollowUp')
                        : t('NLSAiActionSend')}
                  >
                    {isGenerating ? (
                      <StopRoundedIcon fontSize="inherit" />
                    ) : (
                      <ArrowUpwardRoundedIcon fontSize="inherit" />
                    )}
                  </Pressable>
                </div>
              </div>
              </div>
            </MergedComposer>
            </div>
            </CitationDispatchProvider>
                </div>
              )}
            />
            <AnimatePresence>
              {isMobileMenuOpen && (
                <>
                  <MotionPressable
                    className="aiManagerMobileBackdrop"
                    aria-label={t('NLSAiAriaCloseChatHistory')}
                    onClick={() => setIsMobileMenuOpen(false)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  />
                  <motion.div className="aiManagerMobileAgents" initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}>
                    <AiChatHistoryPanel
                      sessions={chatSessions.filter((session) => session.operatingMode === effectiveOperatingMode)}
                      activeId={activeChatId}
                      onOpen={loadSession}
                      onNew={() => void startNewChat()}
                      onRename={openRenameDialog}
                      onDelete={openDeleteDialog}
                    />
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </motion.div>
          <AppDialog
            open={historyLimitOpen}
            title={t('NLSAiHistoryLimitTitle')}
            message={(
              <>
                <p>{t('NLSAiHistoryLimitBody')}</p>
                <div className="aiHistoryLimitList">
                  {chatSessions.map((session) => (
                    <div key={session.id}>
                      <span>{session.title}</span>
                      <Button
                        variant="secondary"
                        tone="danger"
                        size="sm"
                        onClick={() => {
                          openDeleteDialog(session, { closeHistoryLimitOnConfirm: true })
                        }}
                      >
                        {t('NLSAiActionDelete')}
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
            actions={[
              {
                label: t('NLSCommonActionCancel'),
                onClick: () => setHistoryLimitOpen(false),
              },
            ]}
          />
        </>
      )}
      {tierUpsell && (
        <AiTierUpsellModal
          open
          attemptedTier={tierUpsell.tier}
          trigger={tierUpsell.trigger}
          onUpgrade={() => {
            appendWarningMessage('Open Settings → Billing to upgrade your plan.')
            setTierUpsell(null)
          }}
          onBuyCredits={() => {
            appendWarningMessage('Open Settings → Billing → Credit Packs to top up.')
            setTierUpsell(null)
          }}
          onDismiss={() => setTierUpsell(null)}
        />
      )}
      <AppDialog
        open={Boolean(renameDialogSession)}
        title={t('NLSAiDialogRenameTitle')}
        message={(
          <div className="aiChatDialogCopy">
            <p className="aiChatDialogLead">{t('NLSAiDialogRenameBody')}</p>
            <label className="aiChatDialogField">
              <span className="aiChatDialogFieldLabel">{t('NLSAiDialogChatTitleLabel')}</span>
              <TextInput
                autoFocus
                value={renameDialogValue}
                onChange={setRenameDialogValue}
                maxLength={60}
                placeholder={t('NLSAiDialogChatTitlePlaceholder')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    if (renameDialogValue.trim()) submitRenameDialog()
                  }
                }}
              />
            </label>
          </div>
        )}
        onDismiss={closeRenameDialog}
        actions={[
          { label: t('NLSCommonActionCancel'), onClick: closeRenameDialog },
          {
            label: t('NLSAiActionRename'),
            variant: 'primary',
            onClick: submitRenameDialog,
            disabled: !renameDialogValue.trim(),
          },
        ]}
      />
      <AppDialog
        open={Boolean(deleteDialog)}
        title={t('NLSAiDialogDeleteTitle')}
        message={deleteDialog ? (
          <div className="aiChatDialogCopy">
            <p className="aiChatDialogLead">{t('NLSAiDialogDeleteBody')}</p>
            <div className="aiChatDeleteSummary">
              <strong className="aiChatDeleteSummaryTitle">{deleteDialog.session.title}</strong>
              <span className="aiChatDeleteSummaryMeta">{t('NLSAiDialogDeleteMeta')}</span>
            </div>
          </div>
        ) : undefined}
        onDismiss={closeDeleteDialog}
        actions={[
          { label: t('NLSCommonActionCancel'), onClick: closeDeleteDialog },
          { label: t('NLSAiActionDelete'), variant: 'danger', onClick: confirmDeleteDialog },
        ]}
      />
      <ChatSourceExpandModal
        context={expandedChatContext}
        onClose={() => setExpandedChatContextId(null)}
        onChange={(nextContext) => {
          setDataContexts((current) => current.map((context) => context.id === nextContext.id ? nextContext : context))
        }}
        onOpenInChat={(channelId) => {
          if (channelId) setActiveChannelId(channelId)
          const workspace = workspaces.find((entry) => entry.id === activeWorkspaceId)
          if (workspace) {
            window.history.pushState(null, '', toConsolePath(`/workspace/${workspace.slug}/chat`))
            window.dispatchEvent(new PopStateEvent('popstate'))
          }
        }}
      />
      <WorkspaceItemPicker
        open={isContextPickerOpen}
        onClose={() => setIsContextPickerOpen(false)}
        onCommit={(contexts) => {
          contexts.forEach((ctx) => attachDataContext(ctx))
        }}
        workspaceId={activeWorkspaceId}
        userId={currentUser?.uid ?? ''}
        initialContexts={activeDataContexts}
        onFiles={(files) => void addImageFiles(files)}
        mode="aiContext"
      />
      <AttachmentPreviewDialog open={Boolean(previewItem)} item={previewItem} onClose={() => setPreviewItem(null)} />
    </AnimatePresence>
  )
}

export const AiGeneratorPanel = (props: Parameters<typeof AiGeneratorPanelContent>[0]) => (
  <AiChatProvider>
    <AiGeneratorPanelContent {...props} />
  </AiChatProvider>
)
